<template>
  <div class="form-container card shadow-sm p-3">

    <div class="header d-flex justify-content-between align-items-center">
      <h4 class="form-title">
        {{ matricula.id ? "Editar Matrícula nº" : "Nova Matrícula" }}
        <span v-if="matricula.numero_contrato">{{ matricula.id }} / Contrato nº ({{ matricula.numero_contrato }})</span>
      </h4>
      <button class="btn btn-secondary" @click="cancelar">Voltar</button>
    </div>

    <div class="spacer"></div>

    <!-- Dados do Contratante -->
    <div class="form-section">
      <h5 class="section-title">Dados do Contratante</h5>
      <div class="row">
        <div class="col-md-6">
          <div class="form-group">
            <label for="aluno">Aluno <span class="required">*</span></label>
            <div class="d-flex">
              <input id="aluno" type="text" class="form-control pessoa-input disabled-input" :value="matricula.aluno_nome || ''" disabled />
              <button class="btn btn-primary pessoa-icon-btn" type="button" @click="() => abrirModalPessoa('aluno')">
                <i class="bi bi-person"></i>
              </button>
            </div>
          </div>
        </div>

        <div class="col-md-6">
          <div class="form-group">
            <label for="responsavel">Responsável <span class="required">*</span></label>
            <div class="d-flex">
              <input id="responsavel" type="text" class="form-control pessoa-input disabled-input" :value="matricula.responsavel_nome || ''" disabled />
              <button class="btn btn-primary pessoa-icon-btn" type="button" @click="() => abrirModalPessoa('responsavel')">
                <i class="bi bi-person"></i>
              </button>
            </div>
          </div>
        </div>

       <div class="col-md-6 mt-3">
         <div class="form-group">
           <label for="direitoImagem">
             Concorda com o uso da imagem do aluno pela escola? <span class="required">*</span>
           </label>
           <select
             id="direitoImagem"
             v-model="matricula.direito_imagem"
             class="form-control"
             required
           >
             <option value="" disabled>Selecione</option>
             <option :value="true">Sim - Autorizo o uso da imagem do aluno</option>
             <option :value="false">Não</option>
           </select>
         </div>
       </div>

      </div>
    </div>

    <!-- Dados do Plano de Matrícula -->
    <div class="form-section">
      <h5 class="section-title">Dados do Plano de Matrícula</h5>

      <div class="row mb-3">
        <div class="col-md-4">
          <div class="form-group">
            <label for="categoria">Categoria <span class="required">*</span></label>
            <select id="categoria" v-model="matricula.categoria_id" class="form-control" @change="carregarTiposPlano">
              <option value="">Selecione</option>
              <option v-for="categoria in categorias" :key="categoria.id" :value="categoria.id">
                {{ categoria.nome }}
              </option>
            </select>
          </div>
        </div>

        <div class="col-md-4">
          <div class="form-group">
            <label for="tipo_plano">Tipo de Plano <span class="required">*</span></label>
            <select
              id="tipo_plano"
              v-model="matricula.tipo_plano_id"
              class="form-control"
              @focus="backupTipoPlano"
              @change="onTipoPlanoChange">
              <option value="">Selecione</option>
              <option v-for="tipo in tiposPlano" :key="tipo.id" :value="tipo.id">
                {{ tipo.nome }} - {{ tipo.duracao }} dias
              </option>
            </select>
          </div>
        </div>

        <div class="col-md-2">
          <div class="form-group">
            <label for="aulas_semana">Aulas por Semana</label>
            <input id="aulas_semana" v-model="plano.aulas_semana" class="form-control" type="number" min="1" disabled/>
          </div>
        </div>

        <div class="col-md-2">
          <div class="form-group">
            <label for="qtd_parcelas">Qtd. de Parcelas</label>
            <input
              id="qtd_parcelas"
              v-model.number="plano.quantidade_parcelas"
              class="form-control"
              type="number"
              min="1"
            />
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-md-4">
          <div class="form-group">
            <label for="nivel">Nível <span class="required">*</span></label>
            <select id="nivel" v-model="matricula.nivel_id" class="form-control">
              <option value="">Selecione</option>
              <option v-for="nivel in niveis" :key="nivel.id" :value="nivel.id">
                {{ nivel.nivel }}
              </option>
            </select>
          </div>
        </div>

        <div class="col-md-4">
          <div class="form-group">
            <label for="inicio_vigencia">Início da Vigência <span class="required">*</span></label>
            <input
              type="date"
              id="inicio_vigencia"
              v-model="matricula.inicio_vigencia"
              class="form-control"
              @change="onInicioVigenciaChange(); carregarDiasVencimentoCobranca()"
            />
          </div>
        </div>

        <div class="col-md-4">
          <div class="form-group">
            <label for="termino_vigencia">Término da Vigência</label>
            <input
              type="date"
              id="termino_vigencia"
              v-model="matricula.termino_vigencia"
              class="form-control"
              @input="onTerminoVigenciaManual"
            />

            <div class="form-check mt-2 d-flex align-items-center gap-2">
              <input
                id="encerrar_final_ano"
                type="checkbox"
                class="form-check-input m-0"
                v-model="matricula.encerrar_vigencia_final_ano"
                @change="onEncerrarFinalAnoChange"
              />
              <label class="form-check-label mb-0" for="encerrar_final_ano">
                Encerrar vigência no final do ano
              </label>
            </div>
          </div>
        </div>
      </div>

      <div class="form-group">
        <label for="turmas"></label>
        <div class="table-responsive mt-2">
          <table class="table table-borderless">
            <thead>
              <tr>
                <th v-if="modoAvancado">Nível</th>
                <th>Turma(s) <span class="required">*</span></th>
                <th style="width: 50px;"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(turmaSelecionada, index) in matricula.turmas" :key="index">

                <td v-if="modoAvancado">
                  <select
                    v-model="turmaSelecionada.nivel_id"
                    class="form-control"
                    @change="onNivelLinhaChange(index)"
                  >
                    <option :value="null" disabled>Selecione</option>
                    <option v-for="nivel in niveis" :key="nivel.id" :value="nivel.id">
                      {{ nivel.nivel }}
                    </option>
                  </select>
                </td>

                <td>
                  <select
                      v-model="turmaSelecionada.turma_id"
                      class="form-control"
                      @change="() => {
                        atualizarTurma(index);
                      }"
                    >
                    <option value="" disabled>Selecione</option>
                    <option v-if="turmaSelecionada.turma_id && !turmaEstaDisponivel(turmaSelecionada)" :value="turmaSelecionada.turma_id" disabled>
                      {{ turmaSelecionada.turma_id }} — {{ turmaHistoricaPreservada(turmaSelecionada) ? 'Atual / indisponível para novas escolhas' : 'Indisponível — selecione outra turma' }}
                    </option>
                    <option
                      v-for="t in (modoAvancado
                                  ? turmasDisponiveisPorNivel(turmaSelecionada.nivel_id || matricula.nivel_id)
                                  : turmasDisponiveisPorNivel(matricula.nivel_id))"
                      :key="`${t.id}-${t.dia}-${t.horario}`"
                      :value="`${t.id}-${t.dia}-${t.horario}`"
                    >
                      <!-- Exibição bonitinha, mas sem mudar o value -->
                      {{ `Turma: ${t.id} | Dia: ${t.dia} | Hora: ${t.horario_fmt} | Instrutor: ${t.instrutor}` }}
                      <!-- Se preferir usar a label pronta:
                      {{ t.label }} -->
                    </option>
                  </select>
                </td>
                <td class="text-center">
                  <button @click="removerTurma(index)" class="btn btn-danger btn-sm">
                    🗑️
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
          <!-- Botão para adicionar nova turma -->
          <div class="add-turma-container">
            <button class="btn btn-primary" @click="adicionarTurmaSlot">
              <i class="bi bi-plus"></i>
            </button>
          </div>
        </div>
      </div>

    </div>


    <!-- Dados Financeiros -->
    <div class="form-section">
      <h5 class="section-title">Dados Financeiros</h5>

      <div class="row mb-3">
        <!-- Data da Matrícula -->
        <div class="col-md-2">
          <div class="form-group">
            <label for="data_matricula">Data da Matrícula <span class="required">*</span></label>
            <input
              type="date"
              id="data_matricula"
              v-model="matricula.data_matricula"
              class="form-control"
              required
            />
          </div>
        </div>

        <!-- Valor -->
        <div class="col-md-3">
          <div class="form-group">
            <label for="valor">Valor <span class="required">*</span></label>
            <input
              id="valor"
              type="text"
              class="form-control"
              v-model="valorInput"
              @focus="onValorFocus"
              @blur="onValorBlur"
              required
            />
          </div>
        </div>

        <!-- Desconto (%) -->
        <div class="col-md-2">
          <div class="form-group">
            <label for="desconto">Desconto (%)</label>
            <input
              type="number"
              id="desconto"
              class="form-control"
              v-model="matricula.desconto"
              min="0"
              max="100"
              @input="calcularValorComDesconto"
            />
          </div>
        </div>

        <!-- Valor com Desconto -->
        <div class="col-md-3">
          <div class="form-group">
            <label for="valor_com_desconto">Valor com Desconto</label>
            <input
              type="text"
              class="form-control"
              :value="globalUtils.formatarValor(matricula.valor_com_desconto)"
              disabled
            />
          </div>
        </div>

        <!-- Convênio -->
        <div class="col-md-2">
          <div class="form-group">
            <label for="convenio">Convênio</label>
            <select
              id="convenio"
              v-model="matricula.convenio_id"
              class="form-control"
              @change="atualizarDescontoConvenio"
            >
              <option value="">Selecione</option>
              <option
                v-for="convenio in convenios"
                :key="convenio.id"
                :value="convenio.id"
              >
                {{ convenio.nome }}
              </option>
            </select>
          </div>
        </div>
      </div>

      <div class="row mt-3">
        <!-- Forma de Pagamento -->
        <div class="col-md-2">
          <div class="form-group">
            <label for="forma_pagamento">Forma de Pagamento <span class="required">*</span></label>
            <select
              id="forma_pagamento"
              v-model="matricula.pagamento_matricula.forma_pagamento_id"
              class="form-control"
              required
              @change="onPagamentoMatriculaChange()"
            >
              <option value="">Selecione</option>
              <option
                v-for="forma in formasPagamento"
                :key="forma.id"
                :value="forma.id"
              >
                {{ forma.nome }}
              </option>
            </select>
          </div>
            <!-- Autorização do Cartão (Matrícula) -->
            <div v-if="isCartao(matricula.pagamento_matricula.forma_pagamento_id)">
              <div class="form-group">
                <label for="aut_matricula">Autorização (Matrícula) <span class="required">*</span></label>
                <input
                  id="aut_matricula"
                  type="text"
                  class="form-control"
                  v-model="matricula.pagamento_matricula.numero_autorizacao"
                  placeholder="Digite o número da autorização"
                  :disabled="isAInformar(matricula.pagamento_matricula.forma_pagamento_id)"
                />
              </div>
            </div>

        </div>

        <!-- Conta Financeira / Cheque -->
        <div v-if="isCheque(matricula.pagamento_matricula.forma_pagamento_id)" class="col-md-3 d-flex">
          <div class="form-group flex-fill mr-2">
            <label for="banco_numero">Banco nº <span class="required">*</span></label>
            <input
              type="text"
              id="banco_numero"
              v-model="matricula.banco_numero"
              class="form-control"
              required
            />
          </div>
          <div class="form-group flex-fill mr-2">
            <label for="agencia">Agência <span class="required">*</span></label>
            <input
              type="text"
              id="agencia"
              v-model="matricula.agencia"
              class="form-control"
              required
            />
          </div>
          <div class="form-group flex-fill">
            <label for="cheque_numero">Cheque nº <span class="required">*</span></label>
            <input
              type="text"
              id="cheque_numero"
              v-model="matricula.cheque_numero"
              class="form-control"
              required
            />
          </div>
        </div>

        <div class="col-md-3">
          <div class="form-group">
            <label for="conta_financeira_matricula">Conta Financeira <span class="required">*</span></label>
            <select
              id="conta_financeira_matricula"
              v-model="matricula.pagamento_matricula.conta_financeira_id"
              class="form-control"
              required
              :disabled="isAInformar(matricula.pagamento_matricula.forma_pagamento_id)"
            >
              <!-- Caso A informar -->
              <option
                v-if="isAInformar(matricula.pagamento_matricula.forma_pagamento_id)"
                :value="null"
              >
                A informar
              </option>

              <!-- Caso normal (inclui Espécie) -->
              <template v-else>
                <option value="" disabled>Selecione</option>
                <option
                  v-for="conta in contasFinanceiras"
                  :key="conta.id"
                  :value="Number(conta.id)"
                >
                  {{ conta.apelido || 'Sem apelido' }}
                </option>
              </template>
            </select>


          </div>
        </div>

        <!-- Data de Vencimento -->
        <div class="col-md-2">
          <div class="form-group">
            <label for="data_vencimento">Data de Vencimento <span class="required">*</span></label>
            <input
              type="date"
              id="data_vencimento"
              v-model="matricula.data_vencimento"
              class="form-control"
              @input="dataVencimentoTouched = true"
              @change="onPagamentoMatriculaChange()"
            />
          </div>
        </div>

        <!-- Data de Pagamento -->
        <div class="col-md-2">
          <div class="form-group">
            <label for="data_pagamento">Data de Pagamento</label>
            <input
              type="date"
              id="data_pagamento"
              v-model="matricula.pagamento_matricula.data_pagamento"
              class="form-control"
              :disabled="isAInformar(matricula.pagamento_matricula.forma_pagamento_id) || (!matricula.pagamento_matricula.id && isBoleto(matricula.pagamento_matricula.forma_pagamento_id))"
            />
          </div>
        </div>

        <!-- Data de Recebimento -->
        <div class="col-md-2">
          <div class="form-group">
            <label for="data_recebimento">Recebimento em</label>
            <input
              type="date"
              id="data_recebimento"
              v-model="matricula.pagamento_matricula.data_recebimento"
              class="form-control"
              :disabled="!canEditRecebimento || isAInformar(matricula.pagamento_matricula.forma_pagamento_id) || (!matricula.pagamento_matricula.id && isBoleto(matricula.pagamento_matricula.forma_pagamento_id))"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Seção Mensalidade -->
    <div class="form-section">
      <h5 class="section-title">Mensalidade</h5>
      <div class="row mb-3">

        <!-- Valor (Mensalidade) -->
        <div class="col-md-2">
          <div class="form-group">
            <label for="valor_mensalidade">Valor:</label>
            <input
              id="valor_mensalidade"
              type="text"
              class="form-control"
              :value="valorMensalidadeDisplay"
              @focus="onValorMensalidadeFocus"
              @blur="onValorMensalidadeBlur"
            />
          </div>
        </div>

        <!-- Desconto (%) -->
        <div class="col-md-2">
          <div class="form-group">
            <label for="desconto_mensalidade">Desconto (%)</label>
            <input
              :type="entradaMensalidadeManaged ? 'text' : 'number'"
              :inputmode="entradaMensalidadeManaged ? 'decimal' : undefined"
              :min="entradaMensalidadeManaged ? undefined : 0"
              :max="entradaMensalidadeManaged ? undefined : 100"
              id="desconto_mensalidade"
              class="form-control"
              v-model="matricula.desconto_mensalidade"
              @input="capturarDescontoMensalidade($event); calcularValorComDescontoMensalidade()"
              :disabled="descontoDesabilitado"
            />
          </div>
        </div>

        <!-- Valor com Desconto -->
        <div class="col-md-2">
          <div class="form-group">
            <label for="valor_com_desconto_mensalidade">Valor com Desconto</label>
            <input type="text" id="valor_com_desconto_mensalidade" class="form-control" :value="globalUtils.formatarValor(matricula.valor_com_desconto_mensalidade)" disabled />
          </div>
        </div>

        <!-- Dia de vencimento do Boleto gerenciado -->
        <div
          v-if="!matricula.id && novaMensalidadeBoletoSemantica"
          class="col-md-2"
        >
          <div class="form-group">
            <label for="dia_vencimento_mensalidade">
              Dia venc. Boleto <span class="required">*</span>
            </label>

            <select
              id="dia_vencimento_mensalidade"
              v-model="matricula.dia_vencimento_mensalidade"
              class="form-control"
              :disabled="carregandoDiasVencimentoCobranca || !matricula.inicio_vigencia"
              @change="onDiaVencimentoMensalidadeChange"
            >
              <option value="">Selecione</option>

              <option
                v-for="item in diasVencimentoCobranca"
                :key="item.dia"
                :value="item.dia"
              >
                Dia {{ item.dia }}{{ item.padrao ? ' (padrão)' : '' }}
              </option>
            </select>

            <small
              v-if="erroPoliticaCobranca"
              class="text-danger d-block mt-1"
            >
              {{ erroPoliticaCobranca }}
            </small>
          </div>
        </div>

        <!-- Primeiro Vencimento -->
        <div class="col-md-2">
          <div class="form-group">
            <label for="primeiro_vencimento">1º Vencimento <span class="required">*</span></label>
            <input
              type="date"
              id="primeiro_vencimento"
              class="form-control"
              v-model="matricula.primeiro_vencimento"
              :disabled="!matricula.id && novaMensalidadeBoletoSemantica"
              required
            />
          </div>
        </div>

        <!-- Quantidade de Pagamentos -->
        <div class="col-md-2">
          <div class="form-group">
            <label for="quantidade_pagamentos">Parcelas contratuais</label>
            <input type="text" id="quantidade_pagamentos" class="form-control" :value="matricula.quantidade_pagamentos" disabled />
          </div>
        </div>

        <!-- Valor Total -->
        <div class="col-md-2">
          <div class="form-group">
            <label for="valor_total_mensalidade">{{ composicaoTroca ? 'Complemento desta troca' : 'Valor Total' }}</label>
            <input type="text" id="valor_total_mensalidade" class="form-control" :value="globalUtils.formatarValor(matricula.valor_total_mensalidade)" readonly />
          </div>
        </div>
      </div>

      <div v-if="matricula.pagamentos.some(p => p.adicional)" class="mb-2">
        Valor das parcelas adicionais:
        <span style="color: green">
          {{ globalUtils.formatarValor(valorParcelasAdicionais) }}
        </span>
      </div>

      <div v-if="matricula.pagamentos.some(p => p.adicional)" class="mb-2">
        Valor total com parcelas adicionais:
        <span style="color: green">
          {{ globalUtils.formatarValor(valorTotalComParcelasAdicionais) }}
        </span>
      </div>

      <div v-if="matricula.id && mensalidadeLoaded && !temParcelasMensalidade && (!composicaoTroca || Number(composicaoTroca.complemento) !== 0)" class="alert alert-warning mt-3">
        <div><strong>Atenção:</strong> não existem parcelas de mensalidade para esta matrícula.</div>
        <div class="mt-2 d-flex gap-2">
          <button class="btn btn-outline-primary btn-sm" @click="recriarParcelasMensalidade()">
            Gerar parcelas do plano
          </button>
          <button class="btn btn-outline-secondary btn-sm" @click="adicionarPagamento()">
            Criar parcela manual
          </button>
        </div>
      </div>

      <!-- Resumo financeiro da mensalidade -->


      <div
        v-if="
          temParcelasMensalidade &&
          !matricula.id &&
          novaMensalidadeBoletoSemantica &&
          carregandoPreviewMensalidadeManaged
        "
        class="alert alert-info mt-3 mb-2 py-2"
      >
        <i class="bi bi-hourglass-split me-1"></i>
        Calculando a prévia das obrigações pela política de cobrança...
      </div>

      <div
        v-if="
          temParcelasMensalidade &&
          !matricula.id &&
          novaMensalidadeBoletoSemantica &&
          erroPreviewMensalidadeManaged
        "
        class="alert alert-warning mt-3 mb-2 py-2"
      >
        <i class="bi bi-exclamation-triangle-fill me-1"></i>
        {{ erroPreviewMensalidadeManaged }}
      </div>

      <div
        v-if="
          temParcelasMensalidade &&
          !matricula.id &&
          novaMensalidadeBoletoSemantica &&
          previewMensalidadeManaged
        "
        class="conferencia-mensalidade mt-3 mb-3"
      >
        <div class="row g-2">
          <div class="col-md-4">
            <div class="conferencia-card">
              <span class="conferencia-label">Total contratual</span>
              <strong>
                {{ globalUtils.formatarValor(
                  previewMensalidadeManaged.resumo.total_contratual
                ) }}
              </strong>
            </div>
          </div>

          <div class="col-md-4">
            <div class="conferencia-card">
              <span class="conferencia-label">Pró-rata</span>
              <strong>
                +{{ globalUtils.formatarValor(
                  previewMensalidadeManaged.resumo.total_prorata
                ) }}
              </strong>
            </div>
          </div>

          <div class="col-md-4">
            <div class="conferencia-card conferencia-ok">
              <span class="conferencia-label">Total previsto</span>
              <strong>
                {{ globalUtils.formatarValor(
                  previewMensalidadeManaged.resumo.total_obrigacoes
                ) }}
              </strong>
            </div>
          </div>
        </div>

        <div class="alert alert-info mt-2 mb-0 py-2">
          <i class="bi bi-info-circle-fill me-1"></i>
          {{ mensagemPreviewProrataManaged }}

          <div
            v-if="primeiroTituloPreviewManaged"
            class="mt-1"
          >
            <strong>Primeiro título previsto:</strong>
            {{ globalUtils.formatarValor(
              primeiroTituloPreviewManaged.valor
            ) }}
            com vencimento em
            {{ globalUtils.formatarData(
              primeiroTituloPreviewManaged.data_vencimento
            ) }}.
          </div>
        </div>
      </div>

      <div v-if="conferenciaTrocaIndisponivel" class="alert alert-warning mt-3" role="status">
        {{ estadoComposicaoTroca === 'carregando' ? 'Carregando composição financeira da troca…' : 'Composição financeira indisponível. Atualize a matrícula antes de conferir os valores ou salvar.' }}
      </div>
      <div v-if="composicaoTroca" class="alert alert-info mt-3">
        <strong>Composição registrada na troca de plano</strong>
        <div class="row g-2 mt-1">
          <div v-for="[rotulo, valor] in resumoTroca" :key="rotulo" class="col-12 col-sm-6 col-lg-4">
            <span class="d-block">{{ rotulo }}</span><strong>{{ globalUtils.formatarValor(valor) }}</strong>
          </div>
        </div>
        <p class="mb-0 mt-2">Os lançamentos do contrato atual representam somente o complemento.
          As obrigações anteriores permanecem preservadas. Pagamentos posteriores não estão refletidos nos valores históricos acima.</p>
      </div>
      <div
        v-if="
          !conferenciaTrocaIndisponivel && (temParcelasMensalidade || composicaoTroca) &&
          !(
            !matricula.id &&
            novaMensalidadeBoletoSemantica
          )
        "
        class="conferencia-mensalidade mt-3 mb-3"
      >
        <div class="row g-2">
          <div class="col-md-4">
            <div class="conferencia-card">
              <span class="conferencia-label">{{ composicaoTroca ? 'Complemento esperado' : 'Total esperado' }}</span>

              <strong>
                {{ globalUtils.formatarValor(totalEsperadoMensalidade) }}
              </strong>
            </div>
          </div>

          <div class="col-md-4">
            <div class="conferencia-card">
              <span class="conferencia-label">Total dos lançamentos</span>

              <strong>
                {{ globalUtils.formatarValor(totalLancamentosMensalidade) }}
              </strong>
            </div>
          </div>

          <div class="col-md-4">
            <div
              class="conferencia-card"
              :class="{
                'conferencia-ok': !existeDiferencaMensalidade,
                'conferencia-alerta': existeDiferencaMensalidade
              }"
            >
              <span class="conferencia-label">Diferença</span>

              <strong v-if="!existeDiferencaMensalidade">
                R$ 0,00
              </strong>

              <strong v-else-if="diferencaTotalMensalidade > 0">
                +{{ globalUtils.formatarValor(diferencaMensalidadeAbsoluta) }}
              </strong>

              <strong v-else>
                -{{ globalUtils.formatarValor(diferencaMensalidadeAbsoluta) }}
              </strong>
            </div>
          </div>
        </div>

        <div
          class="alert mt-2 mb-0 py-2"
          :class="
            existeDiferencaMensalidade
              ? 'alert-warning'
              : 'alert-success'
          "
        >
          <i
            class="bi me-1"
            :class="
              existeDiferencaMensalidade
                ? 'bi-exclamation-triangle-fill'
                : 'bi-check-circle-fill'
            "
          ></i>

          {{ mensagemDiferencaMensalidade }}
        </div>

        <div
          v-if="mensagemComposicoesMensalidade"
          class="alert alert-info mt-2 mb-0 py-2"
        >
          <i class="bi bi-info-circle-fill me-1"></i>
          {{ mensagemComposicoesMensalidade }}
        </div>
      </div>

      <div
        v-if="temParcelasMensalidade"
        class="mensalidade-resumo d-flex flex-wrap align-items-center gap-2 mt-3 mb-2"
      >
        <span class="badge bg-primary">
          {{ matricula.quantidade_pagamentos }} parcelas contratuais
        </span>

        <span
          v-if="quantidadePagamentosSubstitutos"
          class="badge bg-secondary"
        >
          {{ quantidadePagamentosSubstitutos }} pagamentos substitutos
        </span>

        <span class="badge bg-light text-dark border">
          {{ quantidadeRegistrosFinanceiros }} registros financeiros
        </span>

        <span
          v-if="quantidadeParcelasAdicionais"
          class="badge bg-warning text-dark"
        >
          {{ quantidadeParcelasAdicionais }} adicionais
        </span>
      </div>

      <!-- Tabela compacta de pagamentos -->
      <div class="table-responsive mensalidade-table-wrapper mt-2" v-if="temParcelasMensalidade">
        <table class="table table-borderless align-middle mensalidade-table">
          <thead>
            <tr>
              <th class="text-center coluna-parcela">Parcela</th>
              <th class="coluna-situacao">Situação</th>
              <th class="coluna-forma">Forma de Pagamento <span class="required">*</span></th>
              <th class="coluna-data">Vencimento <span class="required">*</span></th>
              <th class="coluna-valor">Valor</th>
              <th class="coluna-data">Pagamento</th>
              <th class="coluna-data">Recebimento</th>
              <th class="text-center coluna-acoes">Ações</th>
            </tr>
          </thead>

          <tbody>
            <template
              v-for="(pagamento, index) in parcelasMensalidade"
              :key="pagamento.id || `novo-${index}`"
            >
              <tr :class="classeLinhaPagamento(pagamento)">
                <td class="text-center parcela-numero-coluna">
                  <strong v-if="!pagamento.adicional">
                    {{ numeroParcelaContratual(pagamento) || '—' }}
                  </strong>

                  <span v-else class="badge bg-warning text-dark">
                    Adicional
                  </span>
                </td>

                <td class="situacao-coluna">
                  <span
                    class="badge situacao-badge"
                    :class="situacaoPagamento(pagamento).classe"
                  >
                    {{ situacaoPagamento(pagamento).texto }}
                  </span>
                </td>

                <td>
                  <select
                    v-model="pagamento.forma_pagamento_id"
                    @change="atualizarPagamentoMensal(pagamento)"
                    class="form-control"
                    required
                    :disabled="isPagamentoBloqueadoEdicao(pagamento)"
                  >
                    <option value="">Selecione</option>
                    <option v-for="forma in formasPagamento" :key="forma.id" :value="forma.id">
                      {{ forma.nome }}
                    </option>
                  </select>
                </td>

                <td>
                  <input
                    v-model="pagamento.data_vencimento"
                    type="date"
                    class="form-control"
                    required
                    :disabled="isPagamentoBloqueadoEdicao(pagamento)"
                    @blur="ordenarParcelasMensalidade"
                  />
                </td>

                <td>
                  <input
                    type="text"
                    :value="globalUtils.formatarValor(pagamento.valor)"
                    class="form-control"
                    :class="{ 'text-success': pagamento.adicional }"
                    :disabled="isPagamentoBloqueadoEdicao(pagamento)"
                    @focus="onParcelaValorFocus($event, pagamento)"
                    @blur="onParcelaValorBlur($event, pagamento)"
                  />
                </td>

                <td>
                  <input
                    v-model="pagamento.data_pagamento"
                    type="date"
                    class="form-control"
                    :disabled="isPagamentoBloqueadoEdicao(pagamento) || isAInformar(pagamento.forma_pagamento_id) || (!pagamento.id && isBoleto(pagamento.forma_pagamento_id))"
                  />
                </td>

                <td>
                  <input
                    type="date"
                    v-model="pagamento.data_recebimento"
                    class="form-control"
                    :disabled="isPagamentoBloqueadoEdicao(pagamento) || !canEditRecebimento || isAInformar(pagamento.forma_pagamento_id) || (!pagamento.id && isBoleto(pagamento.forma_pagamento_id))"
                  />
                </td>

                <td class="text-center">
                  <div class="d-flex justify-content-center align-items-center gap-1 acao-pagamento">
                    <span
                      v-if="isPagamentoConciliado(pagamento)"
                      class="badge bg-success pagamento-status-indicator pagamento-status-icon"
                      :title="motivoBloqueioPagamento(pagamento)"
                    >
                      <i class="bi bi-bank"></i>
                    </span>

                    <span
                      v-else-if="isPagamentoBloqueadoEdicao(pagamento)"
                      class="badge bg-secondary pagamento-status-indicator pagamento-status-icon"
                      :title="motivoBloqueioPagamento(pagamento)"
                    >
                      <i class="bi bi-lock-fill"></i>
                    </span>

                    <button
                      type="button"
                      class="btn btn-outline-secondary btn-sm"
                      :class="{ 'btn-detalhes-pendente': temPendenciaDetalhesPagamento(pagamento) }"
                      :title="pagamento._mostrarHistorico
                        ? 'Ocultar dados complementares'
                        : tituloDetalhesPagamento(pagamento)"
                      @click="alternarHistoricoPagamento(pagamento)"
                    >
                      <i
                        :class="pagamento._mostrarHistorico
                          ? 'bi bi-chevron-up'
                          : 'bi bi-sliders'"
                      ></i>
                    </button>

                    <button
                      type="button"
                      class="btn btn-danger btn-sm"
                      :disabled="!podeRemoverPagamento(pagamento)"
                      :title="podeRemoverPagamento(pagamento)
                        ? 'Excluir registro'
                        : motivoBloqueioPagamento(pagamento)"
                      @click="removerPagamento(pagamento)"
                    >
                      <i class="bi bi-trash"></i>
                    </button>
                  </div>
                </td>
              </tr>

              <tr
                v-if="pagamento._mostrarHistorico"
                class="linha-historico"
              >
                <td colspan="8">
                  <div class="historico-pagamento">
                    <div class="historico-pagamento-titulo d-flex flex-wrap align-items-center justify-content-between gap-2">
                      <span>
                        <i class="bi bi-sliders me-1"></i>
                        Dados complementares do registro
                        <small v-if="pagamento.id" class="text-muted ms-1">#{{ pagamento.id }}</small>
                      </span>

                      <span
                        v-if="isPagamentoConciliado(pagamento)"
                        class="badge bg-success"
                        :title="motivoBloqueioPagamento(pagamento)"
                      >
                        <i class="bi bi-bank me-1"></i>Conciliado
                      </span>
                      <span
                        v-else-if="isPagamentoBloqueadoEdicao(pagamento)"
                        class="badge bg-secondary"
                        :title="motivoBloqueioPagamento(pagamento)"
                      >
                        <i class="bi bi-lock-fill me-1"></i>Protegido
                      </span>
                    </div>

                    <div
                      v-if="temPendenciaDetalhesPagamento(pagamento)"
                      class="alert alert-warning py-2 px-3 mb-3"
                    >
                      <i class="bi bi-exclamation-triangle me-1"></i>
                      Complete os dados complementares obrigatórios deste lançamento.
                    </div>

                    <div class="row g-3 dados-complementares-grid">
                      <div class="col-12 col-lg-5">
                        <label class="form-label mb-1">
                          Conta Financeira <span class="required">*</span>
                        </label>
                        <select
                          v-model="pagamento.conta_financeira_id"
                          class="form-control"
                          required
                          :disabled="isPagamentoBloqueadoEdicao(pagamento) || isAInformar(pagamento.forma_pagamento_id)"
                          @change="onContaFinanceiraMensalChange(pagamento)"
                        >
                          <option
                            v-if="isAInformar(pagamento.forma_pagamento_id)"
                            :value="null"
                          >
                            A informar
                          </option>

                          <template v-else>
                            <option value="" disabled>Selecione</option>
                            <option
                              v-for="conta in contasFinanceiras"
                              :key="conta.id"
                              :value="conta.id"
                            >
                              {{ conta.apelido || 'Sem apelido' }}
                            </option>
                          </template>
                        </select>
                      </div>

                      <div
                        v-if="isFormaComAutorizacao(pagamento.forma_pagamento_id)"
                        class="col-12 col-md-6 col-lg-3"
                      >
                        <label class="form-label mb-1">
                          Autorização <span class="required">*</span>
                        </label>
                        <input
                          v-model="pagamento.numero_autorizacao"
                          type="text"
                          class="form-control"
                          placeholder="Autorização"
                          @input="onNumeroAutorizacaoMensalInput(pagamento)"
                          :disabled="isPagamentoBloqueadoEdicao(pagamento) || isAInformar(pagamento.forma_pagamento_id)"
                        />
                      </div>

                      <div
                        v-if="isCheque(pagamento.forma_pagamento_id)"
                        class="col-12"
                      >
                        <div class="dados-cheque-box">
                          <div class="dados-cheque-titulo">
                            <i class="bi bi-receipt me-1"></i>Dados do cheque
                          </div>
                          <div class="row g-2">
                            <div class="col-12 col-md-3">
                              <label class="form-label mb-1">Banco nº</label>
                              <input
                                v-model="pagamento.banco_numero"
                                type="text"
                                class="form-control"
                                placeholder="Banco nº"
                                :disabled="isPagamentoBloqueadoEdicao(pagamento)"
                              />
                            </div>
                            <div class="col-12 col-md-3">
                              <label class="form-label mb-1">Agência</label>
                              <input
                                v-model="pagamento.agencia"
                                type="text"
                                class="form-control"
                                placeholder="Agência"
                                :disabled="isPagamentoBloqueadoEdicao(pagamento)"
                              />
                            </div>
                            <div class="col-12 col-md-4">
                              <label class="form-label mb-1">Cheque nº</label>
                              <input
                                v-model="pagamento.cheque_numero"
                                type="text"
                                class="form-control"
                                placeholder="Cheque nº"
                                :disabled="isPagamentoBloqueadoEdicao(pagamento)"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div
                      v-if="pagamento.pagamento_substituto || pagamento.pagamento_substituto_id"
                      class="historico-pagamento-bloco"
                    >
                      <strong>Relação de substituição</strong>
                      <div v-if="pagamento.pagamento_substituto">
                        Este pagamento substitui o registro
                        <strong>#{{ pagamento.pagamento_origem_id }}</strong>.
                      </div>
                      <div v-if="pagamento.pagamento_substituto_id">
                        Este cheque foi substituído pelo pagamento
                        <strong>#{{ pagamento.pagamento_substituto_id }}</strong>.
                      </div>
                    </div>

                    <div
                      v-if="Array.isArray(pagamento.conciliacoes_ativas) && pagamento.conciliacoes_ativas.length"
                      class="historico-pagamento-bloco"
                    >
                      <strong>Conciliação bancária ativa</strong>
                      <ul class="historico-lista mb-0">
                        <li
                          v-for="vinculo in pagamento.conciliacoes_ativas"
                          :key="`vinculo-${vinculo.vinculo_id}`"
                        >
                          Vínculo #{{ vinculo.vinculo_id }} ·
                          {{ vinculo.tipo_vinculo || 'NORMAL' }} ·
                          item {{ vinculo.origem_item_tipo }} #{{ vinculo.origem_item_id }}
                          <span v-if="vinculo.valor_utilizado != null">
                            · {{ globalUtils.formatarValor(vinculo.valor_utilizado) }}
                          </span>
                        </li>
                      </ul>
                    </div>

                    <div
                      v-if="Array.isArray(pagamento.eventos_cheque) && pagamento.eventos_cheque.length"
                      class="historico-pagamento-bloco"
                    >
                      <strong>Eventos do cheque</strong>
                      <ul class="historico-lista mb-0">
                        <li
                          v-for="evento in pagamento.eventos_cheque"
                          :key="`evento-${evento.evento_id}`"
                        >
                          {{ formatarDataHistorico(evento.data_evento) }} ·
                          {{ formatarTipoEventoCheque(evento.tipo_evento) }}
                          <span v-if="evento.motivo"> · {{ evento.motivo }}</span>
                          <span v-if="evento.pagamento_substituto_id">
                            · pagamento substituto #{{ evento.pagamento_substituto_id }}
                          </span>
                        </li>
                      </ul>
                    </div>

                    <div
                      v-if="Array.isArray(pagamento.motivos_bloqueio) && pagamento.motivos_bloqueio.length"
                      class="historico-pagamento-bloco"
                    >
                      <strong>Proteções aplicadas</strong>
                      <div>{{ formatarMotivosBloqueio(pagamento.motivos_bloqueio) }}</div>
                    </div>

                    <div v-if="pagamento.observacoes" class="historico-pagamento-bloco mb-0">
                      <strong>Observações</strong>
                      <div class="historico-pagamento-texto">
                        {{ pagamento.observacoes }}
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
      <div
        v-if="modoAvancado && temParcelasMensalidade"
        class="text-success small mt-1 text-end"
        style="font-size: 0.75rem;"
      >
        Matrícula com turmas em níveis distintos (ex.: individual).
        Se necessário, você pode lançar cobranças extras nas parcelas adicionais.
      </div>
      <!-- Botões de ação da Mensalidade (fora do v-if da tabela) -->
      <div class="d-flex justify-content-end mt-2">
        <button
          v-if="(modoAvancado || temParcelasAdicionais) && temParcelasMensalidade"
          class="btn btn-warning ms-2"
          @click="adicionarParcelaAdicional"
          title="Use para cobrar valores extras (ex.: turma individual, taxas, materiais)."
        >
          Parcelas Adicionais
        </button>

        <button type="button" class="btn btn-primary ms-2" aria-label="Acrescentar parcela" title="Acrescentar parcela sem alterar o total" :disabled="loading || (composicaoTroca && Number(composicaoTroca.complemento) <= 0)" @click="adicionarPagamento">
          <i class="bi bi-plus"></i>
        </button>
      </div>

      <!-- Observações -->
      <div class="form-group mt-3">
        <label for="observacoes">Observações:</label>
        <textarea id="observacoes" v-model="matricula.observacoes" class="form-control" rows="3"></textarea>
      </div>

    </div>

    <div v-if="matricula.id && temAlteracoesQueExigemMotivo" class="alert alert-info mt-3">
      <div class="mb-2">
        <strong>Motivo das alterações</strong> <span class="required">*</span>
        <div class="text-muted" style="font-size:12px;">
          Informe o motivo para salvar as alterações desta matrícula.
        </div>
      </div>

      <textarea
        v-model="motivoAlteracoes"
        class="form-control"
        rows="2"
        placeholder="Ex.: Ajuste solicitado pelo responsável / Correção de data / Mudança de plano, etc."
      ></textarea>

      <!-- opcional: preview do que será registrado -->
      <details class="mt-2">
        <summary style="cursor:pointer;">Ver alterações detectadas ({{ contadorAlteracoesUI }})</summary>
        <div class="mt-2" style="font-size:12px; white-space:pre-wrap;">
          <div v-for="(l, i) in alteracoesDetectadas" :key="i">{{ l }}</div>
        </div>
      </details>
    </div>

    <!-- Botões de ação -->
    <div class="form-actions d-flex justify-content-end">
      <button
        class="btn small-btn me-2"
        :class="loading ? 'btn-success' : 'btn-primary'"
        :disabled="!formValid || loading || !motivoValido || conferenciaTrocaIndisponivel"
        @click="salvarMatricula"
      >
        <i :class="loading ? 'bi bi-hourglass-split me-1' : 'bi bi-save me-1'"></i>
        {{ loading ? 'Salvando…' : (motivoValido ? 'Salvar' : 'Informe o motivo') }}
      </button>
      <button
        class="btn btn-secondary small-btn"
        :disabled="loading"
        @click="cancelar"
      >
        <i class="bi bi-x-circle me-1"></i>Cancelar
      </button>
    </div>

    <CarregarPessoaFisica
      :mostrar="modalPessoaAberto"
      :tipoPessoa="tipoPessoaSelecionada"
      :prospeccaoId="fixedProspeccaoId"
      @fechar="fecharModalPessoa"
      @selecionar="selecionarPessoa"
    />
  </div>
</template>

<script>
import { centavos, redistribuir, calcularPlano } from "@/utils/financeiroPlano";
import { paymentPatch } from "@/utils/operationalPaymentPayload";
import { turmaDisponivel, mensagemTurmaIndisponivel } from '@/utils/turmasSelecao';
import { reactive, ref, onMounted, watch, computed, getCurrentInstance, nextTick } from "vue";
import { useRoute, useRouter } from "vue-router"; // 🔹 Adicionei a importação correta
import axios from "@/services/axios";
import CarregarPessoaFisica from "@/components/modals/CarregarPessoaFisica.vue";
import globalUtils from "@/utils/globalUtils.js";
import { toast } from "vue3-toastify";

export default {
  components: { CarregarPessoaFisica },
  setup() {
    let originalTaxPayment = null;
    let originalMonthlyPayments = [];

    const hydrating = ref(false);
    const valorMensalidadeHydrated = ref(false);

    const pagMatLoaded = ref(false)
    const mensalidadeLoaded = ref(false);

    // no topo do setup()
    const dataVencimentoTouched = ref(false);
    const terminoVigenciaTouched = ref(false);

    // === MODO AVANÇADO ===
    // liga quando clicar no "+" e permanece até sair do formulário
    const modoAvancado = ref(false);

    // se já existir qualquer parcela adicional, mantém botão sempre visível
    const temParcelasAdicionais = computed(() =>
      Array.isArray(matricula.pagamentos) && matricula.pagamentos.some(p => p.adicional === true)
    );

    const contaPadraoGlobalId = computed(() => {
      const c = (contasFinanceiras.value || []).find(x => x.conta_padrao === true || x.conta_padrao === 1);
      return c?.id ? Number(c.id) : null;
    });

    const isDataVencimentoCompleta = (data) => {
      return /^\d{4}-\d{2}-\d{2}$/.test(String(data || ''));
    };

    /*
     * Ordenação determinística dos registros financeiros:
     * 1. vencimento;
     * 2. cheque devolvido/substituído;
     * 3. demais cheques;
     * 4. pagamento substituto/recebido;
     * 5. pendências e adicionais;
     * 6. ID como desempate estável.
     */
    const prioridadeRegistroMensalidade = (pagamento) => {
      if (pagamento?.adicional) return 5;

      const status = String(pagamento?.cheque_status || '').toUpperCase();
      if (status === 'SUBSTITUIDO' || status === 'DEVOLVIDO') return 1;
      if (pagamento?.pagamento_substituto === true) return 2;

      const possuiDadosCheque = Boolean(
        pagamento?.cheque_numero ||
        pagamento?.banco_numero ||
        pagamento?.agencia
      );

      if (possuiDadosCheque) return 3;
      if (pagamento?.data_pagamento || pagamento?.data_recebimento) return 4;

      return 5;
    };

    const compararParcelasMensalidade = (a, b) => {
      const dataA = String(a?.data_vencimento || '');
      const dataB = String(b?.data_vencimento || '');

      if (!dataA && !dataB) {
        return Number(a?.id || 0) - Number(b?.id || 0);
      }
      if (!dataA) return 1;
      if (!dataB) return -1;

      const comparacaoData = dataA.localeCompare(dataB);
      if (comparacaoData !== 0) return comparacaoData;

      const comparacaoPrioridade =
        prioridadeRegistroMensalidade(a) - prioridadeRegistroMensalidade(b);

      if (comparacaoPrioridade !== 0) return comparacaoPrioridade;

      return Number(a?.id || 0) - Number(b?.id || 0);
    };

    const parcelasMensalidade = computed(() => {
      return (matricula.pagamentos || [])
        .filter(pagamento => !pagamento.pgt_matricula)
        .slice()
        .sort(compararParcelasMensalidade);
    });

    const ordenarParcelasMensalidade = () => {
      const pagamentos = Array.isArray(matricula.pagamentos)
        ? matricula.pagamentos
        : [];

      const parcelasMensais = pagamentos.filter(
        pagamento => !pagamento.pgt_matricula
      );

      /*
       * Não altera a ordem enquanto existir alguma parcela
       * com vencimento vazio ou incompleto.
       */
      const existeDataIncompleta = parcelasMensais.some(
        pagamento => !isDataVencimentoCompleta(pagamento.data_vencimento)
      );

      if (existeDataIncompleta) {
        return;
      }

      const parcelasOrdenadas = [...parcelasMensais].sort(
        compararParcelasMensalidade
      );

      /*
       * Recoloca as parcelas ordenadas nas posições destinadas
       * às mensalidades, preservando eventuais outros pagamentos.
       */
      let indiceMensalidade = 0;

      matricula.pagamentos = pagamentos.map(pagamento => {
        if (pagamento.pgt_matricula) {
          return pagamento;
        }

        const parcelaOrdenada = parcelasOrdenadas[indiceMensalidade];
        indiceMensalidade++;

        return parcelaOrdenada;
      });
    };

    const temParcelasMensalidade = computed(() =>
      parcelasMensalidade.value.length > 0
    );

    const vencimentosContratuais = computed(() => {
      const datas = parcelasMensalidade.value
        .filter(pagamento => !pagamento.adicional && pagamento.data_vencimento)
        .map(pagamento => pagamento.data_vencimento);

      return [...new Set(datas)].sort();
    });

    const numeroParcelaContratual = (pagamento) => {
      if (!pagamento || pagamento.adicional) return null;

      const indice = vencimentosContratuais.value.indexOf(
        pagamento.data_vencimento
      );

      return indice >= 0 ? indice + 1 : null;
    };

    const quantidadeRegistrosFinanceiros = computed(() =>
      parcelasMensalidade.value.length
    );

    const isPagamentoSubstituto = (pagamento) => {
      if (!pagamento || pagamento.adicional) return false;

      /*
       * O backend atualizado devolve a relação de substituição de forma
       * explícita. O fallback por vencimento mantém compatibilidade durante
       * uma eventual atualização gradual dos ambientes.
       */
      if (typeof pagamento.pagamento_substituto === 'boolean') {
        return pagamento.pagamento_substituto;
      }

      const status = String(pagamento.cheque_status || '').toUpperCase();
      const possuiDadosCheque = Boolean(
        pagamento.cheque_numero ||
        pagamento.banco_numero ||
        pagamento.agencia
      );

      if (status === 'SUBSTITUIDO' || status === 'DEVOLVIDO' || possuiDadosCheque) {
        return false;
      }

      return parcelasMensalidade.value.some(origem =>
        origem !== pagamento &&
        Number(origem?.id || 0) !== Number(pagamento?.id || 0) &&
        origem?.data_vencimento === pagamento?.data_vencimento &&
        String(origem?.cheque_status || '').toUpperCase() === 'SUBSTITUIDO'
      );
    };

    const quantidadePagamentosSubstitutos = computed(() =>
      parcelasMensalidade.value.filter(isPagamentoSubstituto).length
    );

    const acrescimoPagamentosSubstitutos = computed(() => {
      const total = parcelasMensalidade.value
        .filter(isPagamentoSubstituto)
        .reduce((soma, substituto) => {
          const original = parcelasMensalidade.value.find(
            pagamento =>
              Number(pagamento?.id) ===
              Number(substituto?.pagamento_origem_id)
          )

          if (!original) return soma

          const valorSubstituto =
            valorNumericoPagamento(substituto.valor)

          const valorOriginal =
            valorNumericoPagamento(original.valor)

          const acrescimo =
            valorSubstituto - valorOriginal

          return soma + Math.max(acrescimo, 0)
        }, 0)

      return Number(total.toFixed(2))
    })

    const quantidadeParcelasAdicionais = computed(() =>
      parcelasMensalidade.value.filter(pagamento => pagamento?.adicional).length
    );

    const isPagamentoConciliado = (pagamento) => Boolean(
      pagamento?.bloqueado_por_conciliacao === true ||
      (Array.isArray(pagamento?.conciliacoes_ativas) && pagamento.conciliacoes_ativas.length > 0)
    );

    /*
     * O backend é a fonte principal da proteção. A verificação explícita
     * da conciliação funciona como defesa adicional para impedir edição
     * mesmo que uma versão intermediária da API não preencha
     * bloqueado_edicao corretamente.
     */
    const isPagamentoBloqueadoEdicao = (pagamento) => Boolean(
      pagamento?.bloqueado_edicao === true ||
      isPagamentoConciliado(pagamento)
    );

    const isParcelaNominalProtegida = p => isPagamentoBloqueadoEdicao(p) ||
      Boolean(p.id && (p.data_pagamento || p.data_recebimento || p.politica_cobranca_id != null));

    const formatarTipoEventoCheque = (tipo) => {
      const mapa = {
        DEVOLUCAO: 'Devolução',
        REAPRESENTACAO: 'Reapresentação',
        SUBSTITUICAO: 'Substituição',
        COMPENSACAO: 'Compensação'
      };

      const chave = String(tipo || '').toUpperCase();
      return mapa[chave] || String(tipo || 'Evento');
    };

    const formatarDataHistorico = (data) => {
      if (!data) return 'Data não informada';
      const s = String(data).slice(0, 10);
      const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      return m ? `${m[3]}/${m[2]}/${m[1]}` : String(data);
    };

    const descricaoMotivoBloqueio = (motivo) => {
      const mapa = {
        CONCILIACAO_ATIVA: 'conciliação bancária ativa',
        HISTORICO_CHEQUE: 'histórico de cheque',
        PAGAMENTO_SUBSTITUTO: 'pagamento substituto',
        REGISTRO_SUBSTITUIDO: 'registro substituído',
        CHEQUE_SUBSTITUIDO: 'cheque substituído',
        CHEQUE_DEVOLVIDO: 'cheque devolvido',
        CHEQUE_COMPENSADO: 'cheque compensado'
      };

      const chave = String(motivo || '').toUpperCase();
      return mapa[chave] || String(motivo || '').replaceAll('_', ' ').toLowerCase();
    };

    const formatarMotivosBloqueio = (motivos = []) => {
      const lista = Array.isArray(motivos) ? motivos : [];
      return lista.map(descricaoMotivoBloqueio).filter(Boolean).join(', ');
    };

    const motivoBloqueioPagamento = (pagamento) => {
      if (!pagamento) return 'Registro financeiro indisponível.';

      if (isPagamentoConciliado(pagamento)) {
        return 'Registro com conciliação bancária ativa. Desfaça a conciliação pelo fluxo financeiro antes de editar ou excluir.';
      }

      const motivos = formatarMotivosBloqueio(pagamento.motivos_bloqueio);
      if (motivos) {
        return `Registro protegido por ${motivos}.`;
      }

      if (pagamento.data_recebimento) {
        return 'Não é possível excluir um pagamento que já possui recebimento.';
      }

      if (pagamento.bloqueado_por_conciliacao === true) {
        return 'Registro protegido por conciliação bancária ativa.';
      }

      return 'Registro protegido pelo histórico financeiro.';
    };

    const temDetalhesPagamento = (pagamento) => Boolean(
      pagamento?.observacoes ||
      pagamento?.pagamento_substituto ||
      pagamento?.pagamento_substituto_id ||
      (Array.isArray(pagamento?.eventos_cheque) && pagamento.eventos_cheque.length) ||
      (Array.isArray(pagamento?.conciliacoes_ativas) && pagamento.conciliacoes_ativas.length) ||
      (Array.isArray(pagamento?.motivos_bloqueio) && pagamento.motivos_bloqueio.length)
    );

    const tituloDetalhesPagamento = (pagamento) => {
      if (isPagamentoBloqueadoEdicao(pagamento)) {
        return `Ver detalhes — ${motivoBloqueioPagamento(pagamento)}`;
      }
      return 'Ver dados complementares e histórico do registro';
    };

    const situacaoPagamento = (pagamento) => {
      const statusCheque = String(pagamento?.cheque_status || '').toUpperCase();

      if (statusCheque === 'SUBSTITUIDO') {
        return {
          texto: 'Cheque substituído',
          classe: 'bg-secondary'
        };
      }

      if (statusCheque === 'DEVOLVIDO') {
        return {
          texto: 'Cheque devolvido',
          classe: 'bg-danger'
        };
      }

      if (isPagamentoSubstituto(pagamento)) {
        return {
          texto: 'Pagamento substituto',
          classe: 'bg-info text-dark'
        };
      }

      if (pagamento?.data_recebimento) {
        return {
          texto: 'Recebido',
          classe: 'bg-success'
        };
      }

      if (pagamento?.data_pagamento) {
        return {
          texto: 'A receber',
          classe: 'bg-warning text-dark'
        };
      }

      return {
        texto: 'Pendente',
        classe: 'bg-light text-dark border'
      };
    };

    const classeLinhaPagamento = (pagamento) => ({
      'linha-cheque-substituido':
        String(pagamento?.cheque_status || '').toUpperCase() === 'SUBSTITUIDO',
      'linha-cheque-devolvido':
        String(pagamento?.cheque_status || '').toUpperCase() === 'DEVOLVIDO',
      'linha-pagamento-substituto': isPagamentoSubstituto(pagamento),
      'linha-parcela-adicional': pagamento?.adicional === true,
      'linha-pagamento-bloqueado': isPagamentoBloqueadoEdicao(pagamento),
      'linha-pagamento-conciliado': isPagamentoConciliado(pagamento)
    });

    const podeRemoverPagamento = (pagamento) => {
      if (!pagamento) return false;
      if (pagamento.bloqueado_exclusao === true) return false;
      if (isPagamentoConciliado(pagamento)) return false;

      const statusCheque = String(pagamento.cheque_status || '').toUpperCase();
      if (statusCheque === 'SUBSTITUIDO' || statusCheque === 'DEVOLVIDO') {
        return false;
      }

      if (pagamento.data_recebimento) return false;
      if (pagamento.bloqueado_por_conciliacao === true) return false;

      return true;
    };

    const alternarHistoricoPagamento = (pagamento) => {
      if (!pagamento) return;
      pagamento._mostrarHistorico = !pagamento._mostrarHistorico;
    };

    const temPendenciaDetalhesPagamento = (pagamento) => {
      if (!pagamento || isPagamentoBloqueadoEdicao(pagamento)) return false;
      if (!pagamento.forma_pagamento_id) return false;
      if (isAInformar(pagamento.forma_pagamento_id)) return false;

      if (!pagamento.conta_financeira_id) return true;

      if (
        isFormaComAutorizacao(pagamento.forma_pagamento_id) &&
        !(pagamento.numero_autorizacao || '').trim()
      ) {
        return true;
      }

      if (
        isCheque(pagamento.forma_pagamento_id) &&
        (pagamento.data_pagamento || '').trim()
      ) {
        return !(
          (pagamento.banco_numero || '').trim() &&
          (pagamento.agencia || '').trim() &&
          (pagamento.cheque_numero || '').trim()
        );
      }

      return false;
    };

    const route = useRoute(); // 🔹 Definição correta do `route`
    const router = useRouter(); // 🔹 Definição correta do `router`

    const motivoAlteracoes = ref('');

    // detecta alterações “ao vivo”
    const alteracoesDetectadas = computed(() => {
      if (!matricula.id) return [];                 // só edição
      if (!matriculaOriginal.value) return [];      // ainda carregando
      return detectarAlteracoesCompletas(matriculaOriginal.value, matricula);
    });

    const temAlteracoes = computed(() => alteracoesDetectadas.value.length > 0);

    const motivoValido = computed(() => {
      if (!temAlteracoesQueExigemMotivo.value) return true;
      return (motivoAlteracoes.value || '').trim().length >= 5;
    });

    const getNumeroParcelaNoArray = (p) => {
      // considera somente mensalidades (não pgt_matricula)
      const lista = (matricula.pagamentos || []).filter(x => x && !x.pgt_matricula);

      // tenta achar pelo id (melhor)
      if (p?.id != null) {
        const idx = lista.findIndex(x => Number(x.id) === Number(p.id));
        if (idx >= 0) return idx + 1;
      }

      // fallback por vencimento + valor + adicional
      const idx2 = lista.findIndex(x =>
        (x?.data_vencimento || '') === (p?.data_vencimento || '') &&
        Number(x?.valor || 0) === Number(p?.valor || 0) &&
        !!x?.adicional === !!p?.adicional
      );
      return idx2 >= 0 ? idx2 + 1 : null;
    };

    const qtdParcelasAlteradas = computed(() => {
      const arr = alteracoesDetectadas.value || [];
      const m = arr.find(l => String(l).includes('Parcelas alteradas:'));
      if (!m) return 0;
      const n = Number(String(m).match(/Parcelas alteradas:\s*(\d+)/)?.[1]);
      return Number.isFinite(n) ? n : 0;
    });

    const contadorAlteracoesUI = computed(() => {
      // se tiver parcelas alteradas, use esse número
      if (qtdParcelasAlteradas.value > 0) return qtdParcelasAlteradas.value;

      // caso contrário, usa o total de linhas mesmo
      return (alteracoesDetectadas.value || []).length;
    });

    const isAlteracaoSomenteRecebimento = (linha) => {
      const s = String(linha || '').trim();

      // 1) linhas-resumo nunca exigem motivo por si só
      if (/^•\s*Parcelas alteradas:\s*\d+/i.test(s)) return true;
      if (/^•\s*Pagamento da Matrícula alterado$/i.test(s)) return true;

      // 2) detalhe de parcela ou pagamento da matrícula
      // só deve ser dispensado se contiver APENAS alteração de recebimento
      const contemReceb = /Receb\.:/i.test(s);

      const contemOutraMudanca =
        /Venc\.|Valor|Forma|Conta|Pgto|Aut\.|Banco|Agência|Cheque nº|Obs\./i.test(
          s.replace(/Receb\.:/gi, '')
        );

      if (contemReceb && !contemOutraMudanca) return true;

      return false;
    };

    const alteracoesQueExigemMotivo = computed(() => {
      const lista = alteracoesDetectadas.value || [];
      return lista.filter(linha => !isAlteracaoSomenteRecebimento(linha));
    });

    const temAlteracoesQueExigemMotivo = computed(() =>
      alteracoesQueExigemMotivo.value.length > 0
    );

    // No início do setup, capture e armazene o valor inicial da query
    const initialProspeccaoId = ref(route.query.prospeccaoId || null);
    const prospeccaoId = ref(initialProspeccaoId.value);
    const fixedProspeccaoId = ref(null);

    // atalho: ajusta a quantidade sem apagar o que já está preenchido
    const ajustarTurmasExatas = () => ensureMinSlots(plano.aulas_semana);

    const matricula = reactive({

      numero_contrato: "", // 🔹 Novo campo para armazenar o número do contrato
      aluno_nome: '',
      responsavel_nome: '',

      categoria_id: '',
      tipo_plano_id: '',
      nivel_id: '',
      inicio_vigencia: '',
      termino_vigencia: '',
      aulas_semana: 0, // Número de aulas por semana
      quantidade_parcelas: 0,
      turmas: [],

      data_matricula: new Date().toISOString().split("T")[0],
      valor: "",
      desconto: 0,
      valor_com_desconto: "",
      forma_pagamento_id: "",
      data_vencimento: "",
      data_pagamento: "",
      convenio_id: "",
      pagamento_pendente: true,

      desconto_mensalidade: 0,
      valor_com_desconto_mensalidade: 0,
      primeiro_vencimento: "",
      dia_vencimento_mensalidade: "",
      quantidade_pagamentos: 0,
      valor_total_mensalidade: "",
      pagamentos: [],
      observacoes: "",
      direito_imagem: null,
      encerrar_vigencia_final_ano: false,

      // inicializa um objeto vazio para evitar undefined
      pagamento_matricula: {
        id: null,                     // ← adiciona esse campo
        data_pagamento: "",
        data_recebimento: "",
        data_vencimento: "",
        forma_pagamento_id: "",
        valor: 0,
        conta_financeira_id: "",
        banco_numero: "",
        agencia: "",
        cheque_numero: "",
        adicional: false,
        contrato: "",
        pgt_matricula: true,
        observacoes: null
      }
    });

    // =====================================================
    // AUDITORIA (EDIÇÃO): snapshot + diff completo
    // =====================================================
    const matriculaOriginal = ref(null);

    const deepClone = (obj) => JSON.parse(JSON.stringify(obj));

    const isEmpty = (v) =>
      v === null || v === undefined || (typeof v === 'string' && v.trim() === '');

    const normalizeNumber = (v) => {
      const n = Number(v);
      return Number.isFinite(n) ? n : 0;
    };

    const normalizeBool = (v) => {
      if (v === true || v === 'true' || v === 1 || v === '1') return true;
      if (v === false || v === 'false' || v === 0 || v === '0') return false;
      return null;
    };

    const toBRDate = (iso) => {
      if (!iso) return '';
      const s = String(iso).slice(0, 10);
      const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (!m) return String(iso);
      return `${m[3]}/${m[2]}/${m[1]}`;
    };

    const toBRMoney = (v) => globalUtils.formatarValor(normalizeNumber(v));

    const toPct = (v) => {
      if (isEmpty(v)) return '';
      const n = Number(v);
      return Number.isFinite(n) ? String(n) : String(v);
    };

    const toSimNao = (v) => {
      const b = normalizeBool(v);
      if (b === true) return 'Sim';
      if (b === false) return 'Não';
      return '';
    };

    const toStr = (v) => (isEmpty(v) ? '' : String(v).trim());
    const toHHMM = (h) => (h ? String(h).slice(0, 5) : '');

    const labelCategoria = (id) => {
      const c = (categorias.value || []).find(x => Number(x.id) === Number(id));
      return c ? `${c.nome} (#${c.id})` : (id ? `#${id}` : '');
    };

    const labelTipoPlano = (id) => {
      const t = (tiposPlano.value || []).find(x => Number(x.id) === Number(id));
      return t ? `${t.nome} (${t.duracao} dias) (#${t.id})` : (id ? `#${id}` : '');
    };

    const labelNivel = (id) => {
      const n = (niveis.value || []).find(x => Number(x.id) === Number(id));
      return n ? `${n.nivel} (#${n.id})` : (id ? `#${id}` : '');
    };

    const labelConvenio = (id) => {
      const c = (convenios.value || []).find(x => Number(x.id) === Number(id));
      return c ? `${c.nome} (#${c.id})` : (id ? `#${id}` : '');
    };

    const labelForma = (id) => {
      const f = (formasPagamento.value || []).find(x => Number(x.id) === Number(id));
      return f ? `${f.nome} (#${f.id})` : (id ? `#${id}` : '');
    };

    const labelConta = (id) => {
      const c = (contasFinanceiras.value || []).find(x => Number(x.id) === Number(id));
      if (!id) return '';
      return c
        ? `${c.apelido || 'Sem apelido'} – ${c.agencia || 'Sem agência'} – ${c.numero_conta || 'Sem número'} (#${c.id})`
        : `#${id}`;
    };

    // Normaliza turmas para comparar de forma estável
    const normalizeTurmas = (arr) => {
      const list = Array.isArray(arr) ? arr : [];
      return list
        .filter(t => t && (t.turma_id || t.id))
        .map(t => {
          // turma_id no form pode ser "1-quinta-feira-17:00:00"
          const raw = String(t.turma_id || '').trim();
          const id = Number(raw.split('-')[0] || t.id || 0) || null;
          const dia = toStr(t.dia || t.dia_semana);
          const horario = toStr(t.horario);

          return {
            key: `${id || ''}__${dia}__${toHHMM(horario)}`,
            turma_id: id,
            dia,
            horario,
            // instrutor propositalmente ignorado no diff (campo derivado)
            nivel_id: t.nivel_id != null ? Number(t.nivel_id) : null
          };        })
        .sort((a,b) => a.key.localeCompare(b.key));
    };

    const formatTurmasHuman = (arr) => {
      const t = normalizeTurmas(arr);
      if (!t.length) return '';
      return t.map(x => {
        const niv = x.nivel_id ? `Nível: ${labelNivel(x.nivel_id)} | ` : '';
        const inst = x.instrutor ? ` | Instrutor: ${x.instrutor}` : '';
        return `${niv}Turma: ${x.turma_id} | Dia: ${x.dia} | Hora: ${toHHMM(x.horario)}${inst}`;
      }).join(' ; ');
    };

    // Pagamento da matrícula (objeto único)
    const normalizePagMat = (pg) => {
      const p = pg || {};
      return {
        forma_pagamento_id: p.forma_pagamento_id != null ? Number(p.forma_pagamento_id) : null,
        conta_financeira_id: p.conta_financeira_id != null ? Number(p.conta_financeira_id) : null,
        data_vencimento: toStr(p.data_vencimento),
        data_pagamento: toStr(p.data_pagamento),
        data_recebimento: toStr(p.data_recebimento),
        numero_autorizacao: toStr(p.numero_autorizacao),
        banco_numero: toStr(p.banco_numero),
        agencia: toStr(p.agencia),
        cheque_numero: toStr(p.cheque_numero),
        observacoes: toStr(p.observacoes),
        valor: normalizeNumber(p.valor),
      };
    };

    // Parcelas (mensalidades): comparar por id quando existe; senão fallback por "vencimento+valor+adicional+index"
    const normalizeParcelas = (arr) => {
      const list = Array.isArray(arr) ? arr : [];
      return list
        .filter(p => p && !p.pgt_matricula) // mensalidades apenas
        .map((p, idx) => {
          const id = p.id != null ? Number(p.id) : null;
          const venc = toStr(p.data_vencimento);
          const key = id ? `ID:${id}` : `K:${idx}__${venc}__${normalizeNumber(p.valor)}__${!!p.adicional}`;
          return {
            key,
            id,
            idx,
            adicional: !!p.adicional,
            forma_pagamento_id: p.forma_pagamento_id != null ? Number(p.forma_pagamento_id) : null,
            conta_financeira_id: p.conta_financeira_id != null ? Number(p.conta_financeira_id) : null,
            data_vencimento: venc,
            data_pagamento: toStr(p.data_pagamento),
            data_recebimento: toStr(p.data_recebimento),
            valor: normalizeNumber(p.valor),
            numero_autorizacao: toStr(p.numero_autorizacao),
            banco_numero: toStr(p.banco_numero),
            agencia: toStr(p.agencia),
            cheque_numero: toStr(p.cheque_numero),
            observacoes: toStr(p.observacoes),
          };
        })
        .sort((a,b) => a.key.localeCompare(b.key));
    };

    const formatParcelaShort = (p) => {
      const tag = p.id ? `Parcela #${p.id}` : `Parcela (nova)`;
      const ad = p.adicional ? 'Adicional' : 'Plano';
      const forma = labelForma(p.forma_pagamento_id);
      const conta = labelConta(p.conta_financeira_id);

      const parts = [
        `${tag} [${ad}]`,
        p.data_vencimento ? `Venc.: ${toBRDate(p.data_vencimento)}` : null,
        `Valor: ${toBRMoney(p.valor)}`,
        forma ? `Forma: ${forma}` : null,
        conta ? `Conta: ${conta}` : null,
        p.data_pagamento ? `Pgto: ${toBRDate(p.data_pagamento)}` : null,
        p.data_recebimento ? `Receb.: ${toBRDate(p.data_recebimento)}` : null,
        p.numero_autorizacao ? `Aut.: ${p.numero_autorizacao}` : null,
      ].filter(Boolean);

      const cheque = [p.banco_numero, p.agencia, p.cheque_numero].some(x => !isEmpty(x))
        ? `Cheque: Banco ${p.banco_numero || '-'} / Ag ${p.agencia || '-'} / Nº ${p.cheque_numero || '-'}`
        : null;

      if (cheque) parts.push(cheque);

      return parts.join(' | ');
    };

    const diffField = (changes, label, before, after) => {
      const b = isEmpty(before) ? '' : String(before);
      const a = isEmpty(after) ? '' : String(after);
      if (b !== a) {
        changes.push(`• ${label}: de "${b || '(vazio)'}" para "${a || '(vazio)'}"`);
      }
    };

    const diffComplex = (changes, label, beforeObj, afterObj, formatter) => {
      const b = formatter(beforeObj);
      const a = formatter(afterObj);
      if (b !== a) {
        changes.push(`• ${label}:\n    Antes: ${b || '(vazio)'}\n    Depois: ${a || '(vazio)'}`);
      }
    };

    const detectarAlteracoesCompletas = (antes, depois) => {
      const changes = [];
      if (!antes || !depois) return changes;

      // ---------------------------
      // CAMPOS PRINCIPAIS (matriculas)
      // ---------------------------
      diffField(changes, 'Aluno', toStr(antes.aluno_nome || antes.aluno_id), toStr(depois.aluno_nome || depois.aluno_id));
      diffField(changes, 'Responsável', toStr(antes.responsavel_nome || antes.responsavel_id), toStr(depois.responsavel_nome || depois.responsavel_id));

      diffField(changes, 'Categoria', labelCategoria(antes.categoria_id), labelCategoria(depois.categoria_id));
      diffField(changes, 'Tipo de Plano', labelTipoPlano(antes.tipo_plano_id), labelTipoPlano(depois.tipo_plano_id));
      diffField(changes, 'Nível', labelNivel(antes.nivel_id), labelNivel(depois.nivel_id));

      diffField(changes, 'Início da Vigência', toBRDate(antes.inicio_vigencia), toBRDate(depois.inicio_vigencia));
      diffField(changes, 'Término da Vigência', toBRDate(antes.termino_vigencia), toBRDate(depois.termino_vigencia));

      diffField(changes, 'Aulas por Semana', String(antes.aulas_semana ?? ''), String(depois.aulas_semana ?? ''));
      diffField(changes, 'Qtd. Parcelas (Plano)', String(antes.quantidade_parcelas ?? ''), String(depois.quantidade_parcelas ?? ''));

      diffComplex(changes, 'Turmas', antes.turmas, depois.turmas, formatTurmasHuman);

      diffField(changes, 'Data da Matrícula', toBRDate(antes.data_matricula), toBRDate(depois.data_matricula));
      diffField(changes, 'Valor da Matrícula', toBRMoney(antes.valor), toBRMoney(depois.valor));
      diffField(changes, 'Desconto Matrícula (%)', toPct(antes.desconto), toPct(depois.desconto));
      diffField(changes, 'Valor Matrícula com Desconto', toBRMoney(antes.valor_com_desconto), toBRMoney(depois.valor_com_desconto));
      diffField(changes, 'Convênio', labelConvenio(antes.convenio_id), labelConvenio(depois.convenio_id));

      diffField(changes, 'Autoriza Uso de Imagem', toSimNao(antes.direito_imagem), toSimNao(depois.direito_imagem));

      // ---------------------------
      // MENSALIDADE (matriculas + parcelas)
      // ---------------------------
      diffField(changes, 'Valor Mensalidade', toBRMoney(antes.valor_mensalidade), toBRMoney(depois.valor_mensalidade));
      diffField(changes, 'Desconto Mensalidade (%)', toPct(antes.desconto_mensalidade), toPct(depois.desconto_mensalidade));
      diffField(changes, 'Valor Mensalidade com Desconto', toBRMoney(antes.valor_com_desconto_mensalidade), toBRMoney(depois.valor_com_desconto_mensalidade));
      diffField(changes, '1º Vencimento', toBRDate(antes.primeiro_vencimento), toBRDate(depois.primeiro_vencimento));
      diffField(changes, 'Qtd. Pagamentos', String(antes.quantidade_pagamentos ?? ''), String(depois.quantidade_pagamentos ?? ''));
      diffField(changes, 'Valor Total Mensalidade', toBRMoney(antes.valor_total_mensalidade), toBRMoney(depois.valor_total_mensalidade));

      // ---------------------------
      // PAGAMENTO DA MATRÍCULA (matriculas_pagamentos)
      // ---------------------------
      const pagMatAntes = normalizePagMat(antes.pagamento_matricula);
      const pagMatDepois = normalizePagMat(depois.pagamento_matricula);

      const pagMatDiffs = [];

      const cmpPagMat = (lbl, va, vb, fmt = (x) => x) => {
        const A1 = fmt(va);
        const B1 = fmt(vb);
        if (String(A1) !== String(B1)) {
          pagMatDiffs.push(`${lbl}: "${A1 || '(vazio)'}" → "${B1 || '(vazio)'}"`);
        }
      };

      cmpPagMat('Forma',   pagMatAntes.forma_pagamento_id, pagMatDepois.forma_pagamento_id, labelForma);
      cmpPagMat('Venc.',   pagMatAntes.data_vencimento,    pagMatDepois.data_vencimento,    toBRDate);
      cmpPagMat('Valor',   pagMatAntes.valor,              pagMatDepois.valor,              toBRMoney);
      cmpPagMat('Pgto',    pagMatAntes.data_pagamento,     pagMatDepois.data_pagamento,     toBRDate);
      cmpPagMat('Receb.',  pagMatAntes.data_recebimento,   pagMatDepois.data_recebimento,   toBRDate);
      cmpPagMat('Conta',   pagMatAntes.conta_financeira_id,pagMatDepois.conta_financeira_id,labelConta);
      cmpPagMat('Aut.',    pagMatAntes.numero_autorizacao, pagMatDepois.numero_autorizacao, toStr);
      cmpPagMat('Banco',   pagMatAntes.banco_numero,       pagMatDepois.banco_numero,       toStr);
      cmpPagMat('Agência', pagMatAntes.agencia,            pagMatDepois.agencia,            toStr);
      cmpPagMat('Cheque nº', pagMatAntes.cheque_numero,    pagMatDepois.cheque_numero,      toStr);
      cmpPagMat('Obs.',    pagMatAntes.observacoes,        pagMatDepois.observacoes,        toStr);

      if (pagMatDiffs.length) {
        changes.push(`• Pagamento da Matrícula alterado`);
        pagMatDiffs.forEach(d => changes.push(`    ${d}`));
      }

      // ---------------------------
      // PARCELAS DE MENSALIDADE (pagamentos_matriculas)
      // ---------------------------
      const A = normalizeParcelas(antes.pagamentos);
      const B = normalizeParcelas(depois.pagamentos);

      // Mapas por key
      const mapA = new Map(A.map(x => [x.key, x]));
      const mapB = new Map(B.map(x => [x.key, x]));

      // removidas
      const removed = A.filter(x => !mapB.has(x.key));
      // adicionadas
      const added = B.filter(x => !mapA.has(x.key));
      // possivelmente alteradas (mesma key)
      const commonKeys = B.filter(x => mapA.has(x.key)).map(x => x.key);

      if (added.length || removed.length) {
        if (added.length) {
          changes.push(`• Parcelas adicionadas: ${added.length}`);
          added.slice(0, 10).forEach(p => changes.push(`    + ${formatParcelaShort(p)}`));
          if (added.length > 10) changes.push(`    + ... (${added.length - 10} outras)`);
        }
        if (removed.length) {
          changes.push(`• Parcelas removidas: ${removed.length}`);
          removed.slice(0, 10).forEach(p => changes.push(`    - ${formatParcelaShort(p)}`));
          if (removed.length > 10) changes.push(`    - ... (${removed.length - 10} outras)`);
        }
      }

      // alterações internas por parcela
      const parcelaChanges = [];
      for (const k of commonKeys) {
        const a = mapA.get(k);
        const b = mapB.get(k);

        const diffs = [];

        const cmp = (lbl, va, vb, fmt = (x)=>x) => {
          const A1 = fmt(va);
          const B1 = fmt(vb);
          if (String(A1) !== String(B1)) diffs.push(`${lbl}: "${A1 || '(vazio)'}" → "${B1 || '(vazio)'}"`);
        };

        cmp('Venc.', a.data_vencimento, b.data_vencimento, toBRDate);
        cmp('Valor', a.valor, b.valor, toBRMoney);
        cmp('Forma', a.forma_pagamento_id, b.forma_pagamento_id, labelForma);
        cmp('Aut.', a.numero_autorizacao, b.numero_autorizacao, toStr);
        cmp('Conta', a.conta_financeira_id, b.conta_financeira_id, labelConta);
        cmp('Pgto', a.data_pagamento, b.data_pagamento, toBRDate);
        cmp('Receb.', a.data_recebimento, b.data_recebimento, toBRDate);
        cmp('Obs.', a.observacoes, b.observacoes, toStr);

        // cheque
        cmp('Banco', a.banco_numero, b.banco_numero, toStr);
        cmp('Agência', a.agencia, b.agencia, toStr);
        cmp('Cheque nº', a.cheque_numero, b.cheque_numero, toStr);

        if (diffs.length) {
          const num = getNumeroParcelaNoArray(a);
          const titulo = num ? `Parcela ${num}` : (a.id ? `Parcela #${a.id}` : 'Parcela');

          parcelaChanges.push(`    • ${titulo}\n      ${diffs.join(' | ')}`);
        }
      }

      if (parcelaChanges.length) {
        changes.push(`• Parcelas alteradas: ${parcelaChanges.length}`);
        parcelaChanges.slice(0, 10).forEach(x => changes.push(x));
        if (parcelaChanges.length > 10) changes.push(`    • ... (${parcelaChanges.length - 10} outras)`);
      }

      // Observações gerais do formulário (texto livre)
      diffField(changes, 'Observações (campo livre)', toStr(antes.observacoes), toStr(depois.observacoes));

      return changes;
    };

    const montarAuditoriaAlteracoes = (matriculaId) => {
      if (!matricula.id) return;               // só edição
      if (!matriculaOriginal.value) return;

      const changes = detectarAlteracoesCompletas(matriculaOriginal.value, matricula);
      if (!changes.length) return;

      // ✅ motivo obrigatório quando há alterações
      const motivo = (motivoAlteracoes.value || '').trim();

      if (temAlteracoesQueExigemMotivo.value && !motivo) {
        toast?.error?.('Informe o Motivo das alterações para salvar.') || alert('Informe o Motivo das alterações para salvar.');
        throw new Error('Motivo das alterações não informado.');
      }

      const hojeISO = new Date().toISOString().slice(0, 10);
      const agora = new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
      const userName = instance?.appContext.config.globalProperties.$globalState.userName || '';

      const cabecalho = [
        `Alterações na Matrícula nº ${matriculaId}${matricula.numero_contrato ? ` (Contrato nº ${matricula.numero_contrato})` : ''}`,
        `Data/Hora: ${toBRDate(hojeISO)} ${agora}`,
        userName ? `Usuário: ${userName}` : null,
      ].filter(Boolean).join('\n');

      // ✅ Mensagem final com Motivo + Alterações
      const mensagem =
      `${cabecalho}
      ${motivo ? `\n\nMotivo:\n${motivo}` : ''}

      Alterações:
      ${changes.join('\n')}`;

      return { observacao: mensagem, nome_usuario: userName, data_observacao: hojeISO };
    };

    // =====================================================
    // FIM - AUDITORIA (EDIÇÃO): snapshot + diff completo
    // =====================================================


    const contasFinanceiras = ref([]);
    const carregarContasFinanceiras = async () => {
      try {
        const response = await axios.get('/financeiro/contas-financeiras/selecao-operacional', { params: { fluxo: route.params.id ? 'edicao' : 'matricula' } });
        contasFinanceiras.value = response.data.data || response.data;
      } catch (error) {
        console.error("❌ Erro ao carregar contas financeiras:", error);
      }
    };
    const categorias = ref([]);
    const tiposPlano = ref([]);
    const niveis = ref([]);
    const turmas = ref([]);
    const novaTurma = reactive({ turma_id: '' });
    const modalPessoaAberto = ref(false);
    const tipoPessoaSelecionada = ref('');
    const aulasSemanaOptions = ref([1, 2, 3, 4, 5, 6]); // Número de aulas disponíveis por semana
    const parcelasOptions = ref(Array.from({ length: 12 }, (_, i) => i + 1)); // De 1 a 12 parcelas
    const turmasDisponiveis = ref([]); // Lista de turmas filtradas
    const todasTurmasDisponiveis = ref([]);
    const turmasOriginais = ref([]);
    const turmaEstaDisponivel = t => turmaDisponivel(t.turma_id, turmasDisponiveisPorNivel(modoAvancado.value ? (t.nivel_id || matricula.nivel_id) : matricula.nivel_id));
    const turmaHistoricaPreservada = t => turmasOriginais.value.some(o => o.turma_id === t.turma_id && Number(o.nivel_id) === Number(t.nivel_id) && Number(t.nivel_id) === Number(modoAvancado.value ? t.nivel_id : matricula.nivel_id));


    // Retorna as turmas de um nível específico, ordenadas
    function turmasDisponiveisPorNivel(nivelId) {
      const nId = Number(nivelId);
      const base = (todasTurmasDisponiveis.value || []).filter(t => Number(t.nivel_id) === nId);
      // manter mesma ordenação usada atualmente: id -> dia -> horário
      return base.sort((a, b) =>
        (Number(a.id) - Number(b.id)) ||
        (idxDia(a.dia) - idxDia(b.dia)) ||
        (toMin(a.horario) - toMin(b.horario))
      );
    }

    const formasPagamento = ref([]);
    const convenios = ref([]);

    const diasVencimentoCobranca = ref([]);
    const carregandoDiasVencimentoCobranca = ref(false);
    const erroPoliticaCobranca = ref('');

    const previewMensalidadeManaged =
      ref(null);

    const carregandoPreviewMensalidadeManaged =
      ref(false);

    const erroPreviewMensalidadeManaged =
      ref('');

    let sequenciaPreviewMensalidadeManaged =
      0;

    let skipTurmasRecreation = false;

    const plano = reactive({
      aulas_semana: null,
      quantidade_parcelas: null,
      valor_mensalidade: 0,
    });

    const abrirModalPessoa = (tipo) => {
      tipoPessoaSelecionada.value = tipo;
      modalPessoaAberto.value = true;
    };

    const fecharModalPessoa = () => {
      modalPessoaAberto.value = false;
      // tipoPessoaSelecionada.value = null;
    };

    const selecionarPessoa = (payload) => {
      if (payload.prospeccaoId) {
        prospeccaoId.value = payload.prospeccaoId;
      }

      if (tipoPessoaSelecionada.value === "responsavel") {
        matricula.responsavel_id   = payload.pessoa.id;
        matricula.responsavel_nome = payload.pessoa.nome;
      }
      else if (tipoPessoaSelecionada.value === "aluno") {
        matricula.aluno_id   = payload.pessoa.id;
        matricula.aluno_nome = payload.pessoa.nome;

        axios.get('/operacional/matriculas', {
          params: { aluno_id: matricula.aluno_id }
        })
        .then(response => {
          // extrai o array completo
          const todos = Array.isArray(response.data.data)
            ? response.data.data
            : Array.isArray(response.data)
              ? response.data
              : [];

          // **filtra** apenas os que têm mesmo aluno_id
          const registros = todos.filter(rec => Number(rec.aluno_id) === Number(matricula.aluno_id));

          // verifica duplicidade
          const existeOutro = registros.some(rec => {
            // em edição, ignora o próprio registro
            if (route.params.id) {
              return rec.id !== Number(route.params.id);
            }
            // em novo, qualquer conta
            return true;
          });

          if (existeOutro) {
            toast.error("Já existe matrícula para esse aluno.");
            matricula.aluno_id   = null;
            matricula.aluno_nome = "";
          }
        })
        .catch(error => {
          console.error("Erro ao verificar matrícula do aluno:", error);
        });
      }

      fecharModalPessoa();
    };

    const carregarCategorias = async () => {
      try {
        const response = await axios.get('/cadastros/categorias-de-planos');
        const todasCategorias = Array.isArray(response.data) ? response.data : response.data.data || [];

        // 🔹 Filtra apenas categorias ativas
        categorias.value = todasCategorias.filter(categoria => categoria.ativo);

      } catch (error) {
        console.error('❌ Erro ao carregar categorias de planos:', error);
      }
    };

    const carregarTiposPlano = async () => {
      if (!matricula.categoria_id) return; // só carrega com categoria

      try {
        // 1) Planos de matrícula da categoria (sem paginação -)
    const respPlanos = await axios.get('/cadastros/planos-matriculas', {
      params: {
        categoria_id: matricula.categoria_id,
        ativo: 'true',
        sort: 'tipo',     // se o endpoint ignorar, tudo bem
        order: 'asc',
        page: 1,
        limit: 10000,     // ⚡ pega tudo para o select
      }
    });

        const planosArray = respPlanos?.data?.data || respPlanos?.data || [];
        const planosAtivos = Array.isArray(planosArray)
          ? planosArray.filter(plano => ('ativo' in plano) ? (plano.ativo === true || plano.ativo === 1) : true)
          : [];

        // 2) Tipos de planos (sem paginação, ordenado por duração no back)
    const respTipos = await axios.get('/cadastros/tipos-planos', {
      params: {
        ativo: 'true',
        sort: 'duracao',
        order: 'asc',
        page: 1,
        limit: 10000,     // ⚡ pega tudo para o select
      }
    });
        const tiposPlanosLista = respTipos?.data?.data || respTipos?.data || [];

        // 3) Casa plano ativo com tipo pelo nome e monta a lista
        const lista = planosAtivos.map(plano => {
          const tipoEncontrado = tiposPlanosLista.find(
            tipo => (tipo?.nome || '').trim() === (plano?.tipo || '').trim()
          );
          return {
            id: tipoEncontrado ? tipoEncontrado.id : null,
            nome: plano?.tipo || '',
            duracao: tipoEncontrado?.duracao ?? null,
            aulas_semana: plano?.aulas_semana ?? 0,
            quantidade_parcelas: plano?.quantidade_parcelas ?? 0,
            valor_mensalidade: plano?.valor_parcela ?? 0,
          };
        }).filter(t => t.id !== null);

        // 4) Garante ordenação por duração asc no front (fallbacks seguros)
        lista.sort((a, b) => {
          const da = Number.isFinite(a.duracao) ? a.duracao : Number.MAX_SAFE_INTEGER;
          const db = Number.isFinite(b.duracao) ? b.duracao : Number.MAX_SAFE_INTEGER;
          return da - db;
        });

        tiposPlano.value = lista;

        // 5) Se já existe um tipo selecionado…
        if (matricula.tipo_plano_id) {
          const planoSel = tiposPlano.value.find(p => p.id === matricula.tipo_plano_id);
          if (planoSel && !matricula.id) {
            // ❗ NÃO sobrescreva o valor_mensalidade em EDIÇÃO quando já veio do banco
            if (!matricula.id || !valorMensalidadeHydrated.value) {
              matricula.valor_mensalidade = planoSel.valor_mensalidade;
            }
            // quantidade_pagamentos você pode manter como está (normalmente faz sentido atualizar):
            const quantidadePlano = Number(planoSel.quantidade_parcelas) || 0;

            if (
              matricula.encerrar_vigencia_final_ano &&
              matricula.inicio_vigencia &&
              matricula.termino_vigencia
            ) {
              /*
              * Quando a vigência termina no final do ano, a quantidade
              * efetiva deve ser calculada pela janela da matrícula,
              * e não copiada da configuração padrão do plano.
              */
              aplicarQuantidadePagamentosAteTermino();
            } else {
              plano.quantidade_parcelas = quantidadePlano;
              matricula.quantidade_parcelas = quantidadePlano;
              matricula.quantidade_pagamentos = quantidadePlano;
            }

            if (typeof calcularValorComDescontoMensalidade === 'function') {
              calcularValorComDescontoMensalidade();
            }
            if (matricula.inicio_vigencia && typeof calcularTerminoVigencia === 'function') {
              calcularTerminoVigencia();
            }
          }
        }
      } catch (error) {
        console.error("❌ Erro ao carregar tipos de planos:", error?.response?.data || error);
        tiposPlano.value = [];
      }
    };

    const carregarNiveis = async () => {
      try {
        const { data } = await axios.get('/cadastros/niveis-turmas', {
          params: { page: 1, limit: 10000 } // pega todos
        });
        const dados = data?.data ?? data ?? [];
        niveis.value = Array.isArray(dados) ? dados.filter(n => n.ativo) : [];
      } catch (error) {
        console.error('❌ Erro ao carregar níveis:', error);
        niveis.value = [];
      }
    };


    // dia -> índice (1=seg .. 7=dom), cobrindo variações ("terça-feira", "Sab.", etc.)
    const idxDia = (d) => {
      let s = String(d || '')
        .normalize('NFD').replace(/\p{Diacritic}/gu, '')
        .toLowerCase().replace(/-?feira/g, '').trim();
      s = s.split(/\s+/)[0].slice(0,3); // "ter", "sab", etc.
      return {seg:1, ter:2, qua:3, qui:4, sex:5, sab:6, dom:7}[s] ?? 99;
    };

    // HH:MM(:SS) -> minutos
    const toMin = (h) => {
      const m = String(h||'').match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
      if (!m) return Number.MAX_SAFE_INTEGER;
      return (+m[1])*60 + (+m[2]);
    };

    // Exibir sem segundos (não altera o valor salvo)
    const formatHora = (h) => {
      const m = String(h||'').match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
      return m ? `${m[1].padStart(2,'0')}:${m[2]}` : String(h||'');
    };


    const carregarTurmas = async () => {
      try {
        const { data } = await axios.get('/cadastros/dias-horarios-turmas', { params: { ativo: true } });
        const linhas = data?.data || data || [];

        // mapa nome do nível -> id (precisa de `niveis` já carregado)
        const nivelNomeToId = new Map((niveis.value || []).map(n => [String(n.nivel).trim(), Number(n.id)]));

        const processadas = (Array.isArray(linhas) ? linhas : []).map(t => {
          const nivelNome = t.nivel_nome || t.nivel || '';
          const nivelId = Number(t.nivel_id ?? nivelNomeToId.get(String(nivelNome).trim()) ?? null);
          return {
            id:        t.turma_id ?? t.id,
            dia:       t.dia_semana ?? t.dia,
            horario: String(t.horario || '').slice(0,8),
            horario_fmt: formatHora(t.horario),
            instrutor: t.instrutor_nome || 'Não informado',
            nivel:     nivelNome,
            nivel_id:  nivelId,
            label: `${t.turma_id ?? t.id} | ${t.instrutor_nome || 'Não informado'} | ${(t.dia_norm || t.dia_semana || t.dia)} | ${formatHora(t.horario)}`
          };
        }).filter(t => Number.isFinite(t.nivel_id));

        // preenche a base completa e a lista do modo básico (nível principal)
        todasTurmasDisponiveis.value = processadas;

        const nPrincipal = Number(matricula.nivel_id);
        turmasDisponiveis.value = processadas
          .filter(t => Number(t.nivel_id) === nPrincipal)
          .sort((a, b) =>
            (Number(a.id) - Number(b.id)) ||
            (idxDia(a.dia) - idxDia(b.dia)) ||
            (toMin(a.horario) - toMin(b.horario))
          );
      } catch (e) {
        console.error("❌ Erro ao carregar turmas:", e);
        todasTurmasDisponiveis.value = [];
        turmasDisponiveis.value = [];
      }
    };

    const carregarDadosPlanoMatricula = async () => {
      if (!matricula.categoria_id || !matricula.tipo_plano_id) return; // Se não houver categoria/tipo, não busca

      try {

        const response = await axios.get(`/cadastros/planos-matriculas`, {
          params: {
            categoria_id: matricula.categoria_id,
            tipo_plano_id: matricula.tipo_plano_id
          }
        });

        if (response.data && response.data.aulas_semana !== undefined) {
          matricula.aulas_semana = response.data.aulas_semana; // Não substitua por 1 se for nulo
          matricula.quantidade_parcelas = response.data.quantidade_parcelas ?? 1;
          matricula.quantidade_pagamentos = response.data.quantidade_parcelas ?? 1;

        }
      } catch (error) {
        console.error("❌ Erro ao carregar dados do plano de matrícula:", error);
      }
    };

    function onPagamentoInput(pagamento) {
      const forma = formasPagamento.value.find(f => f.id === pagamento.forma_pagamento_id);

      if (
        forma &&
        (
          forma.nome === 'A informar' ||
          isBoleto(pagamento.forma_pagamento_id)
        )
      ) {
        pagamento.data_pagamento = null;
        pagamento.data_recebimento = null;
        return;
      }

      // Para as demais formas, mantém o pagamento igual ao vencimento.
      pagamento.data_pagamento = pagamento.data_vencimento;
    }

    const contarPagamentosAteTermino = (primeiroVencimento, terminoVigencia) => {
      if (!primeiroVencimento || !terminoVigencia) return 0;

      const inicio = new Date(`${primeiroVencimento}T00:00:00`);
      const fim = new Date(`${terminoVigencia}T00:00:00`);

      if (Number.isNaN(inicio.getTime()) || Number.isNaN(fim.getTime())) return 0;
      if (inicio > fim) return 0;

      let count = 0;
      const cursor = new Date(inicio);

      while (cursor <= fim) {
        count++;
        cursor.setMonth(cursor.getMonth() + 1);
      }

      return count;
    };

    const aplicarQuantidadePagamentosAteTermino = () => {
      const dataBase =
        matricula.inicio_vigencia ||
        matricula.primeiro_vencimento ||
        matricula.data_matricula;

      const qtd = contarPagamentosAteTermino(
        dataBase,
        matricula.termino_vigencia
      );

      if (!qtd) return;

      plano.quantidade_parcelas = qtd;
      matricula.quantidade_parcelas = qtd;
      matricula.quantidade_pagamentos = qtd;

      calcularValorTotalMensalidade();

      if (!matricula.id) {
        if (!matricula.primeiro_vencimento && matricula.inicio_vigencia) {
          matricula.primeiro_vencimento = matricula.inicio_vigencia;
        }

        distribuirParcelasMensalidade();
      }
    };

    const aplicarEncerramentoFinalAno = () => {
      if (!matricula.inicio_vigencia) return;

      const ano = String(matricula.inicio_vigencia).slice(0, 4);
      if (!ano || ano.length !== 4) return;

      matricula.termino_vigencia = `${ano}-12-31`;
      terminoVigenciaTouched.value = false;

      aplicarQuantidadePagamentosAteTermino();
    };

    const aplicarQuantidadeParcelasDoPlano = () => {
      const planoSelecionado = tiposPlano.value.find(
        p => Number(p.id) === Number(matricula.tipo_plano_id)
      );

      if (!planoSelecionado) return;

      const qtd = Number(planoSelecionado.quantidade_parcelas) || 0;

      plano.quantidade_parcelas = qtd;
      matricula.quantidade_parcelas = qtd;
      matricula.quantidade_pagamentos = qtd;

      calcularValorTotalMensalidade();

      if (!matricula.id && matricula.primeiro_vencimento) {
        distribuirParcelasMensalidade();
      }
    };

    const onEncerrarFinalAnoChange = () => {
      if (matricula.encerrar_vigencia_final_ano) {
        aplicarEncerramentoFinalAno();
        return;
      }

      terminoVigenciaTouched.value = false;

      // Volta para a regra normal do plano
      calcularTerminoVigencia();

      // Restaura a quantidade de parcelas/pagamentos do plano selecionado
      aplicarQuantidadeParcelasDoPlano();
    };

    const onTerminoVigenciaManual = () => {
      terminoVigenciaTouched.value = true;

      if (matricula.encerrar_vigencia_final_ano) {
        aplicarQuantidadePagamentosAteTermino();
      }
    };

    const calcularTerminoVigencia = () => {
      if (terminoVigenciaTouched.value) return;

      if (matricula.encerrar_vigencia_final_ano) {
        aplicarEncerramentoFinalAno();
        return;
      }

      const tipoPlanoSelecionado = tiposPlano.value.find(
        p => Number(p.id) === Number(matricula.tipo_plano_id)
      );

      if (tipoPlanoSelecionado && matricula.inicio_vigencia) {
        const inicio = new Date(`${matricula.inicio_vigencia}T00:00:00`);
        inicio.setDate(inicio.getDate() + Number(tipoPlanoSelecionado.duracao || 0));
        matricula.termino_vigencia = inicio.toISOString().split('T')[0];
      }
    };

    const podeAdicionarTurma = computed(() => {
      return matricula.turmas.length < (plano.aulas_semana || 0);
    });


    const adicionarTurma = (index) => {
      const turmaSelecionada = matricula.turmas[index];

      if (!turmaSelecionada.turma_id) {
        console.warn("⚠️ Nenhuma turma selecionada.");
        return;
      }

      // Separar turma_id, dia e horário corretamente
      const match = turmaSelecionada.turma_id.match(/^(\d+)-(.+)-(\d{2}:\d{2}:\d{2})$/);

      if (!match) {
        console.error("🚨 Erro ao interpretar turma selecionada:", turmaSelecionada.turma_id);
        return;
      }

      const turmaId = parseInt(match[1]); // ID da turma
      const dia = match[2];               // Dia da semana completo
      const horario = match[3];           // Horário correto

      // Encontrar a turma na lista disponível
      const turmaEncontrada = turmasDisponiveis.value.find(
        t => t.id === turmaId && t.dia === dia && t.horario === horario
      );

      if (!turmaEncontrada) {
        console.error("🚨 Nenhuma turma encontrada para os critérios!", { turmaId, dia, horario });
        return;
      }

      // Verificar se essa turma já foi adicionada
      const turmaJaExiste = matricula.turmas.some(
        t => t.id === turmaEncontrada.id && t.dia === turmaEncontrada.dia && t.horario === turmaEncontrada.horario
      );

      if (turmaJaExiste) {
        console.warn("🔹 Essa turma já foi adicionada para esse dia e horário.");
        return;
      }

      // Preencher os dados corretamente
      matricula.turmas[index] = { ...turmaEncontrada };
    };

    const adicionarTurmaSlot = () => {
      // liga modo avançado
      modoAvancado.value = true;

      // preenche nivel_id nas linhas já existentes que ainda não têm
      const nivelPadrao = Number(matricula.nivel_id) || null;
      matricula.turmas = (matricula.turmas || []).map(l => ({
        ...l,
        nivel_id: (l && l.nivel_id != null) ? l.nivel_id : nivelPadrao
      }));

      // adiciona o novo slot já com nivel_id padrão
      matricula.turmas.push({
        nivel_id: nivelPadrao,
        turma_id: "",
        id: null,
        dia: "",
        horario: "",
        instrutor: ""
      });
    };

    const onNivelLinhaChange = (index) => {
      const linha = matricula.turmas[index];
      if (!linha) return;
      // ao trocar o nível, força o usuário a escolher uma nova turma
      linha.turma_id = "";
      linha.id = null;
      linha.dia = "";
      linha.horario = "";
      linha.instrutor = "";
    };


    // ✅ Atualiza a turma quando o usuário seleciona
    const atualizarTurma = (index) => {
      try {
        const slot = matricula.turmas[index];
        let turmaId = slot?.turma_id;
        if (!turmaId) return;

        // Em modo avançado: exigir o nível da linha antes de escolher turma
        if (modoAvancado?.value && !slot?.nivel_id) {
          toast.error('Selecione o Nível desta linha antes de escolher a turma.');
          slot.turma_id = "";
          return;
        }

        // Parse do value "ID-DIA-HH:MM:SS"
        const id = turmaId.split('-')[0];
        const resto = turmaId.substring(turmaId.indexOf('-') + 1);
        const lastHyphen = resto.lastIndexOf('-');
        if (lastHyphen === -1) {
          console.warn("⚠️ turmaId não contém um formato esperado:", turmaId);
          slot.turma_id = "";
          return;
        }
        const dia = resto.substring(0, lastHyphen);
        const horario = resto.substring(lastHyphen + 1);

        // Base correta: por nível da linha (modo avançado) ou nível principal (modo básico)
        const nivelRef = (modoAvancado?.value ? (slot?.nivel_id ?? matricula.nivel_id) : matricula.nivel_id) || matricula.nivel_id;
        const base = (typeof turmasDisponiveisPorNivel === 'function')
          ? turmasDisponiveisPorNivel(nivelRef)
          : turmasDisponiveis.value; // fallback

        const turmaEncontrada = base.find(
          t => String(t.id) === String(id) && t.dia === dia && t.horario === horario
        );

        if (!turmaEncontrada) {
          console.warn("⚠️ Nenhuma turma encontrada para os critérios!", { id, dia, horario, nivelRef });
          slot.turma_id = "";
          return;
        }

        // ✅ Validação imediata de conflito com outras linhas
        if (hasChoqueComOutraLinha(turmaEncontrada.dia, turmaEncontrada.horario, index)) {
          toast.error(`Conflito: já existe turma em "${turmaEncontrada.dia}" às "${String(turmaEncontrada.horario).slice(0,5)}" em outra linha.`);
          slot.turma_id = "";  // desfaz seleção
          return;
        }

        // Preserva nível da linha (não deixa sumir)
        const nivelAtual = (slot?.nivel_id ?? Number(matricula.nivel_id) ?? null);

        // Preenche a linha com os dados da turma escolhida
        matricula.turmas[index] = {
          ...turmaEncontrada,
          turma_id: turmaId,
          nivel_id: nivelAtual
        };

      } catch (error) {
        console.error("❌ Erro em atualizarTurma:", error);
      }
    };

    const removerTurma = (index) => {
      matricula.turmas.splice(index, 1);
    };

    // === Ajusta quantidade de slots sem apagar o que já foi escolhido
    // Garante pelo menos N slots, sem apagar os já preenchidos
    function ensureMinSlots(min) {
      const alvo = Number(min) || 0;
      const atual = matricula.turmas.length;
      if (atual >= alvo) return;

      const nivelPadrao = Number(matricula.nivel_id) || null;
      for (let i = atual; i < alvo; i++) {
        matricula.turmas.push({
          nivel_id: nivelPadrao,
          turma_id: "",
          id: null,
          dia: "",
          horario: "",
          instrutor: "",
        });
      }


      // atual > alvo -> remover excesso dando preferência a linhas vazias
      let remover = atual - alvo;

      // 1º remove vazias (sem turma_id) do fim pro começo
      for (let i = matricula.turmas.length - 1; i >= 0 && remover > 0; i--) {
        const t = matricula.turmas[i];
        if (!t || !(t.turma_id || '').trim()) {
          matricula.turmas.splice(i, 1);
          remover--;
        }
      }

      // 2º se ainda sobrar, remove do fim
      while (remover > 0 && matricula.turmas.length > 0) {
        matricula.turmas.pop();
        remover--;
      }
    }


    // === VALIDAÇÕES FINAIS ===

    // exige exatamente N turmas = aulas/semana e todas preenchidas
    // const validarQuantidadeTurmasMinimas = () => {
    //   const min = Number(plano.aulas_semana || 0);
    //   const selecionadas = matricula.turmas.filter(t => (t?.turma_id || '').trim()).length;
    //   if (selecionadas < min) {
    //     toast.error(`Selecione pelo menos ${min} turma(s).`);
    //     return false;
    //   }
    //   return true;
    // };

    // impede duas turmas no mesmo dia/horário (independente do nível)
    // const validarChoqueTurmas = () => {
    //   const toKey = (dia, horario) => {
    //     // usa idxDia para normalizar o dia e recorta hora para HH:MM
    //     const d = idxDia(dia); // você já tem essa helper
    //     const h = String(horario || '').slice(0,5); // HH:MM
    //     return `${d}__${h}`;
    //   };

    //   const seen = new Set();
    //   for (let i = 0; i < matricula.turmas.length; i++) {
    //     const t = matricula.turmas[i] || {};
    //     if (!t.dia || !t.horario) continue; // se não tem dia/hora ainda, deixa salvar? (opção) — aqui ignoramos
    //     const key = toKey(t.dia, t.horario);
    //     if (seen.has(key)) {
    //       toast.error(`Conflito de agenda: mais de uma turma em "${t.dia}" às "${String(t.horario).slice(0,5)}".`);
    //       return false;
    //     }
    //     seen.add(key);
    //   }
    //   return true;
    // };

    // ——— Conflito imediato: existe outra linha no mesmo dia/horário? ———
    const hasChoqueComOutraLinha = (dia, horario, indexAtual) => {
      const key = (d, h) => `${idxDia(d)}__${String(h || '').slice(0,5)}`; // HH:MM
      const k = key(dia, horario);
      return matricula.turmas.some((t, i) =>
        i !== indexAtual &&
        t && t.dia && t.horario &&
        key(t.dia, t.horario) === k
      );
    };

    const carregarValorMatricula = async () => {
      try {
        // 1) buscar tipos
        const resTipos = await axios.get("/financeiro/tipos-valores");
        const tiposValores = resTipos.data.data || resTipos.data;
        const tipoMatricula = tiposValores.find(t => t.nome === "Matrícula");

        if (!tipoMatricula) {
          console.warn("⚠️ Tipo 'Matrícula' não encontrado.");
          console.groupEnd();
          return;
        }

        // 2) buscar valores (continua limit=1000 pra garantir)
        const resValores = await axios.get("/financeiro/valores?limit=1000");
        const valores = resValores.data.data || resValores.data;

        // 3) buscar por nome
        const valorMatricula = valores.find(v =>
          v.tipo === tipoMatricula.nome
        );

        if (!valorMatricula) {
          console.warn("⚠️ Nenhum valor encontrado para o tipo 'Matrícula'.");
          console.groupEnd();
          return;
        }

        // 4) atribuir
        if (!matricula.id) {
          matricula.valor = parseFloat(valorMatricula.valor).toFixed(2);
        }

        console.groupEnd();
      } catch (error) {
        console.error("❌ Erro ao carregar valor da matrícula:", error);
      }
    };

    const calcularValorComDesconto = () => {
      const valorBase = parseFloat(matricula.valor) || 0;
      const desconto = parseFloat(matricula.desconto) || 0;
      matricula.valor_com_desconto = (valorBase - (valorBase * desconto / 100)).toFixed(2);

    };

    const atualizarDescontoConvenio = () => {
      const convenioSelecionado = convenios.value.find(c => c.id === matricula.convenio_id);
      if (convenioSelecionado) {
        matricula.desconto = convenioSelecionado.desconto;
        calcularValorComDesconto();
      }
    };


    const valorNumericoPagamento = (valor) => {
      if (valor === null || valor === undefined || valor === '') {
        return 0;
      }

      if (typeof valor === 'number') {
        return Number.isFinite(valor) ? valor : 0;
      }

      const texto = String(valor).trim();

      /*
      * Valor numérico vindo do sistema:
      * "453", "453.00", "1234.56"
      *
      * Neste formato, o ponto é decimal e não separador de milhar.
      */
      if (/^-?\d+(?:\.\d+)?$/.test(texto)) {
        const numero = Number(texto);
        return Number.isFinite(numero) ? numero : 0;
      }

      /*
      * Valor formatado em pt-BR:
      * "453,00", "1.234,56", "R$ 453,00"
      */
      const numero = converterValor(texto);

      return Number.isFinite(numero) ? numero : 0;
    };


    /*
    * Um registro original substituído não deve entrar novamente
    * no total dos lançamentos efetivos.
    */
    const isRegistroOriginalSubstituido = (pagamento) => {
      if (!pagamento) return false;

      const statusCheque = String(
        pagamento.cheque_status || ''
      ).toUpperCase();

      return (
        statusCheque === 'SUBSTITUIDO' ||
        statusCheque === 'DEVOLVIDO' ||
        Boolean(pagamento.pagamento_substituto_id)
      );
    };


    /*
    * Soma somente os lançamentos que efetivamente compõem
    * o valor financeiro atual das mensalidades.
    */
    const totalLancamentosMensalidade = computed(() => {
      const total = (matricula.pagamentos || [])
        .filter(pagamento => {
          if (!pagamento || pagamento.pgt_matricula) return false;
          if (pagamento.adicional) return false;
          if (isRegistroOriginalSubstituido(pagamento)) return false;

          return true;
        })
        .reduce((soma, pagamento) => {
          return soma + valorNumericoPagamento(pagamento.valor);
        }, 0);

      return Number(total.toFixed(2));
    });


    const resumoComposicoesMensalidade = computed(() => {
      let totalProrata = 0;
      let totalCreditoTrocaPlano = 0;
      let primeiroProrata = null;

      (matricula.pagamentos || [])
        .filter(pagamento => {
          if (!pagamento || pagamento.pgt_matricula) return false;
          if (pagamento.adicional) return false;
          if (isRegistroOriginalSubstituido(pagamento)) return false;

          return true;
        })
        .forEach(pagamento => {
          const composicoes =
            Array.isArray(
              pagamento?.composicoes
            )
              ? pagamento.composicoes
              : [];

          composicoes.forEach(
            composicao => {
              const tipo =
                String(
                  composicao?.tipo ||
                  ''
                ).toUpperCase();

              const valor =
                valorNumericoPagamento(
                  composicao?.valor
                );

              if (
                tipo ===
                'PRORATA'
              ) {
                totalProrata +=
                  valor;

                if (!primeiroProrata) {
                  primeiroProrata =
                    composicao;
                }

                return;
              }

              if (
                tipo ===
                'CREDITO_TROCA_PLANO'
              ) {
                totalCreditoTrocaPlano +=
                  valor;
              }
            }
          );
        });

      return {
        totalProrata:
          Number(
            totalProrata.toFixed(2)
          ),

        totalCreditoTrocaPlano:
          Number(
            totalCreditoTrocaPlano
              .toFixed(2)
          ),

        primeiroProrata
      };
    });


    const mensagemComposicoesMensalidade = computed(() => {
      const resumo =
        resumoComposicoesMensalidade.value;

      const mensagens = [];

      if (
        resumo.totalProrata > 0
      ) {
        const prorata =
          resumo.primeiroProrata;

        const diasAtivos =
          Number(
            prorata?.dias_ativos
          );

        const diasCompetencia =
          Number(
            prorata?.dias_competencia
          );

        const diasTexto =
          Number.isInteger(
            diasAtivos
          ) &&
          diasAtivos > 0 &&
          Number.isInteger(
            diasCompetencia
          ) &&
          diasCompetencia > 0
            ? ` (${diasAtivos} de ${diasCompetencia} dias)`
            : '';

        const inicio =
          globalUtils.formatarData(
            matricula.inicio_vigencia
          );

        mensagens.push(
          `Pró-rata aplicado: ${globalUtils.formatarValor(
            resumo.totalProrata
          )} referente à competência inicial a partir de ${inicio}${diasTexto}. O valor está incorporado ao primeiro título.`
        );
      }

      if (
        resumo
          .totalCreditoTrocaPlano >
        0
      ) {
        mensagens.push(
          `Crédito de troca de plano aplicado: ${globalUtils.formatarValor(
            resumo
              .totalCreditoTrocaPlano
          )}.`
        );
      }

      return mensagens.join(' ');
    });


    const composicaoTroca = ref(null);
    const estadoComposicaoTroca = ref('ausente');
    const possuiComponenteTroca = computed(() => (matricula.pagamentos || []).some(p =>
      Array.isArray(p?.composicoes) && p.composicoes.some(c => c.tipo === 'COMPLEMENTO_TROCA_PLANO')));
    const conferenciaTrocaIndisponivel = computed(() =>
      ['carregando', 'indisponivel'].includes(estadoComposicaoTroca.value) ||
      (possuiComponenteTroca.value && !composicaoTroca.value));
    // Valores históricos autorizados pelo backend. Não representam saldo atualizado.
    const resumoTroca = computed(() => {
      const c = composicaoTroca.value;
      if (c?.origem === 'LEGACY') return [['Novo plano', c.valor_plano], ['Obrigações anteriores', c.valor_anterior], ['Complemento', c.complemento]];
      return c ? [
        ['Novo plano (G)', c.valor_plano], ['Composição anterior (A)', c.valor_anterior],
        ['Pago na troca (P)', c.valor_pago], ['Pendente na troca (U)', c.valor_pendente],
        ['Complemento criado (C)', c.complemento],
        ['Total exigível na troca (U + C)', ((Math.round(Number(c.valor_pendente) * 100) + Math.round(Number(c.complemento) * 100)) / 100).toFixed(2)]
      ] : [];
    });
    let sequenciaComposicaoTroca = 0;
    const carregarComposicaoTroca = async () => {
      const sequencia = ++sequenciaComposicaoTroca;
      composicaoTroca.value = null;
      estadoComposicaoTroca.value = 'ausente';
      if (!matricula.id) return;
      if (matricula.politica_cobranca_id == null) {
        estadoComposicaoTroca.value = 'carregando';
        try {
          const { data } = await axios.get(`/operacional/matriculas/detalhes/${matricula.id}`);
          if (sequencia !== sequenciaComposicaoTroca) return;
          composicaoTroca.value = data.composicaoTroca ? { ...data.composicaoTroca, origem: 'LEGACY' } : null;
          estadoComposicaoTroca.value = composicaoTroca.value ? 'disponivel' : 'ausente';
        } catch { if (sequencia === sequenciaComposicaoTroca) estadoComposicaoTroca.value = 'indisponivel'; }
        return;
      }
      estadoComposicaoTroca.value = 'carregando';
      try {
        const { data } = await axios.get(`/operacional/matriculas/salvar-trocas-planos/composicao/${matricula.id}`);
        if (sequencia !== sequenciaComposicaoTroca) return;
        if (data === null) { estadoComposicaoTroca.value = 'ausente'; return; }
        const campos = ['valor_plano', 'valor_anterior', 'valor_pago', 'valor_pendente', 'complemento'];
        const valores = campos.map(k => {
          const v = data?.[k];
          return /^(0|[1-9]\d*)\.\d{2}$/.test(String(v)) ? Math.round(Number(v) * 100) : NaN;
        });
        const [g, a, pago, u, c] = valores;
        if (!valores.every(v => Number.isSafeInteger(v) && v >= 0) ||
            !Number.isSafeInteger(u + c) || g !== a + c || a !== pago + u ||
            !Array.isArray(data.obrigacoes_preservadas)) throw new Error('Composição incompleta');
        composicaoTroca.value = data;
        estadoComposicaoTroca.value = 'disponivel';
      } catch {
        if (sequencia === sequenciaComposicaoTroca) estadoComposicaoTroca.value = 'indisponivel';
      }
    };

    const totalEsperadoMensalidade = computed(() => {
      if (conferenciaTrocaIndisponivel.value) return null;
      if (composicaoTroca.value) return Number(composicaoTroca.value.complemento);

      const contratual =
        Number(
          matricula
            .valor_total_mensalidade ||
          0
        );

      const totalContratual =
        Number.isFinite(
          contratual
        )
          ? contratual
          : 0;

      const resumo =
        resumoComposicoesMensalidade.value;

      const total =
        totalContratual +
        resumo.totalProrata -
        resumo.totalCreditoTrocaPlano;

      return Number(
        total.toFixed(2)
      );
    });


    const diferencaTotalMensalidade = computed(() => {
      if (conferenciaTrocaIndisponivel.value) return null;
      const diferenca =
        totalLancamentosMensalidade.value -
        totalEsperadoMensalidade.value;

      return Number(diferenca.toFixed(2));
    });


    const existeDiferencaMensalidade = computed(() => {
      if (conferenciaTrocaIndisponivel.value) return null;
      /*
      * Tolerância inferior a um centavo para evitar diferenças
      * causadas por ponto flutuante.
      */
      return Math.abs(diferencaTotalMensalidade.value) > 0.009;
    });


    const diferencaMensalidadeAbsoluta = computed(() => {
      if (conferenciaTrocaIndisponivel.value) return null;
      return Math.abs(diferencaTotalMensalidade.value);
    });


    const mensagemDiferencaMensalidade = computed(() => {
      if (conferenciaTrocaIndisponivel.value) return 'Conferência financeira indisponível.';
      if (!existeDiferencaMensalidade.value) {
        return 'Os valores das parcelas conferem com o total esperado.'
      }

      if (
        diferencaTotalMensalidade.value > 0 &&
        acrescimoPagamentosSubstitutos.value > 0 &&
        Math.abs(
          diferencaTotalMensalidade.value -
          acrescimoPagamentosSubstitutos.value
        ) <= 0.01
      ) {
        return `Os lançamentos estão ${globalUtils.formatarValor(
          diferencaMensalidadeAbsoluta.value
        )} acima do total contratual. A diferença corresponde a acréscimos, juros ou multa registrados no pagamento substituto.`
      }

      if (diferencaTotalMensalidade.value > 0) {
        return `Os lançamentos estão ${globalUtils.formatarValor(
          diferencaMensalidadeAbsoluta.value
        )} acima do total esperado. Verifique se existe acréscimo, juros ou valor lançado a maior.`
      }

      return `Faltam ${globalUtils.formatarValor(
        diferencaMensalidadeAbsoluta.value
      )} para atingir o total esperado das mensalidades.`
    })


    const normalizarMoedaContratualTexto = (value) => {
      let text = String(value ?? '')
        .trim()
        .replace(/\s+/g, '')
        .replace(/^R\$/i, '');

      if (!text) return null;

      if (text.includes(',')) {
        text = text
          .replace(/\./g, '')
          .replace(',', '.');
      }

      const match =
        /^(\d+)(?:\.(\d{0,2}))?$/
          .exec(text);

      if (!match) return null;

      const inteiro =
        match[1]
          .replace(
            /^0+(?=\d)/,
            ''
          ) ||
        '0';

      const fracao =
        String(
          match[2] || ''
        ).padEnd(
          2,
          '0'
        );

      return (
        `${inteiro}.${fracao}`
      );
    };

    const normalizarPercentualContratualTexto = (value) => {
      const text = String(
        value ?? ''
      )
        .trim()
        .replace(
          ',',
          '.'
        );

      const match =
        /^(\d+)(?:\.(\d{0,6}))?$/
          .exec(text);

      if (!match) return null;

      const inteiro =
        match[1]
          .replace(
            /^0+(?=\d)/,
            ''
          ) ||
        '0';

      const fracaoInformada =
        String(
          match[2] || ''
        );

      const fracaoSeis =
        fracaoInformada
          .padEnd(
            6,
            '0'
          );

      /*
       * Comparação textual exata, sem Number/float/BigInt.
       *
       * O domínio permitido é 0..100.
       * O contrato persistido em matriculas.desconto_mensalidade
       * possui escala 2; dígitos adicionais só são aceitos
       * quando forem zeros e puderem ser descartados sem arredondar.
       */
      if (
        inteiro.length > 3 ||
        (
          inteiro.length === 3 &&
          inteiro > '100'
        ) ||
        (
          inteiro === '100' &&
          fracaoSeis !== '000000'
        ) ||
        (
          fracaoInformada.length > 2 &&
          /[1-9]/
            .test(
              fracaoInformada
                .slice(
                  2
                )
            )
        )
      ) {
        return null;
      }

      const fracaoDuas =
        fracaoInformada
          .padEnd(
            2,
            '0'
          )
          .slice(
            0,
            2
          );

      return (
        `${inteiro}.${fracaoDuas}`
      );
    };

    const isBoletoSemantico = (formaId) => {
      const forma =
        formasPagamento.value
          .find(
            fp =>
              Number(fp?.id) ===
              Number(formaId)
          );

      if (!forma) return false;

      return (
        String(
          forma.codigo_sistema ||
          ''
        )
          .trim()
          .toUpperCase() ===
        'BOLETO'
      );
    };

    const parcelasContratuaisNovas = () =>
      (matricula.pagamentos || [])
        .filter(
          p =>
            !p.pgt_matricula &&
            !p.adicional
        );

    const novaMensalidadeBoletoSemantica =
      computed(
        () => {
          if (matricula.id) {
            return false;
          }

          const primeira =
            parcelasContratuaisNovas()[0];

          return Boolean(
            primeira &&
            isBoletoSemantico(
              primeira
                .forma_pagamento_id
            )
          );
        }
      );

    const entradaMensalidadeManaged = computed(() =>
      novaMensalidadeBoletoSemantica.value ||
      matricula.politica_cobranca_id != null ||
      (matricula.pagamentos || []).some(p => !p.adicional && p.politica_cobranca_id != null)
    );

    const calcularPrimeiroVencimentoRegular = (
      dataInicio,
      diaVencimento
    ) => {
      const match =
        /^(\d{4})-(\d{2})-(\d{2})$/
          .exec(
            String(
              dataInicio ||
              ''
            ).trim()
          );

      const dia =
        Number(
          diaVencimento
        );

      if (
        !match ||
        !Number.isInteger(dia) ||
        dia < 1 ||
        dia > 28
      ) {
        return null;
      }

      let ano =
        Number(
          match[1]
        );

      let mes =
        Number(
          match[2]
        ) + 1;

      if (mes === 13) {
        mes = 1;
        ano += 1;
      }

      return (
        String(ano)
          .padStart(4, '0') +
        '-' +
        String(mes)
          .padStart(2, '0') +
        '-' +
        String(dia)
          .padStart(2, '0')
      );
    };

    const onDiaVencimentoMensalidadeChange = () => {
      const primeiro =
        calcularPrimeiroVencimentoRegular(
          matricula.inicio_vigencia,
          matricula
            .dia_vencimento_mensalidade
        );

      if (!primeiro) {
        return;
      }

      matricula.primeiro_vencimento =
        primeiro;

      distribuirParcelasMensalidade();

      void carregarPreviewMensalidadeManaged();
    };

    const carregarDiasVencimentoCobranca = async () => {
      const dataInicio =
        String(
          matricula.inicio_vigencia ||
          ''
        ).trim();

      if (!dataInicio) {
        diasVencimentoCobranca.value =
          [];

        erroPoliticaCobranca.value =
          '';

        limparPreviewMensalidadeManaged();

        return false;
      }

      carregandoDiasVencimentoCobranca.value =
        true;

      erroPoliticaCobranca.value =
        '';

      try {
        const { data } =
          await axios.get(
            '/operacional/matriculas/politica-cobranca-aplicavel',
            {
              params: {
                data_inicio:
                  dataInicio
              }
            }
          );

        const dias =
          Array.isArray(
            data
              ?.dias_vencimento
          )
            ? data
                .dias_vencimento
                .map(
                  item => ({
                    dia:
                      Number(
                        item?.dia
                      ),

                    padrao:
                      item?.padrao ===
                      true
                  })
                )
                .filter(
                  item =>
                    Number.isInteger(
                      item.dia
                    ) &&
                    item.dia >= 1 &&
                    item.dia <= 28
                )
                .sort(
                  (a, b) =>
                    a.dia -
                    b.dia
                )
            : [];

        diasVencimentoCobranca.value =
          dias;

        const diaAtual =
          Number(
            matricula
              .dia_vencimento_mensalidade
          );

        if (
          diaAtual &&
          !dias.some(
            item =>
              item.dia ===
              diaAtual
          )
        ) {
          matricula
            .dia_vencimento_mensalidade =
            '';

          if (
            novaMensalidadeBoletoSemantica
              .value
          ) {
            matricula.primeiro_vencimento =
              '';
          }
        } else if (
          diaAtual
        ) {
          onDiaVencimentoMensalidadeChange();
        }

        return (
          dias.length >
          0
        );
      } catch (error) {
        diasVencimentoCobranca.value =
          [];

        if (
          novaMensalidadeBoletoSemantica
            .value
        ) {
          matricula.primeiro_vencimento =
            '';
        }

        erroPoliticaCobranca.value =
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          'Não foi possível carregar os dias de vencimento da política aplicável.';

        return false;
      } finally {
        carregandoDiasVencimentoCobranca.value =
          false;
      }
    };


    const limparPreviewMensalidadeManaged = () => {
      sequenciaPreviewMensalidadeManaged +=
        1;

      previewMensalidadeManaged.value =
        null;

      erroPreviewMensalidadeManaged.value =
        '';

      carregandoPreviewMensalidadeManaged.value =
        false;
    };


    const carregarPreviewMensalidadeManaged = async () => {
      if (
        matricula.id ||
        !novaMensalidadeBoletoSemantica.value
      ) {
        limparPreviewMensalidadeManaged();
        return false;
      }

      const parcelas =
        parcelasContratuaisNovas();

      const primeira =
        parcelas[0];

      const dia =
        Number(
          matricula
            .dia_vencimento_mensalidade
        );

      const formaId =
        Number(
          primeira
            ?.forma_pagamento_id
        );

      const contaId =
        Number(
          primeira
            ?.conta_financeira_id
        );

      if (
        !primeira ||
        !Number.isInteger(dia) ||
        !diasVencimentoCobranca.value
          .some(
            item =>
              item.dia ===
              dia
          ) ||
        !Number.isInteger(formaId) ||
        formaId <= 0 ||
        !Number.isInteger(contaId) ||
        contaId <= 0 ||
        !parcelas.every(
          p =>
            Number(
              p.forma_pagamento_id
            ) ===
              formaId &&
            Number(
              p.conta_financeira_id
            ) ===
              contaId
        )
      ) {
        limparPreviewMensalidadeManaged();
        return false;
      }

      const valorMensalBruto =
        normalizarMoedaContratualTexto(
          matricula
            .valor_mensalidade
        );

      const descontoMensalidadePercentual =
        normalizarPercentualContratualTexto(
          matricula
            .desconto_mensalidade
        );

      if (
        !valorMensalBruto ||
        descontoMensalidadePercentual ===
          null ||
        !matricula.inicio_vigencia ||
        !matricula.categoria_id ||
        !matricula.tipo_plano_id
      ) {
        limparPreviewMensalidadeManaged();
        return false;
      }

      const requisicao =
        ++sequenciaPreviewMensalidadeManaged;

      carregandoPreviewMensalidadeManaged.value =
        true;

      erroPreviewMensalidadeManaged.value =
        '';

      try {
        const { data } =
          await axios.post(
            '/operacional/matriculas/mensalidade-managed/preview',
            {
              inicio_vigencia:
                matricula.inicio_vigencia,

              categoria_id:
                Number(
                  matricula.categoria_id
                ),

              tipo_plano_id:
                Number(
                  matricula.tipo_plano_id
                ),

              dia_vencimento_mensalidade:
                dia,

              mensalidade_forma_pagamento_id:
                formaId,

              mensalidade_conta_financeira_id:
                contaId,

              encerrar_vigencia_final_ano:
                Boolean(
                  matricula
                    .encerrar_vigencia_final_ano
                ),

              valor_mensalidade:
                valorMensalBruto,

              desconto_mensalidade:
                descontoMensalidadePercentual
            }
          );

        if (
          requisicao !==
          sequenciaPreviewMensalidadeManaged
        ) {
          return false;
        }

        previewMensalidadeManaged.value =
          data;

        if (
          data
            ?.primeiro_vencimento_regular
        ) {
          matricula.primeiro_vencimento =
            data
              .primeiro_vencimento_regular;
        }

        return true;
      } catch (error) {
        if (
          requisicao !==
          sequenciaPreviewMensalidadeManaged
        ) {
          return false;
        }

        previewMensalidadeManaged.value =
          null;

        erroPreviewMensalidadeManaged.value =
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          'Não foi possível calcular a prévia da mensalidade gerenciada.';

        return false;
      } finally {
        if (
          requisicao ===
          sequenciaPreviewMensalidadeManaged
        ) {
          carregandoPreviewMensalidadeManaged.value =
            false;
        }
      }
    };


    const mensagemPreviewProrataManaged =
      computed(
        () => {
          const preview =
            previewMensalidadeManaged.value;

          if (!preview) {
            return '';
          }

          const prorata =
            preview.prorata;

          if (
            !prorata ||
            String(
              prorata.valor ||
              '0.00'
            ) ===
              '0.00'
          ) {
            return 'Não há pró-rata a acrescentar à competência inicial para esta matrícula.';
          }

          const diasAtivos =
            Number(
              prorata.dias_ativos
            );

          const diasCompetencia =
            Number(
              prorata.dias_competencia
            );

          const diasTexto =
            Number.isFinite(
              diasAtivos
            ) &&
            Number.isFinite(
              diasCompetencia
            )
              ? ` (${diasAtivos} de ${diasCompetencia} dias)`
              : '';

          const inicio =
            globalUtils.formatarData(
              preview.data_inicio
            );

          const modo =
            String(
              prorata.modo ||
              ''
            ).toUpperCase();

          const destino =
            modo ===
              'ACUMULAR_PROXIMA_PARCELA'
              ? ' O valor será acumulado à primeira mensalidade.'
              : (
                  modo ===
                    'PRIMEIRA_PARCELA'
                    ? ' O valor será cobrado em título próprio antes das mensalidades integrais.'
                    : ''
                );

          return (
            `Pró-rata aplicado: ${globalUtils.formatarValor(
              prorata.valor
            )} referente à competência inicial a partir de ${inicio}${diasTexto}.${destino}`
          );
        }
      );


    const primeiroTituloPreviewManaged =
      computed(
        () =>
          previewMensalidadeManaged.value
            ?.obrigacoes
            ?.[0] ||
          null
      );


    const prepararContextoMensalidadeManagedNova = async () => {
      if (matricula.id) {
        return null;
      }

      const parcelas =
        parcelasContratuaisNovas();

      const primeira =
        parcelas[0];

      if (
        !primeira ||
        !isBoletoSemantico(
          primeira
            .forma_pagamento_id
        )
      ) {
        return null;
      }

      const formaId =
        Number(
          primeira
            .forma_pagamento_id
        );

      if (
        !parcelas.every(
          p =>
            isBoletoSemantico(
              p
                .forma_pagamento_id
            ) &&
            Number(
              p
                .forma_pagamento_id
            ) ===
              formaId
        )
      ) {
        throw new Error(
          'No Boleto gerenciado, todas as parcelas contratuais devem usar a mesma forma BOLETO.'
        );
      }

      const contaId =
        Number(
          primeira
            .conta_financeira_id
        );

      if (
        !Number.isInteger(
          contaId
        ) ||
        contaId <= 0 ||
        !parcelas.every(
          p =>
            Number(
              p
                .conta_financeira_id
            ) ===
              contaId
        )
      ) {
        throw new Error(
          'No Boleto gerenciado, todas as parcelas contratuais devem usar a mesma conta financeira.'
        );
      }

      await carregarDiasVencimentoCobranca();

      const dia =
        Number(
          matricula
            .dia_vencimento_mensalidade
        );

      if (
        !diasVencimentoCobranca.value
          .some(
            item =>
              item.dia ===
              dia
          )
      ) {
        throw new Error(
          'Selecione explicitamente um dia de vencimento permitido pela política de cobrança.'
        );
      }

      const valorMensalBruto =
        normalizarMoedaContratualTexto(
          matricula
            .valor_mensalidade
        );

      if (!valorMensalBruto) {
        throw new Error(
          'O valor da mensalidade deve possuir formato monetário válido.'
        );
      }

      const descontoMensalidadePercentual =
        normalizarPercentualContratualTexto(
          matricula
            .desconto_mensalidade
        );

      if (
        descontoMensalidadePercentual ===
        null
      ) {
        throw new Error(
          'O desconto da mensalidade deve estar entre 0 e 100 e ser exatamente representável com no máximo duas casas decimais.'
        );
      }

      const primeiro =
        calcularPrimeiroVencimentoRegular(
          matricula
            .inicio_vigencia,
          dia
        );

      if (!primeiro) {
        throw new Error(
          'Não foi possível determinar o primeiro vencimento regular do Boleto.'
        );
      }

      /*
       * Isto é apenas preview/validação visual.
       * O backend e o motor 6B1 continuam autoridades do cronograma.
       */
      matricula.primeiro_vencimento =
        primeiro;

      return {
        diaVencimento:
          dia,

        formaPagamentoId:
          formaId,

        contaFinanceiraId:
          contaId,

        valorMensalBruto,

        descontoMensalidadePercentual
      };
    };


    const carregarFormasPagamento = async () => {
      try {
        const { data } = await axios.get('/cadastros/formas-pagamento');

        const norm = s => (s ?? '')
          .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
          .trim().toLowerCase();

        const ordemPersonalizada = {
          'pix': 1,
          'especie': 2,
          'cartao de credito a vista': 3,
          'cartao de credito parcelado': 4,
          'cartao de debito': 5,
          'boleto': 6,
          'transferencia bancaria': 7,
          'cheque': 8,
          'a informar': 9,
        };

        formasPagamento.value = (Array.isArray(data) ? data : (data?.data ?? []))
          .filter(fp => norm(fp.nome) !== 'brinde')
          .map(fp => ({
            id: fp.id,
            nome: fp.nome,
            prazo_pagamento: fp.prazo_pagamento,
            conta_financeira_padrao_id: fp.conta_financeira_padrao_id ?? null,
            codigo_sistema: fp.codigo_sistema ?? null,
            tipo_integracao: fp.tipo_integracao ?? null,
            gera_cobranca: fp.gera_cobranca === true,
            ativo: fp.ativo !== false
          }))
          .sort((a, b) => {
            const oa = ordemPersonalizada[norm(a.nome)] ?? 999;
            const ob = ordemPersonalizada[norm(b.nome)] ?? 999;

            if (oa !== ob) return oa - ob;
            return a.nome.localeCompare(b.nome, 'pt-BR');
          });

      } catch (error) {
        console.error("❌ Erro ao carregar formas de pagamento:", error);
      }
    };

    const carregarConvenios = async () => {
      try {
        const response = await axios.get("/cadastros/convenios");

        // ✅ Garantir que response.data seja um array ou acesse corretamente os dados
        const conveniosData = response.data.data || response.data;

        if (Array.isArray(conveniosData)) {
          convenios.value = conveniosData.filter(c => c.ativo);
        } else {
          console.error("❌ Estrutura inesperada para convenios:", response.data);
          convenios.value = [];
        }
      } catch (error) {
        console.error("❌ Erro ao carregar convenios:", error);
      }
    };


    const calcularValorComDescontoMensalidade = () => {

      const valorBase = parseFloat(matricula.valor_mensalidade) || 0;
      const desconto = parseFloat(matricula.desconto_mensalidade) || 0;
      matricula.valor_com_desconto_mensalidade = (valorBase - (valorBase * desconto / 100)).toFixed(2);

      calcularValorTotalMensalidade();

      // Em matrícula nova, atualiza valores/datas das parcelas sem apagar
      // forma de pagamento e demais dados já digitados pelo usuário.
      if (!matricula.id && matricula.primeiro_vencimento) {
        distribuirParcelasMensalidade();
      }

      if (
        !matricula.id &&
        novaMensalidadeBoletoSemantica.value
      ) {
        void carregarPreviewMensalidadeManaged();
      }
    };


    const calcularValorTotalMensalidade = () => {
      if (matricula.id || !Number(matricula.valor_mensalidade)) return;
      const quantidade = parseInt(matricula.quantidade_pagamentos) || 1;
      matricula.valor_total_mensalidade = (calcularPlano({ referencia: matricula.valor_mensalidade, quantidade, duracao: 1, dias: 1, desconto: matricula.desconto_mensalidade || 0 }) / 100).toFixed(2);
    };

    const distribuirParcelasMensalidade = () => {
      if (!matricula.primeiro_vencimento) {
        return;
      }

      // Em matrícula nova, sincroniza a grade preservando tudo o que já foi
      // preenchido pelo usuário (forma, datas financeiras, conta, cheque etc.).
      if (!matricula.id) {
        const quantidade = (matricula.pagamentos || []).filter(p => !p.pgt_matricula && !p.adicional).length || Number(
          matricula.quantidade_pagamentos || plano.quantidade_parcelas
        ) || 0;

        const pagamentosAtuais = Array.isArray(matricula.pagamentos)
          ? matricula.pagamentos
          : [];

        const parcelasAtuais = pagamentosAtuais.filter(
          p => !p.pgt_matricula && !p.adicional
        );

        // Parcelas adicionais (e qualquer outro registro não contratual)
        // não podem desaparecer quando a quantidade contratual for recalculada.
        const outrosPagamentos = pagamentosAtuais.filter(
          p => p.pgt_matricula || p.adicional
        );

        const valorParcela = Number(
          matricula.valor_com_desconto_mensalidade || 0
        );

        const novasParcelas = [];
        let dataBase = new Date(`${matricula.primeiro_vencimento}T00:00:00`);

        for (let i = 0; i < quantidade; i++) {
          const vencimento = dataBase.toISOString().split("T")[0];
          const existente = parcelasAtuais[i];

          const vencimentoAnterior = existente?.data_vencimento
            ? String(existente.data_vencimento).slice(0, 10)
            : null;

          const pagamentoAnterior = existente?.data_pagamento
            ? String(existente.data_pagamento).slice(0, 10)
            : null;

          const vencimentoMudou =
            !!existente &&
            !!vencimentoAnterior &&
            vencimentoAnterior !== vencimento;

          const pagamentoEraAutomatico =
            !!vencimentoAnterior &&
            !!pagamentoAnterior &&
            vencimentoAnterior === pagamentoAnterior;

          const dataPagamentoPreservada =
            vencimentoMudou && pagamentoEraAutomatico
              ? vencimento
              : (existente?.data_pagamento ?? "");

          const dataRecebimentoPreservada =
            vencimentoMudou && pagamentoEraAutomatico
              ? null
              : (existente?.data_recebimento ?? null);

          novasParcelas.push({
            ...(existente || {}),
            forma_pagamento_id: existente?.forma_pagamento_id ?? null,
            data_vencimento: vencimento,
            valor: valorParcela,
            data_pagamento: dataPagamentoPreservada,
            data_recebimento: dataRecebimentoPreservada,
            observacoes: existente?.observacoes ?? null,
            banco_numero: existente?.banco_numero ?? "",
            agencia: existente?.agencia ?? "",
            cheque_numero: existente?.cheque_numero ?? "",
            conta_financeira_id: existente?.conta_financeira_id ?? null,
            numero_autorizacao: existente?.numero_autorizacao ?? null,
            adicional: false,
            pgt_matricula: false
          });

          dataBase.setMonth(dataBase.getMonth() + 1);
        }

        if (novasParcelas.length) {
          try { redistribuir(centavos(totalEsperadoMensalidade.value), novasParcelas, isParcelaNominalProtegida); }
          catch (error) { toast.error(error.message); return; }
        }
        matricula.pagamentos = [...novasParcelas, ...outrosPagamentos];
        return;
      } else {
        // Se estiver editando, atualiza apenas os vencimentos das parcelas não pagas
        let dataBase;
        const parcelasPagas = matricula.pagamentos.filter(p => p.data_pagamento && p.data_pagamento.trim() !== "");
        if (parcelasPagas.length > 0) {
          parcelasPagas.sort((a, b) => new Date(a.data_vencimento) - new Date(b.data_vencimento));
          dataBase = new Date(parcelasPagas[parcelasPagas.length - 1].data_vencimento);
          dataBase.setMonth(dataBase.getMonth() + 1);
        } else {
          dataBase = new Date(matricula.primeiro_vencimento);
        }

        const parcelasNaoPagas = matricula.pagamentos.filter(p => !p.adicional && !p.pgt_matricula &&
          !isParcelaNominalProtegida(p) &&
          (!p.contrato || String(p.contrato) === String(matricula.numero_contrato)));
        parcelasNaoPagas.forEach((parcela, index) => {
          let novaData = new Date(dataBase);
          novaData.setMonth(novaData.getMonth() + index);
          parcela.data_vencimento = novaData.toISOString().split("T")[0];
        });
      }
    };

    const recriarParcelasMensalidade = () => {
      // só faz sentido em edição e quando está vazio
      if (!matricula.id) return;
      if ((matricula.pagamentos || []).length > 0) return;

      // precisa ter 1º vencimento e quantidade
      const qtd = Number(matricula.quantidade_pagamentos || plano.quantidade_parcelas || 0);
      if (!qtd) {
        toast.error("Quantidade de pagamentos do plano não definida.");
        return;
      }
      if (!matricula.primeiro_vencimento) {
        toast.error("Informe o 1º vencimento para gerar as parcelas.");
        return;
      }

      // garante valores atualizados
      calcularValorComDescontoMensalidade();
      calcularValorTotalMensalidade();

      const valorParcela = Number(matricula.valor_com_desconto_mensalidade || 0);

      matricula.pagamentos = [];
      let dataBase = new Date(matricula.primeiro_vencimento);

      for (let i = 0; i < qtd; i++) {
        const venc = dataBase.toISOString().split("T")[0];

        matricula.pagamentos.push({
          id: null,                    // novo
          forma_pagamento_id: null,    // usuário escolhe
          data_vencimento: venc,
          valor: valorParcela,
          data_pagamento: "",
          data_recebimento: "",
          conta_financeira_id: null,
          banco_numero: "",
          agencia: "",
          cheque_numero: "",
          adicional: false,
          pgt_matricula: false,
          observacoes: null,
          numero_autorizacao: null
        });

        dataBase.setMonth(dataBase.getMonth() + 1);
      }

      // opcional: já entra em modo avançado
      // modoAvancado.value = true;
    };

    const podeAdicionarPagamento = computed(() => {
      return true;
    });

    const adicionarPagamento = () => {
      matricula.pagamentos.push({
        forma_pagamento_id: null,
        data_vencimento: "",
        valor: 0,
        data_pagamento: "",
        data_recebimento: "",
        conta_financeira_id: null,
        banco_numero: "",
        agencia: "",
        cheque_numero: "",
        numero_autorizacao: "",
        adicional: false,
        pgt_matricula: false,
        observacoes: null,
        _mostrarHistorico: true
      });

      recalcularParcelas();
    };

    const adicionarParcelaAdicional = () => {
      const todas = Array.isArray(matricula.pagamentos) ? matricula.pagamentos : [];
      const datasValidas = todas
        .map(p => p.data_vencimento)
        .filter(d => d && d.trim());

      const base = datasValidas.length
        ? new Date(datasValidas.sort((a, b) => new Date(a) - new Date(b)).slice(-1)[0])
        : new Date(matricula.primeiro_vencimento || matricula.data_matricula || new Date());

      const sugestao = new Date(base);
      const venc = sugestao.toISOString().split('T')[0];

      matricula.pagamentos.push({
        forma_pagamento_id: null,
        data_vencimento: venc,
        valor: "",
        data_pagamento: "",
        data_recebimento: "",
        conta_financeira_id: null,
        banco_numero: "",
        agencia: "",
        cheque_numero: "",
        numero_autorizacao: "",
        adicional: true,
        pgt_matricula: false,
        observacoes: null,
        _mostrarHistorico: true
      });
    };

    const desformatarValorAdicional = (pagamento) => {
      if (pagamento.valor) {
        // Remove espaços, "R$", e pontos de milhar
        let unformatted = pagamento.valor
          .replace(/\s/g, '')
          .replace('R$', '')
          .replace(/\./g, '')
          .trim();
        // Aqui, mantemos a vírgula intacta para que o usuário possa usá-la
        pagamento.valor = unformatted;
      }
    };

    // Método para formatar o valor quando o campo perde o foco
    const formatarValorAdicional = (pagamento) => {
      if (pagamento.valor) {
        // Remove espaços e "R$"
        let valorLimpo = pagamento.valor
          .replace(/\s/g, '')
          .replace('R$', '')
          .replace(/\./g, ''); // remove pontos de milhar que o usuário eventualmente digitar
        // Substitui vírgula por ponto para a conversão
        valorLimpo = valorLimpo.replace(',', '.');

        const numero = parseFloat(valorLimpo);
        if (isNaN(numero)) {
          pagamento.valor = '';
          return;
        }
        // Formata o número para o padrão brasileiro, ex: R$ 1.234,56
        const valorFormatado = new Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: 'BRL',
          minimumFractionDigits: 2
        }).format(numero);
        pagamento.valor = valorFormatado;
      }
    };

    const converterValor = (valorFormatado) => {
      if (typeof valorFormatado === 'string') {
        // Remove tudo que não seja dígito, vírgula, ponto ou hífen
        let valorLimpo = valorFormatado.replace(/[^\d,.-]/g, '');
        // Remove pontos de milhar (se houver)
        valorLimpo = valorLimpo.replace(/\./g, '');
        // Troca a vírgula decimal por ponto
        valorLimpo = valorLimpo.replace(',', '.');
        return parseFloat(valorLimpo);
      }
      return valorFormatado;
    };

    // exibe sempre formatado (R$ 1.234,56)
    const valorMensalidadeDisplay = computed(() =>
      globalUtils.formatarValor(matricula.valor_mensalidade)
    );

    // ao focar: mostra número editável (1.234,56 → 1234,56)
    function onValorMensalidadeFocus(e) {
      const v = Number(matricula.valor_mensalidade || 0);
      e.target.value = v.toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    }

    // Guarda a entrada antes do parser legado para a transição para Boleto.
    let entradaMensalidadeTexto = null;
    let valorMensalidadeLegado;
    let entradaDescontoTexto = null;
    let descontoMensalidadeLegado;

    function capturarDescontoMensalidade(e) {
      entradaDescontoTexto = e.target.value;
      descontoMensalidadeLegado = parseFloat(e.target.value);
    }

    watch(entradaMensalidadeManaged, (managed) => {
      if (managed) {
        matricula.valor_mensalidade = normalizarMoedaContratualTexto(
          entradaMensalidadeTexto !== null && Object.is(matricula.valor_mensalidade, valorMensalidadeLegado)
            ? entradaMensalidadeTexto
            : matricula.valor_mensalidade ?? '0.00'
        ) || '0.00';
        matricula.desconto_mensalidade = entradaDescontoTexto !== null &&
          Object.is(matricula.desconto_mensalidade, descontoMensalidadeLegado)
            ? entradaDescontoTexto
            : String(matricula.desconto_mensalidade ?? '0');
      } else {
        matricula.valor_mensalidade = Number(matricula.valor_mensalidade) || 0;
        matricula.desconto_mensalidade = parseFloat(matricula.desconto_mensalidade) || 0;
      }
    }, { flush: 'sync' });

    // ao sair: parseia o que o usuário digitou e re-formata
    function onValorMensalidadeBlur(e) {
      entradaMensalidadeTexto = e.target.value;
      if (!entradaMensalidadeManaged.value) {
        // usa seu helper para aceitar “1.234,56”, “1234.56”, etc.
        const num = globalUtils.converterValor(e.target.value);
        matricula.valor_mensalidade = isNaN(num) ? 0 : num;

        // re-renderiza formatado e dispara seus cálculos
        e.target.value = globalUtils.formatarValor(matricula.valor_mensalidade);
        onValorMensalidadeChange(); // você já tem essa função
        valorMensalidadeLegado = matricula.valor_mensalidade;
        return;
      }

      const canonical =
        normalizarMoedaContratualTexto(
          e.target.value
        );

      matricula.valor_mensalidade =
        canonical ||
        '0.00';

      // A apresentação continua formatada; a autoridade
      // contratual permanece como texto decimal canônico.
      e.target.value =
        globalUtils.formatarValor(
          matricula.valor_mensalidade
        );

      onValorMensalidadeChange();
    }


    // Mostra número “editável” ao focar (2 casas, sem R$)
    function onParcelaValorFocus(e, pag) {
      const v = Number(pag.valor || 0);
      e.target.value = v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }

    // Ao sair: converte string BR -> número e volta a exibir formatado
    function onParcelaValorBlur(e, pag) {
      // usa seu converterValor para parsear “1.234,56”
      const n = converterValor(e.target.value);
      pag.valor = isNaN(n) ? 0 : n;
      // a UI será re-renderizada e :value usa globalUtils.formatarValor(pag.valor)
    }

    // Propriedade computada para calcular o novo valor total somando as parcelas adicionais
    const valorTotalComParcelasAdicionais = computed(() => {
      let total = parseFloat(matricula.valor_total_mensalidade) || 0;
      // Filtra apenas as parcelas adicionais que tenham valor definido
      const adicionais = matricula.pagamentos.filter(p => p.adicional && p.valor);
      adicionais.forEach(p => {
        const valor = typeof p.valor === 'string' ? converterValor(p.valor) : p.valor;
        total += isNaN(valor) ? 0 : valor;
      });
      return total;
    });

    const valorParcelasAdicionais = computed(() => {
      let soma = 0;
      matricula.pagamentos.forEach(p => {
        if (p.adicional && p.valor) {
          let valor = typeof p.valor === 'string'
            ? p.valor.replace(/[^\d,.-]/g, '').replace(/\./g, '').replace(',', '.')
            : p.valor;
          valor = parseFloat(valor);
          if (!isNaN(valor)) {
            soma += valor;
          }
        }
      });
      return soma;
    });


    const recalcularParcelas = () => {
      if (conferenciaTrocaIndisponivel.value || totalEsperadoMensalidade.value == null) {
        toast.error('Composição financeira indisponível. Atualize a matrícula antes de redistribuir.');
        return;
      }
      const parcelasAutomaticas = matricula.pagamentos.filter(p => !p.adicional && !p.pgt_matricula &&
        (!p.contrato || String(p.contrato) === String(matricula.numero_contrato)));
      const protegida = isParcelaNominalProtegida;
      try {
        redistribuir(centavos(totalEsperadoMensalidade.value), parcelasAutomaticas, protegida);
      } catch (error) {
        toast.error(error.message);
        return;
      }

      // Atualiza os vencimentos:
      let dataBase;
      const parcelasPagasArray = parcelasAutomaticas.filter(protegida);
      if (parcelasPagasArray.length > 0) {
        parcelasPagasArray.sort((a, b) => new Date(a.data_vencimento) - new Date(b.data_vencimento));
        dataBase = new Date(parcelasPagasArray[parcelasPagasArray.length - 1].data_vencimento);
        dataBase.setMonth(dataBase.getMonth() + 1);
      } else if (matricula.primeiro_vencimento) {
        dataBase = new Date(matricula.primeiro_vencimento);
      } else {
        return;
      }

      // A quantidade de registros financeiros é livre; todos os elegíveis recebem vencimento.
      const parcelasNaoPagas = parcelasAutomaticas.filter(p => !protegida(p));
      parcelasNaoPagas.forEach((parcela, index) => {
        const novaData = new Date(dataBase);
        novaData.setMonth(novaData.getMonth() + index);
        parcela.data_vencimento = novaData.toISOString().split("T")[0];
      });
    };


    const isCheque = (formaPagamentoId) => {
        const forma = formasPagamento.value.find(fp => fp.id === formaPagamentoId);
        const isChequePagamento = forma ? forma.nome.toLowerCase() === "cheque" : false;
        return isChequePagamento;
    };

    const existeCheque = () => {
      return matricula.pagamentos.some(p => {
        const nome = formasPagamento.value.find(f => f.id === p.forma_pagamento_id)?.nome;
        return (nome || '').toLowerCase() === 'cheque';
      });
    };

    const isEspecie = (formaId) => {
      const forma = formasPagamento.value.find(f => f.id === formaId);
      return forma?.nome === 'Espécie';
    };
    const isAInformar = (formaId) => {
      const forma = formasPagamento.value.find(f => f.id === formaId);
      return forma?.nome === 'A informar';
    };

    const norm = (s) => (s ?? '')
      .toString()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .trim().toLowerCase();

    const getFormaById = (id) => formasPagamento.value.find(f => Number(f.id) === Number(id));

    const isCartao = (formaId) => {
      const forma = getFormaById(formaId);
      return norm(forma?.nome).includes('cartao');
    };

    const isCartaoDebito = (formaId) => {
      const forma = getFormaById(formaId);
      const n = norm(forma?.nome);
      return n.includes('cartao') && n.includes('debito');
    };

    const isCartaoCreditoAVista = (formaId) => {
      const forma = getFormaById(formaId);
      const n = norm(forma?.nome);
      return (
        n === 'cartao de credito' ||
        (n.includes('cartao') && n.includes('credito') && n.includes('vista'))
      );
    };

    const isCartaoCreditoParcelado = (formaId) => {
      const forma = getFormaById(formaId);
      const n = norm(forma?.nome);
      return n.includes('cartao') && n.includes('credito') && n.includes('parcelado');
    };

    const isFormaComAutorizacao = (formaId) => {
      return (
        isCartaoCreditoAVista(formaId) ||
        isCartaoCreditoParcelado(formaId) ||
        isCartaoDebito(formaId)
      );
    };

    const getDataBaseBlocoParcelado = (pag) => {
      const bloco = getBlocoParceladoDaParcela(pag);
      const primeira = bloco?.[0];

      if (!primeira) return null;

      return (
        primeira.data_pagamento ||
        primeira.data_vencimento ||
        pag.data_pagamento ||
        pag.data_vencimento ||
        null
      );
    };

    const getParcelasMensalidadeOrdenadas = () => {
      return (matricula.pagamentos || [])
        .filter(p => !p.pgt_matricula)
        .slice()
        .sort((a, b) => {
          const da = String(a?.data_vencimento || '');
          const db = String(b?.data_vencimento || '');
          return da.localeCompare(db);
        });
    };

    const getBlocoParceladoDaParcela = (pag) => {
      const lista = getParcelasMensalidadeOrdenadas();
      const idx = lista.findIndex(p =>
        p === pag || (p?.id && pag?.id && Number(p.id) === Number(pag.id))
      );

      if (idx === -1) return [];

      if (!isCartaoCreditoParcelado(lista[idx]?.forma_pagamento_id)) return [lista[idx]];

      let ini = idx;
      let fim = idx;

      while (ini > 0 && isCartaoCreditoParcelado(lista[ini - 1]?.forma_pagamento_id)) {
        ini--;
      }

      while (fim < lista.length - 1 && isCartaoCreditoParcelado(lista[fim + 1]?.forma_pagamento_id)) {
        fim++;
      }

      return lista.slice(ini, fim + 1);
    };

    const getNumeroParcelaNoBlocoParcelado = (pag) => {
      const bloco = getBlocoParceladoDaParcela(pag);
      const idx = bloco.findIndex(p =>
        p === pag || (p?.id && pag?.id && Number(p.id) === Number(pag.id))
      );
      return idx >= 0 ? idx + 1 : 1;
    };

    const isInicioBlocoParcelado = (pag) => {
      if (!isCartaoCreditoParcelado(pag?.forma_pagamento_id)) return false;

      const lista = getParcelasMensalidadeOrdenadas();
      const idx = lista.findIndex(p =>
        p === pag || (p?.id && pag?.id && Number(p.id) === Number(pag.id))
      );

      if (idx === -1) return false;
      if (idx === 0) return true;

      return !isCartaoCreditoParcelado(lista[idx - 1]?.forma_pagamento_id);
    };

    const propagarBlocoParceladoParaLinhasVazias = (pag) => {
      if (!isInicioBlocoParcelado(pag)) return;

      const lista = getParcelasMensalidadeOrdenadas();
      const idx = lista.findIndex(p =>
        p === pag || (p?.id && pag?.id && Number(p.id) === Number(pag.id))
      );

      if (idx === -1) return;

      const autorizacao = (pag.numero_autorizacao || '').trim();
      const conta = pag.conta_financeira_id ?? null;

      for (let i = idx + 1; i < lista.length; i++) {
        const atual = lista[i];
        const formaAtual = atual?.forma_pagamento_id;

        // para ao encontrar uma próxima linha já definida
        if (formaAtual !== null && formaAtual !== '' && formaAtual !== undefined) {
          break;
        }

        atual.forma_pagamento_id = pag.forma_pagamento_id;

        if (!(atual.numero_autorizacao || '').trim() && autorizacao) {
          atual.numero_autorizacao = autorizacao;
        }

        if (
          (atual.conta_financeira_id === null ||
            atual.conta_financeira_id === '' ||
            atual.conta_financeira_id === undefined) &&
          conta
        ) {
          atual.conta_financeira_id = conta;
        }

        if (!atual.data_pagamento) {
          atual.data_pagamento = atual.data_vencimento || null;
        }
      }
    };

    const replicarAutorizacaoNoBlocoParcelado = (pag) => {
      if (!isCartaoCreditoParcelado(pag?.forma_pagamento_id)) return;
      const bloco = getBlocoParceladoDaParcela(pag);
      const valor = (pag.numero_autorizacao || '').trim();
      bloco.forEach(p => {
        p.numero_autorizacao = valor;
      });
    };

    const replicarContaNoBlocoParcelado = (pag) => {
      if (!isCartaoCreditoParcelado(pag?.forma_pagamento_id)) return;
      const bloco = getBlocoParceladoDaParcela(pag);
      const conta = pag.conta_financeira_id ?? null;
      bloco.forEach(p => {
        p.conta_financeira_id = conta;
      });
    };

    const recalcularRecebimentoParcelaMensal = (pag) => {
      const formaId = pag?.forma_pagamento_id;

      if (
        !formaId ||
        isAInformar(formaId) ||
        isBoleto(formaId)
      ) {
        pag.data_recebimento = null;
        return;
      }

      if (isCartaoCreditoParcelado(formaId)) {
        const numeroParcela = getNumeroParcelaNoBlocoParcelado(pag);
        const dataBaseOperacao = getDataBaseBlocoParcelado(pag);

        if (!dataBaseOperacao) {
          pag.data_recebimento = null;
          return;
        }

        pag.data_recebimento = globalUtils.calcularDataRecebimento(
          dataBaseOperacao,
          formaId,
          formasPagamento.value,
          numeroParcela
        );
        return;
      }

      if (!pag?.data_pagamento) {
        pag.data_recebimento = null;
        return;
      }

      pag.data_recebimento = globalUtils.calcularDataRecebimento(
        pag.data_pagamento,
        formaId,
        formasPagamento.value,
        1
      );
    };

    const recalcularRecebimentoBlocoParcelado = (pag) => {
      // Existing installments are corrected individually; creating a card block
      // still uses the established schedule for all new installments.
      if (pag.id) {
        recalcularRecebimentoParcelaMensal(pag);
        return;
      }
      const bloco = getBlocoParceladoDaParcela(pag);
      if (!bloco.length) return;

      const primeira = bloco[0];

      if (!primeira.data_pagamento) {
        primeira.data_pagamento = primeira.data_vencimento || null;
      }

      const dataBaseOperacao = getDataBaseBlocoParcelado(primeira);

      bloco.forEach((p, idx) => {
        if (p.id) return;
        if (!p.data_pagamento) {
          p.data_pagamento = p.data_vencimento || null;
        }

        if (!dataBaseOperacao) {
          p.data_recebimento = null;
          return;
        }

        p.data_recebimento = globalUtils.calcularDataRecebimento(
          dataBaseOperacao,
          p.forma_pagamento_id,
          formasPagamento.value,
          idx + 1
        );
      });
    };

    const onNumeroAutorizacaoMensalInput = (pag) => {
      if (isCartaoCreditoParcelado(pag?.forma_pagamento_id)) {
        replicarAutorizacaoNoBlocoParcelado(pag);
      }
    };

    const replicarContaNasParcelasBoletoNovas = (pag) => {
      if (!pag) return;
      if (!isBoleto(pag.forma_pagamento_id)) return;
      if (!isPrimeiraParcelaMensalidade(pag)) return;

      const contaFinanceiraId =
        pag.conta_financeira_id
          ? Number(
              pag.conta_financeira_id
            )
          : null;

      const lista =
        getParcelasMensalidadeOrdenadas();

      lista.forEach((parcela) => {
        if (!parcela) return;

        // A primeira parcela é a origem da alteração.
        if (parcela === pag) return;

        // Não altera silenciosamente registros já persistidos.
        if (parcela.id) return;

        if (
          !isBoleto(
            parcela.forma_pagamento_id
          )
        ) {
          return;
        }

        if (
          isPagamentoBloqueadoEdicao(
            parcela
          )
        ) {
          return;
        }

        parcela.conta_financeira_id =
          contaFinanceiraId;
      });
    };

    const onContaFinanceiraMensalChange = (pag) => {
      if (
        isBoleto(
          pag?.forma_pagamento_id
        )
      ) {
        replicarContaNasParcelasBoletoNovas(
          pag
        );

        return;
      }

      if (
        isCartaoCreditoParcelado(
          pag?.forma_pagamento_id
        )
      ) {
        replicarContaNoBlocoParcelado(
          pag
        );
      }
    };

    // ✅ conta padrão automática (exceto Espécie e A informar)
    const aplicarContaPadraoSeVazio = (formaId, targetObj, fieldName = 'conta_financeira_id') => {
      const forma = getFormaById(formaId);
      if (!forma || !targetObj) return;

      const nome = norm(forma.nome);

      // ❌ não aplicar para Espécie ou A informar
      if (nome === 'especie' || nome === 'a informar' || nome === 'informar') return;

      // só preenche se estiver vazio/nulo
      const atual = targetObj[fieldName];
      if (atual !== null && atual !== '' && atual !== undefined) return;

      // 1) padrão por forma
      const contaPorForma = forma.conta_financeira_padrao_id ? Number(forma.conta_financeira_padrao_id) : null;
      if (contaPorForma) {
        targetObj[fieldName] = contaPorForma;
        return;
      }

      // 2) fallback global (contas_financeiras.conta_padrao)
      const fallback = contaPadraoGlobalId.value;
      if (fallback) targetObj[fieldName] = fallback;
    };


    const onValorMensalidadeChange = () => {
      // atualiza valores agregados
      calcularValorComDescontoMensalidade();
      calcularValorTotalMensalidade();
      // em novo OU edição: redistribui o saldo entre parcelas não pagas
      recalcularParcelas();
    };


  const removerPagamento = (pagamento) => {
    if (!pagamento) return;

    const pagamentoNovo = !pagamento.id;

    /*
    * Registros protegidos pelo backend:
    * conciliados, substituídos ou com histórico financeiro.
    */
    if (
      !pagamentoNovo &&
      (
        pagamento.bloqueado_exclusao ||
        isParcelaNominalProtegida(pagamento) ||
        pagamento.bloqueado_por_conciliacao
      )
    ) {
      toast.error(
        'Este registro possui conciliação ou histórico financeiro e não pode ser excluído.'
      );
      return;
    }

    /*
    * Pagamento efetivamente recebido não pode ser excluído.
    * A exceção é uma linha nova ainda não salva, caso algum preenchimento
    * automático tenha colocado uma data temporária.
    */
    if (!pagamentoNovo && (pagamento.data_pagamento || pagamento.data_recebimento)) {
      toast.error('Não é possível excluir um pagamento que já foi recebido.');
      return;
    }

    if (!pagamento.adicional && !pagamento.pgt_matricula && matricula.pagamentos.filter(p => !p.adicional && !p.pgt_matricula).length <= 1) {
      toast.error('Mantenha ao menos uma parcela para distribuir o total.');
      return;
    }
    const idx = (matricula.pagamentos || []).findIndex(p =>
      p === pagamento ||
      (
        p?.id &&
        pagamento?.id &&
        Number(p.id) === Number(pagamento.id)
      )
    );

    if (idx === -1) return;

    /*
    * Linha recém-adicionada e ainda não salva:
    * remove diretamente, sem exigir confirmação.
    */
    if (pagamentoNovo) {
      matricula.pagamentos.splice(idx, 1);
      if (!pagamento.adicional) recalcularParcelas();
      return;
    }

    /*
    * Registro existente, mas ainda não recebido e sem bloqueios:
    * pede confirmação antes de removê-lo do formulário.
    * A exclusão no banco ocorrerá ao salvar.
    */
    const descricao = pagamento.adicional
      ? 'esta parcela adicional'
      : 'este lançamento';

    const confirmou = window.confirm(
      `Deseja realmente excluir ${descricao}? A exclusão será efetivada ao salvar a matrícula.`
    );

    if (!confirmou) return;

    matricula.pagamentos.splice(idx, 1);

    /*
    * Não recalcula automaticamente quando for parcela adicional.
    * Para parcelas contratuais, mantém a regra financeira existente.
    */
    if (!pagamento.adicional) {
      recalcularParcelas();
    }
  };

    const descontoDesabilitado = computed(() => {
      return matricula.pagamentos.some(p => p.data_pagamento); // Retorna `true` se existir algum pagamento efetuado
    });

    const carregarMatricula = async () => {
      terminoVigenciaTouched.value = true;
      mensalidadeLoaded.value = false;

      if (!route.params.id) return;

      try {
        // 1️⃣ Busca os dados principais da matrícula
        const response = await axios.get(`/operacional/matriculas/${route.params.id}`);
        Object.assign(matricula, response.data);
        await carregarComposicaoTroca();

        // ✅ PEGA O VALOR SALVO NO BANCO
        if (response?.data) {
          // use os nomes reais que seu backend retorna:
          // exemplo comum: response.data.valor_mensalidade
          const vm = Number(response.data.valor_mensalidade ?? response.data.valorMensalidade ?? matricula.valor_mensalidade);
          if (Number.isFinite(vm)) {
            matricula.valor_mensalidade = vm;
          }
          const dm = Number(response.data.desconto_mensalidade ?? response.data.descontoMensalidade ?? matricula.desconto_mensalidade);
          if (Number.isFinite(dm)) {
            matricula.desconto_mensalidade = dm;
          }
          valorMensalidadeHydrated.value = true; // ← marca que veio do banco
        }

        const intOrNull = (v) => (v === undefined || v === null || v === '' ? null : parseInt(v, 10));

        matricula.categoria_id  = intOrNull(matricula.categoria_id);
        matricula.tipo_plano_id = intOrNull(matricula.tipo_plano_id);
        matricula.nivel_id      = intOrNull(matricula.nivel_id);
        plano.aulas_semana         = Number(matricula.aulas_semana)   || 0;
        plano.quantidade_parcelas = Number(matricula.quantidade_parcelas) || 0;

        // Formata as datas da matrícula
        matricula.inicio_vigencia    = globalUtils.formatarDataParaInput(matricula.inicio_vigencia);
        matricula.termino_vigencia   = globalUtils.formatarDataParaInput(matricula.termino_vigencia);
        matricula.data_matricula     = globalUtils.formatarDataParaInput(matricula.data_matricula);
        matricula.primeiro_vencimento= globalUtils.formatarDataParaInput(matricula.primeiro_vencimento);
        matricula.data_vencimento    = globalUtils.formatarDataParaInput(matricula.data_vencimento);

        // Carrega formas de pagamento antes de montar pagamentos
        await carregarFormasPagamento();

        // Busca nomes de aluno e responsável
        if (matricula.aluno_id) {
          const alunoResp = await axios.get(`/cadastros/pessoas-fisicas/${matricula.aluno_id}`);
          matricula.aluno_nome = alunoResp.data.nome;
        }
        if (matricula.responsavel_id) {
          const respResp = await axios.get(`/cadastros/pessoas-fisicas/${matricula.responsavel_id}`);
          matricula.responsavel_nome = respResp.data.nome;
        }

        // Carrega configuração de plano e turmas
        await carregarTiposPlano();
        {
          const turmasResp = await axios.get(`/operacional/matriculas-turmas?matricula_id=${matricula.id}`);

          const lista = Array.isArray(turmasResp.data) ? turmasResp.data : [];

          const fixTime = (h) => {
            const s = String(h || '').trim();
            // pega só HH:MM:SS do começo, ignorando microssegundos/timezone
            const m = s.match(/^(\d{2}:\d{2}:\d{2})/);
            return m ? m[1] : s;
          };

          matricula.turmas = lista.map(t => {
            const horarioFix = fixTime(t.horario);

            return {
              nivel_id: Number(t.nivel_id ?? matricula.nivel_id) || null,
              turma_id: `${t.turma_id}-${t.dia_semana}-${horarioFix}`,
              dia:      t.dia_semana,
              horario:  horarioFix,
              turma_completa: t.turma_completa,
              instrutor:t.instrutor
            };
          });

          turmasOriginais.value = matricula.turmas.map(t => ({ ...t }));

          // ✅ liga modo avançado se houver MAIS DE UM nível distinto nas turmas
          const niveisUsados = Array.from(
            new Set((matricula.turmas || [])
              .map(t => (t?.nivel_id != null ? Number(t.nivel_id) : null))
              .filter(v => Number.isFinite(v))
            )
          );

          if (niveisUsados.length > 1) {
            modoAvancado.value = true;
          }

          const nivelBase = Number(matricula.nivel_id) || null;
          matricula.turmas = (matricula.turmas || []).map(l => ({
            ...l,
            nivel_id: (l?.nivel_id != null ? Number(l.nivel_id) : nivelBase)
          }));

          // ✅ garante nivel_id em todas as linhas (fallback no nível base)
          matricula.turmas = (matricula.turmas || []).map(l => ({
            ...l,
            nivel_id: (l?.nivel_id != null ? Number(l.nivel_id) : nivelBase)
          }));

          ensureMinSlots(plano.aulas_semana);
        }

        // 2️⃣ Carrega o pagamento de MATRÍCULA na nova tabela matriculas_pagamentos
        const matPagResp = await axios.get(`/operacional/matriculas-pagamentos/${matricula.id}`);
        originalTaxPayment = JSON.parse(JSON.stringify(matPagResp.data));
        const matPag = matPagResp.data; // null ou objeto único

        if (matPag) {
          // Popula o objeto interno
          matricula.pagamento_matricula = {
            id:                 matPag.id,
            forma_pagamento_id: (matPag.forma_pagamento_id != null ? parseInt(matPag.forma_pagamento_id, 10) : null),
            data_vencimento:    globalUtils.formatarDataParaInput(matPag.data_vencimento),
            data_pagamento: matPag.data_pagamento ? globalUtils.formatarDataParaInput(matPag.data_pagamento) : "",
            data_recebimento: matPag.data_recebimento
              ? globalUtils.formatarDataParaInput(matPag.data_recebimento)
              : "",
            valor:              matPag.valor || 0,
            conta_financeira_id: (matPag.conta_financeira_id != null ? parseInt(matPag.conta_financeira_id, 10) : null),
            banco_numero:       matPag.banco_numero || "",
            agencia:            matPag.agencia || "",
            cheque_numero:      matPag.cheque_numero || "",
            adicional:          matPag.adicional || false,
            contrato:           matricula.numero_contrato || "",
            pgt_matricula:      true,
            observacoes:        matPag.observacoes || null,
            numero_autorizacao: matPag.numero_autorizacao || "",
          };
          pagMatLoaded.value = true

        } else {
          // Inicializa vazio para novo registro
          matricula.pagamento_matricula = {
            id:                  null,
            forma_pagamento_id:  "",
            data_vencimento:     "",
            data_pagamento:      "",
            data_recebimento:    "",
            valor:               matricula.valor_com_desconto || 0,
            conta_financeira_id: "",
            banco_numero:        "",
            agencia:             "",
            cheque_numero:       "",
            adicional:           false,
            contrato:            matricula.numero_contrato || "",
            pgt_matricula:       true,
            observacoes:         null
          };
          // Garante que o campo de edição comece vazio
          matricula.data_pagamento = "";

        }

        // 3️⃣ Carrega as parcelas de MENSALIDADE em pagamentos_matriculas
        const mesResp = await axios.get(`/operacional/pagamentos-matriculas/${matricula.id}`);
        const todas = Array.isArray(mesResp.data) ? mesResp.data : [];
        originalMonthlyPayments = JSON.parse(JSON.stringify(todas));

        matricula.pagamentos = todas
          .filter(p => {
            if (p.pgt_matricula) return false

            const contratoPagamento = String(
              p.contrato_efetivo ||
              p.contrato ||
              ''
            ).trim()

            const contratoMatricula = String(
              matricula.numero_contrato ||
              ''
            ).trim()

            return contratoPagamento === contratoMatricula
          })
          .map(p => ({
            ...p,

            contrato:
              p.contrato_efetivo ||
              p.contrato ||
              matricula.numero_contrato,

            forma_pagamento_id: (p.forma_pagamento_id != null ? parseInt(p.forma_pagamento_id, 10) : null),
            forma_pagamento_nome: formasPagamento.value.find(f => f.id === p.forma_pagamento_id)?.nome || "",
            data_vencimento: globalUtils.formatarDataParaInput(p.data_vencimento),
            data_pagamento: p.data_pagamento
              ? globalUtils.formatarDataParaInput(p.data_pagamento)
              : "",
            data_recebimento: p.data_recebimento
              ? globalUtils.formatarDataParaInput(p.data_recebimento)
              : "",
            valor: p.valor != null ? Number(p.valor) : 0,
            banco_numero: p.banco_numero || "",
            agencia: p.agencia || "",
            cheque_numero: p.cheque_numero || "",
            adicional: p.adicional,
            conta_financeira_id: (p.conta_financeira_id != null ? parseInt(p.conta_financeira_id, 10) : null),
            observacoes: p.observacoes || null,
            numero_autorizacao: p.numero_autorizacao || "",
            pagamento_substituto: p.pagamento_substituto === true,
            pagamento_origem_id: p.pagamento_origem_id != null ? Number(p.pagamento_origem_id) : null,
            pagamento_substituto_id: p.pagamento_substituto_id != null ? Number(p.pagamento_substituto_id) : null,
            pagamentos_origem_ids: Array.isArray(p.pagamentos_origem_ids) ? p.pagamentos_origem_ids.map(Number) : [],
            eventos_cheque: Array.isArray(p.eventos_cheque) ? p.eventos_cheque : [],
            conciliacoes_ativas: Array.isArray(p.conciliacoes_ativas) ? p.conciliacoes_ativas : [],
            motivos_bloqueio: Array.isArray(p.motivos_bloqueio) ? p.motivos_bloqueio : [],
            bloqueado_por_conciliacao: p.bloqueado_por_conciliacao === true,
            bloqueado_edicao: p.bloqueado_edicao === true,
            bloqueado_exclusao: p.bloqueado_exclusao === true,
            _mostrarHistorico: false
          }))
          .sort(compararParcelasMensalidade);

        /*
        * Garante que a quantidade contratual fique coerente com
        * a vigência carregada antes de criar o snapshot da edição.
        */
        if (
          matricula.encerrar_vigencia_final_ano &&
          matricula.inicio_vigencia &&
          matricula.termino_vigencia
        ) {
          aplicarQuantidadePagamentosAteTermino();
        }

        mensalidadeLoaded.value = true;

        // Se já houver parcelas adicionais, entra em modo avançado automaticamente
        if (matricula.pagamentos.some(p => p.adicional)) {
          modoAvancado.value = true;
        }

        await nextTick();
        terminoVigenciaTouched.value = false; // libera após montar tudo

      // ✅ snapshot do estado original após hidratar tudo (EDIÇÃO)
      matriculaOriginal.value = deepClone(matricula);

      } catch (error) {
        console.error("❌ Erro ao carregar matrícula:", error);
        mensalidadeLoaded.value = true;
      }
    };


    const formValid = computed(() => {
      // ——— Básicos obrigatórios ———
      const camposObrig = Boolean(
        matricula.aluno_id &&
        matricula.categoria_id &&
        matricula.tipo_plano_id &&
        matricula.nivel_id &&
        matricula.data_matricula &&
        matricula.inicio_vigencia &&
        matricula.direito_imagem !== null
      );

      // ——— Turmas: precisa ter slots preenchidos conforme o plano ———
      const min = Number(plano.aulas_semana || 0);
      const selecionadas = Array.isArray(matricula.turmas)
        ? matricula.turmas.filter(t => (t?.turma_id || '').trim()).length
        : 0;

      const turmaOk = selecionadas >= min; // ✅ mínimo de turmas selecionadas

      // ——— Pagamento da MATRÍCULA (cartão/cheque/depósito/etc) ———
      // const formaFin = formasPagamento.value.find(
      //   f => f.id === matricula.pagamento_matricula.forma_pagamento_id
      // );

      // const nomeFin = formaFin?.nome ?? null;

      // Em EDIÇÃO: se o usuário não mexeu nisso, não bloqueie
      // const pgtMatSemFormaSelecionada = !matricula.pagamento_matricula.forma_pagamento_id;

      const financeiroOk = (() => {
        // Em EDIÇÃO: se o usuário não mexeu nisso, não bloqueie
        const pgtMatSemFormaSelecionada = !matricula.pagamento_matricula.forma_pagamento_id;
        if (pgtMatSemFormaSelecionada) return true;

        const formaRef = formasPagamento.value.find(
          f => f.id === matricula.pagamento_matricula.forma_pagamento_id
        );
        const nomeFin = formaRef?.nome ?? null;

        // "A informar" nunca exige conta nem datas
        if (nomeFin === 'A informar') return true;

        // Cartão exige autorização
        // Cartão exige autorização (pagamento da matrícula)
        if (isCartao(matricula.pagamento_matricula.forma_pagamento_id)) {
          const aut = (matricula.pagamento_matricula.numero_autorizacao || '').trim();
          if (!aut) return false;
        }

        // Demais formas (inclui "Espécie"):
        // Se informou data de pagamento, exige conta
        const temPgto = Boolean((matricula.pagamento_matricula.data_pagamento || "").trim());
        if (!temPgto) return true;
        return Boolean(matricula.pagamento_matricula.conta_financeira_id);
      })();

      // ——— Parcelas de MENSALIDADE ———
      const mensOk = (matricula.pagamentos || []).every(p => {
        /* Registros históricos/protegidos já foram validados no backend. */
        if (isPagamentoBloqueadoEdicao(p)) return true;

        const nome = formasPagamento.value.find(fp => fp.id === p.forma_pagamento_id)?.nome;

        if (!(p.data_vencimento || "").trim()) return false;
        if (!p.forma_pagamento_id) return false;

        if (nome === 'A informar') return true;

        if (!p.conta_financeira_id) return false;

        if ((nome || '').toLowerCase() === 'cheque' && (p.data_pagamento || "").trim()) {
          return Boolean(
            (p.banco_numero || "").trim() &&
            (p.agencia || "").trim() &&
            (p.cheque_numero || "").trim()
          );
        }

        if (isFormaComAutorizacao(p.forma_pagamento_id)) {
          if (!(p.numero_autorizacao || '').trim()) return false;
        }

        return true;
      });

      return camposObrig && turmaOk && financeiroOk && mensOk;
    });

    const loading = ref(false);

    const instance = getCurrentInstance();
    const gp = instance?.appContext.config.globalProperties;
    const canEditRecebimento = computed(() => {
      // 1) Permissão explícita via lista de permissões
      const perms = gp?.$globalState?.userPermissions;
      const hasAdminPerm = Array.isArray(perms)
        ? perms.map(s => String(s).toLowerCase().trim()).includes('administrador')
        : false;
      // 2) Perfil/role pode ser string OU objeto
      const rawProfile = gp?.$globalState?.userProfile;
      const profileStr = typeof rawProfile === 'string'
        ? rawProfile
        : (rawProfile?.perfil ?? rawProfile?.role ?? gp?.$globalState?.role ?? '');
      const isAdminByTitle = /(^|\s)(administrador|admin)(\s|$)/i.test(String(profileStr).trim());
      // 3) Flag booleana opcional
      const isAdminFlag = !!gp?.$globalState?.isAdmin;
      return hasAdminPerm || isAdminByTitle || isAdminFlag || (Array.isArray(perms) && perms.includes('operar_matriculas'));
    });

    const criarRegistrosAcessoriosNovaMatricula = async (matriculaId) => {
      const hojeISO = new Date().toISOString().split('T')[0];
      const hojeBR = globalUtils.formatarData(hojeISO);

      const dataProximoContato = new Date();
      dataProximoContato.setDate(dataProximoContato.getDate() + 45);
      const proximoContatoISO = dataProximoContato.toISOString().split('T')[0];

      const userName =
        instance?.appContext.config.globalProperties.$globalState.userName || '';

      const posvendaPayload = {
        matricula_id: matriculaId,
        data_contato: hojeISO,
        descricao: `Matrícula realizada em ${hojeBR}`,
        proximo_contato: proximoContatoISO,
        nome_usuario: userName,
        aluno_id: matricula.aluno_id ? parseInt(matricula.aluno_id, 10) : null,
        responsavel_id: matricula.responsavel_id
          ? parseInt(matricula.responsavel_id, 10)
          : null,
        finalizado: false
      };

      try {
        await axios.post('/operacional/posvendas', posvendaPayload);
      } catch (error) {
        console.warn(
          '⚠️ Pós-venda automática não criada. Perfil pode não ter permissão.',
          error?.response?.status,
          error?.response?.data
        );
      }

      const observacoesPayload = {
        aluno_id: matricula.aluno_id ? parseInt(matricula.aluno_id, 10) : null,
        matricula_id: matriculaId,
        observacao: `Matrícula realizada em ${hojeBR}`,
        falta_avisada: false,
        data_falta: null,
        nome_usuario: userName,
        data_observacao: hojeISO
      };

      try {
        await axios.post('/operacional/alunos-observacoes', observacoesPayload);
      } catch (error) {
        console.warn(
          '⚠️ Observação inicial automática não criada. Perfil pode não ter permissão.',
          error?.response?.status,
          error?.response?.data
        );
      }
    };

    const validarParcelasMensalidadeNova = () => {
      // Esta barreira roda antes de criar a matrícula no backend.
      // O número de registros financeiros pode ser diferente da
      // quantidade de parcelas contratuais, desde que o total financeiro
      // corresponda ao total esperado do contrato.
      if (matricula.id) return true;

      const quantidadeContratual =
        Number(matricula.quantidade_pagamentos) || 0;

      if (
        novaMensalidadeBoletoSemantica.value &&
        !matricula.dia_vencimento_mensalidade
      ) {
        toast.error(
          "Selecione o dia de vencimento do Boleto antes de salvar."
        );

        return false;
      }

      if (!matricula.primeiro_vencimento) {
        toast.error(
          "Informe o 1º vencimento das mensalidades antes de salvar."
        );
        return false;
      }

      if (quantidadeContratual <= 0) {
        toast.error(
          "A quantidade de parcelas contratuais deve ser maior que zero."
        );
        return false;
      }

      const registrosFinanceiros = (matricula.pagamentos || []).filter(
        p => !p.pgt_matricula && !p.adicional
      );

      if (registrosFinanceiros.length === 0) {
        toast.error(
          "Inclua pelo menos um registro financeiro para as mensalidades."
        );
        return false;
      }

      const registrosIncompletos = registrosFinanceiros
        .map((p, index) => ({ p, numero: index + 1 }))
        .filter(({ p }) =>
          !p.forma_pagamento_id ||
          !p.data_vencimento
        );

      if (registrosIncompletos.length > 0) {
        const numeros = registrosIncompletos
          .map(item => item.numero)
          .join(", ");

        toast.error(
          `Preencha forma de pagamento e vencimento dos registros: ${numeros}.`
        );

        return false;
      }

      /*
      * A quantidade de registros financeiros é livre.
      * O que deve fechar é o valor financeiro com o total contratual.
      */
      if (existeDiferencaMensalidade.value) {
        toast.error(
          `O total dos registros financeiros (${globalUtils.formatarValor(
            totalLancamentosMensalidade.value
          )}) não confere com o total esperado (${globalUtils.formatarValor(
            totalEsperadoMensalidade.value
          )}).`
        );

        return false;
      }

      return true;
    };


    const salvarMatricula = async () => {
      if (loading.value) return;
      if (conferenciaTrocaIndisponivel.value) {
        toast.error('Composição financeira indisponível. Atualize a matrícula antes de salvar.');
        return;
      }
      if (matricula.turmas.some(t => !turmaEstaDisponivel(t) && !turmaHistoricaPreservada(t))) { alert(mensagemTurmaIndisponivel); return; }
      loading.value = true;

      // 1) Validações básicas
      if (
        !matricula.aluno_id ||
        !matricula.categoria_id ||
        !matricula.tipo_plano_id ||
        !matricula.nivel_id
      ) {
        alert("⚠️ Preencha todos os campos obrigatórios.");
        loading.value = false;
        return;
      }

      if (matricula.turmas.length === 0) {
        alert("⚠️ Adicione pelo menos uma turma.");
        loading.value = false;
        return;
      }

      try {
        const isEdicao = !!matricula.id;

        // 2) Recalcula os campos contratuais antes de montar o payload.
        // A sincronização das parcelas agora preserva o que o usuário digitou.
        if (matricula.encerrar_vigencia_final_ano) {
          aplicarQuantidadePagamentosAteTermino();
        }

        // Em matrícula nova, valide todas as mensalidades ANTES do primeiro POST.
        if (!isEdicao && !validarParcelasMensalidadeNova()) {
          return;
        }

        const contextoMensalidadeManagedNova =
          !isEdicao
            ? await prepararContextoMensalidadeManagedNova()
            : null;

        const payloadMatricula = {
          turmas: montarTurmasPayload(),
          numero_contrato: matricula.numero_contrato,
          aluno_id: parseInt(matricula.aluno_id),
          responsavel_id: matricula.responsavel_id
            ? parseInt(matricula.responsavel_id)
            : null,
          categoria_id: parseInt(matricula.categoria_id),
          tipo_plano_id: parseInt(matricula.tipo_plano_id),
          aulas_semana: parseInt(matricula.aulas_semana),
          quantidade_parcelas: parseInt(matricula.quantidade_parcelas),
          nivel_id: parseInt(matricula.nivel_id),
          inicio_vigencia: matricula.inicio_vigencia || null,
          termino_vigencia: matricula.termino_vigencia || null,
          data_matricula: matricula.data_matricula || null,
          valor: parseFloat(matricula.valor) || 0,
          valor_com_desconto: parseFloat(matricula.valor_com_desconto) || 0,
          data_pagamento: matricula.pagamento_matricula.data_pagamento || null,
          data_recebimento: matricula.pagamento_matricula.data_recebimento || null,
          desconto: parseFloat(matricula.desconto) || 0,
          convenio_id: matricula.convenio_id
            ? parseInt(matricula.convenio_id)
            : null,
          pagamento_pendente: Boolean(matricula.pagamento_pendente),
          valor_mensalidade:
            contextoMensalidadeManagedNova
              ? contextoMensalidadeManagedNova.valorMensalBruto
              : (parseFloat(matricula.valor_mensalidade) || 0),

          desconto_mensalidade:
            contextoMensalidadeManagedNova
              ? contextoMensalidadeManagedNova.descontoMensalidadePercentual
              : (parseFloat(matricula.desconto_mensalidade) || 0),
          primeiro_vencimento: matricula.primeiro_vencimento || null,
          quantidade_pagamentos: parseInt(matricula.quantidade_pagamentos) || 0,
          valor_total: parseFloat(matricula.valor_total_mensalidade) || 0,
          observacoes: matricula.observacoes || null,
          direito_imagem: matricula.direito_imagem,
          encerrar_vigencia_final_ano: Boolean(matricula.encerrar_vigencia_final_ano),
          forma_pagamento_id: matricula.forma_pagamento_id
            ? parseInt(matricula.forma_pagamento_id)
            : null,
          data_vencimento: matricula.data_vencimento || null,
          conta_financeira_id: matricula.pagamento_matricula.conta_financeira_id
            ? parseInt(matricula.pagamento_matricula.conta_financeira_id)
            : null,
          banco_numero: matricula.banco_numero || null,
          agencia: matricula.agencia || null,
          cheque_numero: matricula.cheque_numero || null,
        };

        if (contextoMensalidadeManagedNova) {
          payloadMatricula.dia_vencimento_mensalidade =
            contextoMensalidadeManagedNova.diaVencimento;

          payloadMatricula.mensalidade_forma_pagamento_id =
            contextoMensalidadeManagedNova.formaPagamentoId;

          payloadMatricula.mensalidade_conta_financeira_id =
            contextoMensalidadeManagedNova.contaFinanceiraId;
        }

        // 4) Payload para pagamento de matrícula
        const pagamento = matricula.pagamento_matricula;

        const pagamentoPayload = {
          forma_pagamento_id: pagamento.forma_pagamento_id,
          data_matricula: matricula.data_matricula,
          data_vencimento: matricula.data_vencimento,
          valor: parseFloat(matricula.valor_com_desconto) || 0,
          data_pagamento: pagamento.data_pagamento || null,
          data_recebimento: pagamento.data_recebimento || null,
          observacoes: pagamento.observacoes || null,
          banco_numero: matricula.banco_numero || null,
          agencia: matricula.agencia || null,
          cheque_numero: matricula.cheque_numero || null,
          contrato: matricula.numero_contrato,
          conta_financeira_id: pagamento.conta_financeira_id
            ? parseInt(pagamento.conta_financeira_id)
            : null,
          numero_autorizacao: pagamento.numero_autorizacao || null,
        };

        const taxa = {
          id: pagamento.id || null,
          alteracoes: pagamento.id ? paymentPatch(originalTaxPayment, pagamentoPayload) : pagamentoPayload
        };
        const mensalidades = await montarPagamentosPayload(matricula.id, {
          exigirMensalidades: !isEdicao && !contextoMensalidadeManagedNova,
          somenteAdicionais: !!contextoMensalidadeManagedNova
        });
        const auditoria = isEdicao ? montarAuditoriaAlteracoes(matricula.id) : null;
        const payloadCompleto = {
          cabecalho: payloadMatricula,
          taxa: taxa.id && !Object.keys(taxa.alteracoes).length ? null : taxa,
          mensalidades,
          auditoria
        };
        const response = isEdicao
          ? await axios.put(`/operacional/matriculas/${matricula.id}/salvar-completo`, payloadCompleto)
          : await axios.post('/operacional/matriculas/salvar-completo', payloadCompleto);
        const matriculaId = response.data.id;
        matricula.id = matriculaId;
        matricula.numero_contrato = response.data.numero_contrato;
        if (response.data.taxa_id) matricula.pagamento_matricula.id = response.data.taxa_id;
        matriculaOriginal.value = deepClone(matricula);
        motivoAlteracoes.value = '';

        // 7) Registros acessórios de matrícula nova
        // Estes registros NÃO podem impedir o salvamento da matrícula.
        // Ex.: perfil Performance não tem acesso a Pós-venda.
        if (!isEdicao) {
          await criarRegistrosAcessoriosNovaMatricula(matriculaId);
        }

        // 9) Integração com prospecção quando a matrícula veio de uma prospecção
        if (!isEdicao && prospeccaoId.value) {
          const userName =
            instance?.appContext.config.globalProperties.$globalState.userName || "";

          const historicoPayload = {
            data_acao: new Date(),
            classificacao: "Matriculado",
            acao: `Contrato - ${matricula.numero_contrato}`,
            criado_em: new Date(),
            usuario: userName,
            motivo: "",
          };

          try {
            await axios.post(
              `/operacional/prospeccao/${prospeccaoId.value}/historico`,
              historicoPayload
            );

            const sanitizeDate = (date) => {
              if (!date) return null;
              return new Date(date).toISOString().split("T")[0];
            };

            await axios.put(`/operacional/prospeccao/${prospeccaoId.value}/atualizar`, {
              usuario: userName,
              classificacao: "Matriculado",
              data_acao: sanitizeDate(new Date()),
              data_proximo_contato: null,
              acao: `Contrato - ${matricula.numero_contrato}`,
            });

            router.push(`/operacional/prospeccao/${prospeccaoId.value}/detalhar`);
            return;
          } catch (error) {
            console.warn(
              "⚠️ Prospecção de origem não atualizada. A matrícula foi salva normalmente.",
              error?.response?.status,
              error?.response?.data
            );
          }
        }

        router.push("/operacional/matriculas");

      } catch (error) {
        console.error("Erro ao salvar matrícula:", error);
        console.error(
          "⛔ Backend disse:",
          error?.response?.status,
          error?.response?.data
        );

        if (error?.jaTratado) {
          return;
        }

        const mensagem =
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          error?.response?.data?.erro ||
          "Ocorreu um erro ao salvar. Veja o console.";

        toast.error(`❌ ${mensagem}`, {
          autoClose: 7000,
        });
      } finally {
        loading.value = false;
      }
    };

    const montarTurmasPayload = () => matricula.turmas.map(t => {
      const turmaId = Number(String(t.turma_id).split('-')[0]);
      return {
        nivel_id: Number(t.nivel_id || matricula.nivel_id),
        turma_id: turmaId,
        dia_semana: t.dia,
        horario: t.horario,
        turma_completa: t.turma_completa || `Turma: ${turmaId} | Dia: ${t.dia} | Hora: ${t.horario} | Instrutor: ${t.instrutor || 'Não informado'}`,
      };
    });

    const montarPagamentosPayload = async (
      matriculaId,
      {
        exigirMensalidades = false,
        somenteAdicionais = false
      } = {}
    ) => {
      try {
        const pagamentosMensais = (matricula.pagamentos || [])
          .filter(
            p =>
              !p.pgt_matricula &&
              (
                !somenteAdicionais ||
                p.adicional === true
              )
          );

        const pagamentosIncompletos = pagamentosMensais
          .map((p, index) => ({ p, numero: index + 1 }))
          .filter(({ p }) => !p.forma_pagamento_id || !p.data_vencimento);

        if (pagamentosIncompletos.length > 0) {
          const numeros = pagamentosIncompletos
            .map(item => item.numero)
            .join(", ");
          const mensagem =
            `Existem mensalidades incompletas nas linhas: ${numeros}. ` +
            "Preencha forma de pagamento e vencimento antes de salvar.";

          toast.error(mensagem, { autoClose: 7000 });

          const erroValidacao = new Error(mensagem);
          erroValidacao.jaTratado = true;
          throw erroValidacao;
        }

        const pagamentosParaSalvar = pagamentosMensais.map(p => {
          const formaId = Number(p.forma_pagamento_id) || null;
          const formaObj = formasPagamento.value.find(f => Number(f.id) === formaId);
          const nomeForma = formaObj?.nome || '';
          const ehInformar = nomeForma?.toLowerCase?.().includes('informar');
          const ehNovoBoleto = !p.id && isBoleto(formaId);

          const numeroParcela = isCartaoCreditoParcelado(formaId)
            ? getNumeroParcelaNoBlocoParcelado(p)
            : 1;

          const dataBaseOperacao = isCartaoCreditoParcelado(formaId)
            ? getDataBaseBlocoParcelado(p)
            : p.data_pagamento;

          const dataRecebimento = (() => {
            /*
             * Parcelas existentes preservam a data atual, inclusive vazia.
             * Recálculos decorrentes de edição explícita já ocorrem nos
             * handlers; salvar não deve criar uma alteração adicional.
             */
            if (p.id || isPagamentoBloqueadoEdicao(p)) {
              return p.data_recebimento || null;
            }

            if (ehInformar || ehNovoBoleto) return null;

            if (p.data_recebimento && String(p.data_recebimento).trim() !== '') {
              return p.data_recebimento;
            }

            if (!dataBaseOperacao || String(dataBaseOperacao).trim() === '') return null;

            return globalUtils.calcularDataRecebimento(
              dataBaseOperacao,
              formaId,
              formasPagamento.value,
              numeroParcela
            );
          })();

          return {
            id: p.id ? Number(p.id) : null,
            matricula_id: Number(matriculaId),
            contrato: matricula.numero_contrato,
            forma_pagamento_id: formaId,
            data_vencimento: p.data_vencimento,
            valor: p.adicional ? converterValor(p.valor) : (Number(p.valor) || 0),
            data_pagamento: ehNovoBoleto
              ? null
              : (p.data_pagamento || null),

            data_recebimento: ehNovoBoleto
              ? null
              : dataRecebimento,
            observacoes: p.observacoes || null,
            banco_numero: p.banco_numero ? String(p.banco_numero).trim() : null,
            agencia: p.agencia ? String(p.agencia).trim() : null,
            cheque_numero: p.cheque_numero ? String(p.cheque_numero).trim() : null,
            adicional: !!p.adicional,
            pgt_matricula: false,
            conta_financeira_id: p.conta_financeira_id ? Number(p.conta_financeira_id) : null,
            numero_autorizacao: (p.numero_autorizacao || '').trim() || null,
          };
        });

        if (pagamentosParaSalvar.length === 0) {
          if (exigirMensalidades) {
            const mensagem =
              "Nenhuma mensalidade foi preparada para gravação. A matrícula não pode ser concluída.";

            toast.error(mensagem, { autoClose: 7000 });

            const erroValidacao = new Error(mensagem);
            erroValidacao.jaTratado = true;
            throw erroValidacao;
          }

        }

        const oldById = new Map(originalMonthlyPayments.filter(p => !p.pgt_matricula && String(p.contrato_efetivo || p.contrato || '') === String(matricula.numero_contrato || '')).map(p => [Number(p.id),p]));
        const pagamentos = pagamentosParaSalvar.map(p => {
          const original = oldById.get(Number(p.id));
          if (p.id && !original) throw new Error('Parcela desconhecida. Recarregue a matrícula.');
          if (original) return { id: p.id, ...paymentPatch(original, p) };
          const nova = { ...p };
          delete nova.matricula_id;
          delete nova.contrato;
          return nova;
        });
        const remover_pagamentos_ids = [...oldById.keys()].filter(id => !pagamentosParaSalvar.some(p => Number(p.id) === id));
        if (!remover_pagamentos_ids.length && pagamentos.every(p => p.id && Object.keys(p).length === 1)) return null;
        return { pagamentos, remover_pagamentos_ids };

      } catch (e) {
        console.error('❌ Erro ao salvar pagamentos:', e);
        console.error('⛔ Backend pagamentos:', e?.response?.status, e?.response?.data);

        if (e?.response?.status === 409) {
          const mensagem =
            e?.response?.data?.error ||
            e?.response?.data?.message ||
            e?.response?.data?.erro ||
            'Existem pagamentos já conciliados que não podem ser alterados ou excluídos.';

          toast.error(mensagem, {
            autoClose: 7000,
          });

          const erroTratado = new Error(mensagem);
          erroTratado.jaTratado = true;
          erroTratado.status = 409;
          throw erroTratado;
        }

        throw e;
      }
    };

    const atualizarParcelasMensalidade = () => {
      const parcelas = matricula.pagamentos;

      // conta quantos já têm RECEBIMENTO
      const parcelasPagas = parcelas.filter(p =>
        p.forma_pagamento_id &&
        p.data_recebimento &&
        p.data_recebimento.trim() !== ""
      );
      const numParcelasPagas = parcelasPagas.length;

      // se não há nenhum recebimento, não precisamos validar total x recebidos
      if (numParcelasPagas === 0) {
        // só recalcula as datas normalmente
        distribuirParcelasMensalidade();
        return true;
      }

      const novoTotalParcelas = parseInt(matricula.quantidade_pagamentos) || 0;
      if (novoTotalParcelas <= numParcelasPagas) {
        alert("O novo total de parcelas deve ser maior que o número de parcelas já recebidas.");
        return false;
      }

      // 3. Calcula o total já recebido
      const totalRecebido = parcelasPagas.reduce((acc, p) => {
        const valor = parseFloat(p.valor) || 0;
        return acc + valor;
      }, 0);

      // 4. Verifica se o total já recebido não é maior que o Valor Total recalculado
      const valorTotalRecalculado = parseFloat(matricula.valor_total_mensalidade) || 0;
      if (totalRecebido > valorTotalRecalculado) {
        alert("O total já recebido é maior que o novo valor total da mensalidade.");
        return false;
      }

      // 5. Determina quantas parcelas não recebidas devem existir
      const numParcelasNaoPagasEsperado = novoTotalParcelas - numParcelasPagas;
      let parcelasNaoRecebidas = matricula.pagamentos.filter(
        p => !p.data_recebimento || p.data_recebimento.trim() === ""
      );

      if (parcelasNaoRecebidas.length < numParcelasNaoPagasEsperado) {
        const diff = numParcelasNaoPagasEsperado - parcelasNaoRecebidas.length;
        for (let i = 0; i < diff; i++) {
          parcelasNaoRecebidas.push({
            forma_pagamento_id: null,
            data_vencimento:    "", // será preenchido abaixo
            valor:              "0.00",
            data_pagamento:     "",
            data_recebimento:   "",
            banco_numero:       "",
            agencia:            "",
            cheque_numero:      ""
          });
        }
      } else if (parcelasNaoRecebidas.length > numParcelasNaoPagasEsperado) {
        parcelasNaoRecebidas = parcelasNaoRecebidas.slice(0, numParcelasNaoPagasEsperado);
      }

      // 6. Define a data base para os próximos vencimentos:
      let dataBase;
      if (parcelasPagas.length > 0) {
        parcelasPagas.sort((a, b) => new Date(a.data_vencimento) - new Date(b.data_vencimento));
        dataBase = new Date(parcelasPagas[parcelasPagas.length - 1].data_vencimento);
        dataBase.setMonth(dataBase.getMonth() + 1);
      } else if (matricula.primeiro_vencimento) {
        dataBase = new Date(matricula.primeiro_vencimento);
      } else {
        return false;
      }

      // 7. Calcula o saldo a pagar e o novo valor de cada parcela não paga
      const saldoAPagar = valorTotalRecalculado - totalRecebido;
      const novoValorParcela = Number(
        (saldoAPagar / numParcelasNaoPagasEsperado).toFixed(2)
      );

      // 8. Atualiza as parcelas não recebidas
      parcelasNaoRecebidas.forEach((parcela, index) => {
        const dataVencimento = new Date(dataBase);
        dataVencimento.setMonth(dataVencimento.getMonth() + index);
        parcela.data_vencimento  = dataVencimento.toISOString().split("T")[0];
        parcela.valor            = novoValorParcela;
      });

      // 9. Recombina as pagas e não pagas, ordena e atualiza o array
      const novasParcelas = [...parcelasPagas, ...parcelasNaoRecebidas];
      novasParcelas.sort((a, b) => new Date(a.data_vencimento) - new Date(b.data_vencimento));
      matricula.pagamentos = novasParcelas;

      calcularValorTotalMensalidade();
      return true;
    };


  const backupTipoPlanData = ref({
    tipo_plano_id: null,
    valor_mensalidade: null,
    quantidade_pagamentos: null,
    plano_aulas_semana: null,
    plano_quantidade_parcelas: null,
    turmas: []
  });

  const backupTipoPlano = () => {
    backupTipoPlanData.value = {
      tipo_plano_id: matricula.tipo_plano_id,
      valor_mensalidade: matricula.valor_mensalidade,
      quantidade_pagamentos: matricula.quantidade_pagamentos,
      plano_aulas_semana: plano.aulas_semana,
      plano_quantidade_parcelas: plano.quantidade_parcelas,
      turmas: JSON.parse(JSON.stringify(matricula.turmas))
    };
  };

  const revertTipoPlanoChange = () => {
    skipTurmasRecreation = true;
    const backup = backupTipoPlanData.value;
    matricula.tipo_plano_id = backup.tipo_plano_id;
    matricula.valor_mensalidade = backup.valor_mensalidade;
    matricula.quantidade_pagamentos = backup.quantidade_pagamentos;
    plano.aulas_semana = backup.plano_aulas_semana;
    plano.quantidade_parcelas = backup.plano_quantidade_parcelas;
    // Atualiza o array de turmas in place para preservar a reatividade
    matricula.turmas.splice(0, matricula.turmas.length, ...backup.turmas);
    // Mantenha a flag ativa por 200ms (por exemplo)
    setTimeout(() => {
      skipTurmasRecreation = false;
    }, 200);
  };

  const onTipoPlanoChange = async () => {
    const novoTipo = matricula.tipo_plano_id;
    const planoSelecionado = tiposPlano.value.find(p => p.id === novoTipo);

    if (planoSelecionado) {
      const novoTotalParcelas = planoSelecionado.quantidade_parcelas;
      const parcelasPagasCount = (matricula.pagamentos || []).filter(p =>
        p.data_recebimento && p.data_recebimento.trim() !== ""
      ).length;

      if (novoTotalParcelas <= parcelasPagasCount) {
        alert("O novo total de parcelas deve ser maior que o número de parcelas já pagas.");
        revertTipoPlanoChange();
        return;
      }

      // atualiza valores do plano
      matricula.valor_mensalidade     = planoSelecionado.valor_mensalidade;
      matricula.quantidade_pagamentos = planoSelecionado.quantidade_parcelas;
    }

    // 👉 recalcule o término somente se o usuário não mexeu
    if (!terminoVigenciaTouched.value && matricula.inicio_vigencia) {
      calcularTerminoVigencia();
    }

    await carregarDadosPlanoMatricula();
    calcularValorComDescontoMensalidade();
    calcularValorTotalMensalidade();
    atualizarParcelasMensalidade();

    if (!modoAvancado?.value) {
      ensureMinSlots(plano.aulas_semana);
    }
  };

  const carregarNovaPessoa = async (newPersonId, newPersonTipo) => {
    try {
      const response = await axios.get(`/cadastros/pessoas-fisicas/${newPersonId}`);
      const novaPessoa = response.data;
      if (newPersonTipo === 'aluno') {
        matricula.aluno_id = novaPessoa.id;
        matricula.aluno_nome = novaPessoa.nome;
      } else if (newPersonTipo === 'responsavel') {
        matricula.responsavel_id = novaPessoa.id;
        matricula.responsavel_nome = novaPessoa.nome;
      }
    } catch (error) {
      console.error("Erro ao carregar nova pessoa:", error);
    } finally {
      // Remove os parâmetros da query para evitar recarregamento repetido
      router.replace({
        query: {
          ...route.query,
          newPersonId: undefined,
          newPersonTipo: undefined,
          prospeccaoId: fixedProspeccaoId.value // Use o valor fixo armazenado
        }
      });
    }
  };

  const isBoleto = (formaId) => {
    const forma = getFormaById(formaId);

    if (!forma) return false;

    const codigoSistema = String(
      forma.codigo_sistema || ''
    ).trim().toUpperCase();

    const tipoIntegracao = String(
      forma.tipo_integracao || ''
    ).trim().toUpperCase();

    return (
      codigoSistema === 'BOLETO' ||
      (
        tipoIntegracao === 'COBRANCA_BANCARIA' &&
        forma.gera_cobranca === true
      ) ||
      norm(forma.nome) === 'boleto'
    );
  };

  const isPrimeiraParcelaMensalidade = (pag) => {
    const lista = getParcelasMensalidadeOrdenadas();

    if (!lista.length || !pag) return false;

    const primeira = lista[0];

    return (
      primeira === pag ||
      (
        primeira?.id &&
        pag?.id &&
        Number(primeira.id) === Number(pag.id)
      )
    );
  };

  const aplicarBoletoEmTodasParcelasSePrimeira = (pag) => {
    if (!pag) return;
    if (!isBoleto(pag.forma_pagamento_id)) return;
    if (!isPrimeiraParcelaMensalidade(pag)) return;

    const formaBoletoId = Number(pag.forma_pagamento_id) || null;

    const lista = getParcelasMensalidadeOrdenadas();

    lista.forEach((parcela) => {
      parcela.forma_pagamento_id = formaBoletoId;

      // Boleto não usa autorização nem dados de cheque
      parcela.numero_autorizacao = '';
      parcela.banco_numero = '';
      parcela.agencia = '';
      parcela.cheque_numero = '';

      // Reaplica conta padrão, se houver
      parcela.conta_financeira_id = null;
      aplicarContaPadraoSeVazio(
        parcela.forma_pagamento_id,
        parcela,
        'conta_financeira_id'
      );

      // Boleto nasce pendente. Pagamento e recebimento serão
      // preenchidos somente após o processamento bancário.
      parcela.data_pagamento = null;
      parcela.data_recebimento = null;
    });
  };

  const atualizarPagamentoMensal = (pag) => {
    if (!pag) return;

    // Abre os dados complementares ao selecionar/trocar a forma.
    // Assim o usuário visualiza conta, autorização e dados do cheque
    // sem depender de rolagem horizontal.
    pag._mostrarHistorico = true;

    if (!isCheque(pag.forma_pagamento_id)) {
      pag.banco_numero = '';
      pag.agencia = '';
      pag.cheque_numero = '';
    }

    if (!isFormaComAutorizacao(pag.forma_pagamento_id)) {
      pag.numero_autorizacao = '';
    }

    pag.conta_financeira_id = null;
    aplicarContaPadraoSeVazio(pag.forma_pagamento_id, pag, 'conta_financeira_id');

    if (isAInformar(pag.forma_pagamento_id)) {
      pag.data_pagamento = null;
      pag.data_recebimento = null;
      pag.numero_autorizacao = '';
      return;
    }

    // ✅ Regra: se a primeira parcela for Boleto,
    // todas as demais parcelas também assumem Boleto.
    aplicarBoletoEmTodasParcelasSePrimeira(pag);

    if (isBoleto(pag.forma_pagamento_id)) {
      pag.data_pagamento = null;
      pag.data_recebimento = null;
      return;
    }

    pag.data_pagamento = pag.data_vencimento || null;

    if (isCartaoCreditoParcelado(pag.forma_pagamento_id)) {
      propagarBlocoParceladoParaLinhasVazias(pag);

      const bloco = getBlocoParceladoDaParcela(pag);

      const comAutorizacao = bloco.find(p => (p.numero_autorizacao || '').trim());
      if (comAutorizacao) {
        pag.numero_autorizacao = (comAutorizacao.numero_autorizacao || '').trim();
      }

      const comConta = bloco.find(p => p.conta_financeira_id);
      if (comConta) {
        pag.conta_financeira_id = comConta.conta_financeira_id;
      }

      replicarAutorizacaoNoBlocoParcelado(pag);
      replicarContaNoBlocoParcelado(pag);
      recalcularRecebimentoBlocoParcelado(pag);
      return;
    }

    recalcularRecebimentoParcelaMensal(pag);
  };


function onPagamentoMatriculaChange() {
  const pg = matricula.pagamento_matricula;
  const forma = formasPagamento.value.find(f => f.id === pg.forma_pagamento_id);
  if (!forma) return;

  const formaId = forma.id;

  // 🔴 ESPÉCIE: zera campos que não se aplicam
  if (isEspecie(formaId)) {
    pg.conta_financeira_id = null;
    pg.numero_autorizacao = null;
    // mantém vencimento, mas não força pagamento/recebimento
    pg.data_pagamento = pg.data_vencimento || null;
    pg.data_recebimento = globalUtils.calcularDataRecebimento(
      pg.data_pagamento,
      formaId,
      formasPagamento.value
    );
    return;
  }

  // 🔴 A INFORMAR: zera tudo
  if (isAInformar(formaId)) {
    pg.conta_financeira_id = null;
    pg.numero_autorizacao = null;
    pg.data_vencimento   = null;
    pg.data_pagamento    = null;
    pg.data_recebimento  = null;
    return;
  }

  // 🟡 BOLETO: nasce pendente e será quitado pelo fluxo bancário.
  if (isBoleto(formaId)) {
    pg.numero_autorizacao = null;
    pg.banco_numero = null;
    pg.agencia = null;
    pg.cheque_numero = null;
    pg.data_pagamento = null;
    pg.data_recebimento = null;

    aplicarContaPadraoSeVazio(
      pg.forma_pagamento_id,
      pg,
      'conta_financeira_id'
    );

    return;
  }

  // 🟢 DEMAIS FORMAS
  // datas seguem sua regra padrão
  pg.data_pagamento = pg.data_vencimento;
  pg.data_recebimento = globalUtils.calcularDataRecebimento(
    pg.data_pagamento,
    formaId,
    formasPagamento.value
  );

  // 🟢 aplica conta padrão automaticamente (por forma → fallback global)
  aplicarContaPadraoSeVazio(pg.forma_pagamento_id, pg, 'conta_financeira_id');
}

const onInicioVigenciaChange = () => {
  if (matricula.encerrar_vigencia_final_ano) {
    aplicarEncerramentoFinalAno();
    return;
  }

  calcularTerminoVigencia();
};

watch(
  () => [
    matricula.pagamento_matricula.forma_pagamento_id,
    matricula.pagamento_matricula.data_vencimento
  ],
  ([novaForma, novaVenc]) => {
    if (hydrating.value) return; // ← evita rodar na carga
    const pg = matricula.pagamento_matricula;
    const forma = formasPagamento.value.find(f => f.id === novaForma);
    if (!forma) return;

    // === ESPÉCIE EM EDITAR ===
    if (isEspecie(forma.id)) {
      pg.data_pagamento      = novaVenc;
      pg.data_recebimento    = globalUtils.calcularDataRecebimento(
        pg.data_pagamento,
        forma.id,
        formasPagamento.value
      );
      return;
    }

    if (isBoleto(forma.id)) {
      pg.data_pagamento = null;
      pg.data_recebimento = null;
      return;
    }

    // apenas em edição
    if (!matricula.id) return;

    // A informar já tratado em onPagamentoMatriculaChange
    if (isAInformar(forma.id)) return;

    // === OUTRAS FORMAS ===
    pg.data_recebimento = globalUtils.calcularDataRecebimento(
      novaVenc,
      novaForma,
      formasPagamento.value
    );
  }
);


  const cancelar = () => {
    // Utiliza o prospeccaoId reativo
    if (!matricula.id && prospeccaoId.value) {
      router.push(`/operacional/prospeccao/${prospeccaoId.value}/detalhar`);
    } else {
      router.push('/operacional/matriculas');
    }
  };


    // sempre que a forma de pagamento de uma parcela mudar...
    watch(
      () => matricula.pagamentos.map(p => p.forma_pagamento_id),
      (novasFormas) => {
        // só aplica em NOVO (sem id)
        if (matricula.id) return;

        novasFormas.forEach((novaForma, idx) => {
          const pagamento = matricula.pagamentos[idx];
          const formaObj = formasPagamento.value.find(f => f.id === novaForma);

          if (
            formaObj?.nome === 'A informar' ||
            isBoleto(novaForma)
          ) {
            pagamento.data_pagamento = null;
            pagamento.data_recebimento = null;
          } else {
            pagamento.data_pagamento = pagamento.data_vencimento;
          }
        });
      }
    );


    watch(
      () => matricula.pagamento_matricula.data_pagamento,
      (novaData) => {
        if (hydrating.value) return;
        const forma = formasPagamento.value.find(f => f.id === matricula.pagamento_matricula.forma_pagamento_id);
        if (
          !novaData ||
          forma?.nome === 'A informar' ||
          isBoleto(matricula.pagamento_matricula.forma_pagamento_id)
        ) {
          matricula.pagamento_matricula.data_recebimento = null;
        } else {
          matricula.pagamento_matricula.data_recebimento =
            globalUtils.calcularDataRecebimento(
              novaData,
              matricula.pagamento_matricula.forma_pagamento_id,
              formasPagamento.value
            );
        }
      }
    );

    watch(
      () => matricula.pagamentos.map(p => p.data_pagamento),
      (novo, antigo) => {
        if (hydrating.value) return;
        if (!Array.isArray(novo) || !Array.isArray(antigo)) return;

        for (let i = 0; i < novo.length; i++) {
          if (novo[i] === antigo[i]) continue;

          const pag = matricula.pagamentos[i];
          if (!pag) continue;

          if (isCartaoCreditoParcelado(pag.forma_pagamento_id)) {
            recalcularRecebimentoBlocoParcelado(pag);
          } else {
            recalcularRecebimentoParcelaMensal(pag);
          }
        }
      }
    );

    watch(
      () => [
        matricula.pagamento_matricula.forma_pagamento_id,
        matricula.data_vencimento      // <— aqui
      ],
      ([novaForma, novaVenc]) => {
        if (matricula.id) return; // só em NOVO
        // ambos devem existir
        if (!novaForma || !novaVenc) {
          return;
        }

        const formaObj = formasPagamento.value.find(f => f.id === novaForma);
        if (!formaObj) return;

        if (
          formaObj.nome === 'A informar' ||
          isBoleto(novaForma)
        ) {
          matricula.pagamento_matricula.data_pagamento   = null;
          matricula.pagamento_matricula.data_recebimento = null;
        } else {
          // data de pagamento = data de vencimento
          matricula.pagamento_matricula.data_pagamento = novaVenc;
          // recalcula recebimento
          matricula.pagamento_matricula.data_recebimento =
            globalUtils.calcularDataRecebimento(
              matricula.pagamento_matricula.data_pagamento,
              novaForma,
              formasPagamento.value
            );
        }
      },
      { flush: 'post' }
    );

    watch(
      () => matricula.data_vencimento,
      novaVenc => {
        matricula.pagamento_matricula.data_vencimento = novaVenc;
      },
      { immediate: true }
    );


    watch(
      () => [matricula.categoria_id, matricula.tipo_plano_id],
      async ([novaCategoria, novoTipo]) => {
        if (hydrating.value) return;

        if (novaCategoria && novoTipo) {

          try {
            const response = await axios.get(`/cadastros/planos-matriculas`, {
              params: { categoria_id: novaCategoria, tipo_id: novoTipo },
            });

            if (response.data.data?.length) {
              const planoEncontrado = response.data.data[0];
              plano.aulas_semana       = planoEncontrado.aulas_semana ?? 0;
              plano.quantidade_parcelas = planoEncontrado.quantidade_parcelas ?? 0;
            } else {
              plano.aulas_semana       = 0;
              plano.quantidade_parcelas = 0;
            }

            // Se já tiver início, atualiza término (somente se o usuário NÃO mexeu no campo)
            if (!terminoVigenciaTouched.value && matricula.inicio_vigencia) {
              const planoSelecionado = tiposPlano.value.find(p => p.id === novoTipo);
              if (planoSelecionado) {
                const dt = new Date(matricula.inicio_vigencia);
                dt.setDate(dt.getDate() + planoSelecionado.duracao);
                matricula.termino_vigencia = dt.toISOString().split('T')[0];
              }
            }

            // **NOVO**: preenche 1º vencimento com hoje e gera parcelas
            if (!matricula.id) {
              const hoje = new Date().toISOString().split('T')[0];
              matricula.primeiro_vencimento = hoje;
              distribuirParcelasMensalidade();
            }

          } catch (error) {
            console.error("Erro ao buscar plano de matrícula:", error);
          }
        }
      }
    );

    watch(() => matricula.nivel_id, (novoNivel) => {
      if (hydrating.value) return;           // ✅ ADD
      if (skipTurmasRecreation) return;

      // Em modo avançado, não mexe nas linhas existentes
      if (modoAvancado?.value) {
        // Apenas recarrega a lista para o modo básico continuar coerente
        carregarTurmas();
        return;
      }

      if (!novoNivel) {
        matricula.turmas = [];
        return;
      }

      carregarTurmas();
      ensureMinSlots(plano.aulas_semana);
    });

    watch(
      () => plano.aulas_semana,
      (novoValor) => {
        matricula.aulas_semana = novoValor;
      }
    );

    watch(() => plano.aulas_semana, async (novoAulas) => {
      if (hydrating.value) return;
      if (skipTurmasRecreation) return;

      // Em modo avançado, não destrói linhas — apenas ajusta quantidade se quiser
      if (modoAvancado?.value) {
        ensureMinSlots(novoAulas);
        return;
      }

      if (matricula.id && matricula.nivel_id) {
        await carregarTurmas();
      }
      ensureMinSlots(novoAulas);
    });


    watch([() => matricula.valor, () => matricula.desconto], ([novoValor, novoDesconto]) => {
      if (novoValor !== null && novoValor !== undefined) {
        matricula.valor_com_desconto = novoValor - (novoValor * (novoDesconto || 0) / 100);
      }
    });

    watch(() => matricula.convenio_id, (novoConvenioId) => {
      if (!novoConvenioId) {
        matricula.desconto = 0;
      }
    });

    watch(() => plano.valor_mensalidade, calcularValorComDescontoMensalidade);


    watch([() => matricula.valor_mensalidade, () => matricula.desconto_mensalidade], ([novoValor, novoDesconto]) => {
      if (novoValor !== null && novoValor !== undefined) {
        matricula.valor_com_desconto_mensalidade = novoValor - (novoValor * (novoDesconto || 0) / 100);
      }
    });

    watch(() => matricula.primeiro_vencimento, (novaData) => {
      if (matricula.encerrar_vigencia_final_ano && novaData) {
        aplicarQuantidadePagamentosAteTermino();
        return;
      }

      if (!matricula.id && novaData) {
        distribuirParcelasMensalidade();
      }
    });

    watch(() => plano.quantidade_parcelas, (novoValor) => {
      const qtd = Number(novoValor) || 0;

      matricula.quantidade_parcelas = qtd;
      matricula.quantidade_pagamentos = qtd;

      calcularValorTotalMensalidade();

      if (!matricula.id && matricula.primeiro_vencimento) {
        distribuirParcelasMensalidade();
      }
    });

    watch(
      [() => matricula.nivel_id, () => plano.aulas_semana],
      ([novoNivel, novoAulas]) => {
        if (hydrating.value) return;
        if (skipTurmasRecreation) return;

        if (!novoNivel) {
          matricula.turmas = [];
          return;
        }

        // Em modo avançado não reescreve conteúdo; só garante a quantidade
        if (modoAvancado?.value) {
          ensureMinSlots(novoAulas);
          return;
        }

        ensureMinSlots(novoAulas);
      }
    );

    watch(() => route.query, (newQuery) => {
      if (newQuery.newPersonId && newQuery.newPersonTipo) {
        carregarNovaPessoa(newQuery.newPersonId, newQuery.newPersonTipo);
      }
    });

    watch(
      () => matricula.conta_financeira_id,
      (novoValor) => {
        if (matricula.pagamento_matricula) {
          matricula.pagamento_matricula.conta_financeira_id = novoValor;
          // Se necessário, atualize outros campos que dependam de Dados Financeiros
          // por exemplo, forma_pagamento_id, data_vencimento, etc.
        }
      }
    );

// antes estava sobrescrevendo sempre; agora só preenche por padrão
watch(
  () => matricula.data_matricula,
  (novaData) => {
    if (!matricula.id && (!matricula.data_vencimento || !dataVencimentoTouched.value)) {
      matricula.data_vencimento = novaData;
    }
  },
  { immediate: true }
);

    const valorInput = ref(globalUtils.formatarValor(matricula.valor));

    // 2) sempre que mudar o valor numérico, atualiza a string formatada
    watch(
      () => matricula.valor,
      novoValor => {
        valorInput.value = globalUtils.formatarValor(novoValor);
      }
    );

    // 3) ao focar, mostra só o número cru para editar
    const onValorFocus = () => {
      // formata só o número com vírgula (sem símbolo R$)
      const v = Number(matricula.valor) || 0;
      valorInput.value = v.toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    };

    // 4) ao sair, converte a string para número, atualiza desconto e reformat­a
    const onValorBlur = () => {
      const num = globalUtils.converterValor(valorInput.value);
      matricula.valor = num;
      calcularValorComDesconto();
      valorInput.value = globalUtils.formatarValor(num);
    };

    onMounted(async () => {
      hydrating.value = true;   // ← começa hidratação

      fixedProspeccaoId.value = route.query.prospeccaoId || null;

      await carregarCategorias();
      await carregarNiveis();
      await carregarValorMatricula();
      await carregarFormasPagamento();
      await carregarConvenios();
      await carregarContasFinanceiras();
      await carregarTurmas();

      if (route.params.id) {
        await carregarMatricula(route.params.id);
      } else {
        onPagamentoMatriculaChange();
      }

      if (route.query.newPersonId && route.query.newPersonTipo) {
        carregarNovaPessoa(route.query.newPersonId, route.query.newPersonTipo);
      }

      await nextTick();
      hydrating.value = false;  // ← termina hidratação
    });


    return {
      composicaoTroca, estadoComposicaoTroca, conferenciaTrocaIndisponivel, resumoTroca, carregarComposicaoTroca,
      onPagamentoMatriculaChange,
      onPagamentoInput,
      atualizarPagamentoMensal,
      loading,
      contasFinanceiras,
      tipoPessoaSelecionada,
      route,
      router,
      globalUtils,
      matricula,
      novaTurma,
      categorias,
      tiposPlano,
      plano,
      niveis,
      aulasSemanaOptions,
      parcelasOptions,
      turmas,
      podeAdicionarTurma,
      carregarCategorias,
      carregarTiposPlano,
      carregarNiveis,
      carregarTurmas,
      carregarDadosPlanoMatricula,
      calcularTerminoVigencia,

      onEncerrarFinalAnoChange,
      onTerminoVigenciaManual,
      onInicioVigenciaChange,

      adicionarTurma,
      adicionarTurmaSlot,
      atualizarTurma,
      removerTurma,
      turmasDisponiveis,
      modalPessoaAberto,
      abrirModalPessoa,
      fecharModalPessoa,
      selecionarPessoa,
      formValid,
      salvarMatricula,
      montarPagamentosPayload,
      cancelar,

      carregarValorMatricula,
      formasPagamento,
      convenios,
      calcularValorComDesconto,
      atualizarDescontoConvenio,

      calcularValorComDescontoMensalidade,
      calcularValorTotalMensalidade,
      distribuirParcelasMensalidade,
      valorTotalComParcelasAdicionais,
      valorParcelasAdicionais,
      podeAdicionarPagamento,
      adicionarPagamento,
      adicionarParcelaAdicional,
      desformatarValorAdicional,
      formatarValorAdicional,
      isCheque,
      existeCheque,
      isEspecie,
      isAInformar,
      isBoleto,

      // Contrato do template para matrícula inicial com Boleto gerenciado.
      novaMensalidadeBoletoSemantica,
      capturarDescontoMensalidade,
      entradaMensalidadeManaged,
      diasVencimentoCobranca,
      carregandoDiasVencimentoCobranca,
      erroPoliticaCobranca,
      onDiaVencimentoMensalidadeChange,
      carregarDiasVencimentoCobranca,
      previewMensalidadeManaged,
      carregandoPreviewMensalidadeManaged,
      erroPreviewMensalidadeManaged,
      mensagemPreviewProrataManaged,
      primeiroTituloPreviewManaged,

      removerPagamento,
      descontoDesabilitado,
      backupTipoPlano,
      onTipoPlanoChange,
      prospeccaoId,
      fixedProspeccaoId,

      valorInput,
      onValorFocus,
      onValorBlur,
      onValorMensalidadeChange,

      modoAvancado,
      temParcelasAdicionais,
      onNivelLinhaChange,
      todasTurmasDisponiveis,
      turmasDisponiveisPorNivel,
      turmaEstaDisponivel,
      turmaHistoricaPreservada,
      ajustarTurmasExatas,
      onParcelaValorFocus,
      onParcelaValorBlur,
      canEditRecebimento,
      valorMensalidadeDisplay,
      onValorMensalidadeFocus,
      onValorMensalidadeBlur,

      isCartao,
      isFormaComAutorizacao,
      onNumeroAutorizacaoMensalInput,
      onContaFinanceiraMensalChange,
      isCartaoCreditoAVista,
      isCartaoCreditoParcelado,

      contaPadraoGlobalId,

      parcelasMensalidade,
      temParcelasMensalidade,
      recriarParcelasMensalidade,
      mensalidadeLoaded,
      motivoAlteracoes,
      alteracoesDetectadas,
      alteracoesQueExigemMotivo,
      temAlteracoes,
      temAlteracoesQueExigemMotivo,
      motivoValido,
      contadorAlteracoesUI,
      qtdParcelasAlteradas,
      ordenarParcelasMensalidade,
      compararParcelasMensalidade,
      vencimentosContratuais,
      numeroParcelaContratual,
      quantidadeRegistrosFinanceiros,
      quantidadePagamentosSubstitutos,
      quantidadeParcelasAdicionais,
      isPagamentoSubstituto,
      isPagamentoBloqueadoEdicao,
      situacaoPagamento,
      classeLinhaPagamento,
      isPagamentoConciliado,
      podeRemoverPagamento,
      motivoBloqueioPagamento,
      temDetalhesPagamento,
      tituloDetalhesPagamento,
      formatarTipoEventoCheque,
      formatarDataHistorico,
      formatarMotivosBloqueio,
      alternarHistoricoPagamento,
      temPendenciaDetalhesPagamento,

      totalLancamentosMensalidade,
      totalEsperadoMensalidade,
      resumoComposicoesMensalidade,
      mensagemComposicoesMensalidade,
      diferencaTotalMensalidade,
      diferencaMensalidadeAbsoluta,
      existeDiferencaMensalidade,
      mensagemDiferencaMensalidade,
      acrescimoPagamentosSubstitutos,
    };
  }
};
</script>

<style scoped>
/* Layout similar ao FormSection do formulário de Aula Avulsa */
.form-section {
  background: white;
  padding: 15px;
  border-radius: 8px;
  border: 1px solid #dee2e6;
  margin-bottom: 1rem;
}

/* Títulos das seções */
.section-title {
  font-size: 0.9rem;
  font-weight: bold;
  margin-bottom: 10px;
}

/* Estilização para campos desativados */
.disabled-input {
  background: #e9ecef;
  border: 1px solid #ced4da;
}

/* Estilização para a seção das turmas */
.turma-item {
  background: white;
  padding: 8px;
  border-radius: 5px;
  border: 1px solid #ccc;
}

/* Ícones de ação */
.required {
  color: red;
}

.add-turma-container {
  display: flex;
  justify-content: flex-end; /* Alinha o botão à direita */
  margin-top: 5px; /* Pequeno espaçamento acima */
}


/* Estilização da chave de alternância */
.toggle-switch {
  position: relative;
  display: inline-flex;
  align-items: center;
  width: 70px;
  height: 25px;
}

/* Esconde o input nativo */
.toggle-input {
  opacity: 0;
  width: 0;
  height: 0;
}

/* Estilização do botão */
/* Container do switch alinhado com os demais campos */
.toggle-switch {
  display: flex;
  align-items: center;
}

.toggle-switch label {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 6px;  /* Ajuste do padding interno */
  min-width: 70px;  /* Largura mínima para acomodar o texto */
  border-radius: 20px;
  font-weight: bold;
}

/* Esconde o input nativo */
.toggle-input {
  opacity: 0;
  width: 0;
  height: 0;
}

/* Estilização do botão */
.toggle-label {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 100%;
  background-color: #007bff; /* Azul para "Não" (Pagamento Quitado) */
  border-radius: 50px;
  cursor: pointer;
  transition: background-color 0.3s ease;
  padding: 4px 16px;
  font-weight: bold;
  color: white;
  font-size: 14px;
  text-align: center;
  position: relative;
}

/* Mudança de cor para "Sim" (Pagamento Pendente) */
.toggle-input:checked + .toggle-label {
  background-color: #dc3545; /* Vermelho para "Sim" */
}

/* Movimentação do botão ao alternar */
.toggle-label:before {
  content: "";
  position: absolute;
  left: 5px;
  width: 18px;
  height: 18px;
  background: white;
  border-radius: 50%;
  transition: transform 0.3s ease;
}

/* Ajusta a posição do botão quando ativado */
.toggle-input:checked + .toggle-label:before {
  transform: translateX(35px);
}

/* Ajuste do texto dentro do botão */
.toggle-label span {
  flex: 1;
  text-align: center;
  font-size: 12px;
  white-space: nowrap;
}

.spacer {
  margin-bottom: 20px;
}

.btn-warning {
  font-size: 12px;
}


/* ==========================================================
   Mensalidades — leitura de parcelas, substituições e histórico
   ========================================================== */
.mensalidade-resumo .badge {
  font-size: 0.76rem;
  font-weight: 600;
  padding: 0.45rem 0.65rem;
}

.mensalidade-table-wrapper {
  width: 100%;
  overflow-x: auto;
  border: 1px solid #e3e7eb;
  border-radius: 8px;
}

.mensalidade-table {
  width: 100%;
  min-width: 940px;
  table-layout: fixed;
  margin-bottom: 0;
}

.mensalidade-table thead th {
  position: sticky;
  top: 0;
  z-index: 2;
  background: #f8f9fa;
  white-space: nowrap;
  vertical-align: middle;
  font-size: 0.78rem;
  padding: 0.65rem 0.45rem;
}

.mensalidade-table tbody td {
  vertical-align: middle;
  padding: 0.48rem 0.4rem;
}

.mensalidade-table .form-control {
  width: 100%;
  min-width: 0;
  font-size: 0.82rem;
  padding-left: 0.45rem;
  padding-right: 0.45rem;
}

.coluna-parcela { width: 6%; }
.coluna-situacao { width: 14%; }
.coluna-forma { width: 19%; }
.coluna-data { width: 13.5%; }
.coluna-valor { width: 11%; }
.coluna-acoes { width: 10%; }

.parcela-numero-coluna {
  min-width: 58px;
}

.situacao-coluna {
  min-width: 118px;
}

.situacao-badge {
  display: inline-block;
  max-width: 100%;
  padding: 0.4rem 0.5rem;
  white-space: normal;
  line-height: 1.15;
  font-size: 0.72rem;
}

.campo-nao-aplicavel {
  display: block;
  text-align: center;
  color: #adb5bd;
  font-weight: 600;
}

.acao-pagamento {
  min-width: 88px;
}

.acao-pagamento .btn,
.pagamento-status-icon {
  width: 30px;
  height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}

.acao-pagamento .btn:disabled {
  cursor: not-allowed;
  opacity: 0.45;
}

.btn-detalhes-pendente {
  border-color: #ffc107;
  color: #8a6800;
  box-shadow: 0 0 0 2px rgba(255, 193, 7, 0.14);
}

.dados-complementares-grid {
  margin-bottom: 0.9rem;
}

.dados-complementares-grid .form-label {
  font-size: 0.78rem;
  font-weight: 600;
  color: #495057;
}

.dados-cheque-box {
  padding: 0.75rem;
  border: 1px solid #dee2e6;
  border-radius: 7px;
  background: #fff;
}

.dados-cheque-titulo {
  margin-bottom: 0.55rem;
  font-size: 0.8rem;
  font-weight: 700;
  color: #495057;
}

.linha-cheque-substituido > td {
  background-color: #f4f5f6;
}

.linha-cheque-substituido > td:first-child {
  border-left: 4px solid #6c757d;
}

.linha-cheque-devolvido > td {
  background-color: #fff1f2;
}

.linha-cheque-devolvido > td:first-child {
  border-left: 4px solid #dc3545;
}

.linha-pagamento-substituto > td {
  background-color: #eef8ff;
}

.linha-pagamento-substituto > td:first-child {
  border-left: 4px solid #0dcaf0;
}

.linha-parcela-adicional > td {
  background-color: #fff8e1;
}

.linha-parcela-adicional > td:first-child {
  border-left: 4px solid #ffc107;
}

.linha-historico > td {
  padding-top: 0;
  background-color: #fafbfc;
}

.historico-pagamento {
  border: 1px solid #dee2e6;
  border-radius: 6px;
  background: #ffffff;
  padding: 0.75rem 0.9rem;
  margin: 0 0 0.4rem;
}

.historico-pagamento-titulo {
  font-weight: 600;
  color: #495057;
  margin-bottom: 0.35rem;
}

.historico-pagamento-texto {
  color: #495057;
  font-size: 0.82rem;
  line-height: 1.45;
  white-space: pre-wrap;
}


.linha-pagamento-bloqueado .form-control:disabled {
  background-color: #eef0f2;
  color: #495057;
  opacity: 1;
  cursor: not-allowed;
}

.pagamento-status-indicator {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 30px;
  padding: 0;
  cursor: help;
  white-space: nowrap;
}

.linha-pagamento-conciliado > td {
  background-color: #f1f8f3;
}

.linha-pagamento-conciliado > td:first-child {
  border-left: 4px solid #198754;
}

.linha-pagamento-conciliado .form-control:disabled {
  background-color: #e6f1e9;
  border-color: #b8d5c1;
  color: #3f4f44;
}

.historico-pagamento-bloco {
  padding: 0.55rem 0;
  border-bottom: 1px dashed #dee2e6;
  color: #495057;
  font-size: 0.82rem;
  line-height: 1.45;
}

.historico-pagamento-bloco:last-child {
  border-bottom: 0;
}

.historico-lista {
  padding-left: 1.15rem;
  margin-top: 0.3rem;
}

.historico-lista li + li {
  margin-top: 0.2rem;
}

.conferencia-mensalidade {
  border-top: 1px solid #dee2e6;
  padding-top: 12px;
}

.conferencia-card {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-height: 70px;
  padding: 10px 12px;
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 7px;
}

.conferencia-label {
  color: #6c757d;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
}

.conferencia-card strong {
  font-size: 1rem;
}

.conferencia-ok {
  background: #edf8f0;
  border-color: #badbcc;
  color: #146c43;
}

.conferencia-alerta {
  background: #fff8e1;
  border-color: #ffda6a;
  color: #664d03;
}


@media (max-width: 1200px) {
  .mensalidade-table {
    min-width: 880px;
  }

  .mensalidade-table thead th,
  .mensalidade-table .form-control {
    font-size: 0.76rem;
  }
}

@media (max-width: 768px) {
  .mensalidade-table {
    min-width: 860px;
  }

  .historico-pagamento {
    padding: 0.65rem;
  }
}

</style>